// src/components/Accordion/Accordion.tsx
import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@fortawesome/pro-solid-svg-icons/faCaretDown";

// src/lib/utils.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
var PREFLIGHT_CLASS = "sb-preflight";
function cn(...inputs) {
  return twMerge(PREFLIGHT_CLASS, clsx(inputs));
}

// src/components/Accordion/Accordion.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var Accordion = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Root,
  {
    ref,
    className: cn(
      // Appearance
      "bg-neutral-0",
      className
    ),
    ...props
  }
));
Accordion.displayName = "Accordion";
var AccordionItem = React.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Item,
  {
    ref,
    className: cn(
      // Borders
      "border-b-neutral-2 border-b",
      className
    ),
    ...props
  }
));
AccordionItem.displayName = "AccordionItem";
var AccordionTrigger = React.forwardRef(({ className, children, actions, ...props }, ref) => /* @__PURE__ */ jsxs(
  AccordionPrimitive.Header,
  {
    className: cn(
      // Layout
      "flex"
    ),
    children: [
      /* @__PURE__ */ jsx(
        AccordionPrimitive.Trigger,
        {
          ref,
          className: cn(
            // Layout
            "group flex flex-1 items-center justify-between",
            // Spacing
            "px-2 py-2 pr-1",
            // Typography
            "font-lato text-neutral-1 text-left text-sm font-bold",
            // Interactions
            "cursor-pointer transition-all",
            // Disabled state
            "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
            className
          ),
          ...props,
          children: /* @__PURE__ */ jsx("div", { className: "flex-1", children })
        }
      ),
      actions && /* @__PURE__ */ jsx(
        "div",
        {
          className: cn(
            // Layout
            "flex items-center",
            // Spacing
            "gap-3"
          ),
          children: actions
        }
      ),
      /* @__PURE__ */ jsx(
        AccordionPrimitive.Trigger,
        {
          ref,
          className: cn(
            // Layout
            "group",
            // Spacing
            "px-1 py-2",
            // Interactions
            "cursor-pointer",
            // Disabled state
            "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
          ),
          "aria-label": "Toggle accordion caret",
          children: /* @__PURE__ */ jsx(
            FontAwesomeIcon,
            {
              icon: faCaretDown,
              className: cn(
                // Typography
                "text-neutral-4 text-sm",
                // Interactions
                "transition-transform duration-200",
                // Open state
                "group-data-[state=open]:rotate-[-180deg]"
              )
            }
          )
        }
      )
    ]
  }
));
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;
var AccordionContent = React.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx(
  AccordionPrimitive.Content,
  {
    ref,
    className: cn(
      // Layout
      "overflow-hidden",
      // Typography
      "text-neutral-1 text-sm",
      // Animations
      "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
      className
    ),
    ...props,
    children: /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          // Spacing
          "px-2 pb-4 pt-2",
          className
        ),
        children
      }
    )
  }
));
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

// src/components/ActionList/ActionList.tsx
import * as React2 from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var ActionList = React2.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "div",
    {
      ref,
      "data-slot": "action-list",
      className: cn("flex flex-col gap-px", className),
      ...props
    }
  )
);
ActionList.displayName = "ActionList";
var ActionListLabel = React2.forwardRef(
  ({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
    "span",
    {
      ref,
      "data-slot": "action-list-label",
      className: cn(
        // Spacing
        "px-2 py-1.5 select-none",
        // Typography (default brand) — small bold sentence-case (NOT uppercase)
        "text-xs font-semibold text-neutral-3",
        // Studio — caption type + tertiary content color (font-semibold above wins)
        "type-caption studio:text-content-tertiary",
        className
      ),
      ...props
    }
  )
);
ActionListLabel.displayName = "ActionListLabel";
var ActionListGroup = React2.forwardRef(
  ({ className, label, children, ...props }, ref) => /* @__PURE__ */ jsxs2(
    "div",
    {
      ref,
      "data-slot": "action-list-group",
      className: cn("flex flex-col gap-px pb-1", className),
      ...props,
      children: [
        label ? /* @__PURE__ */ jsx2(ActionListLabel, { children: label }) : null,
        children
      ]
    }
  )
);
ActionListGroup.displayName = "ActionListGroup";
var actionListItemVariants = cva(
  [
    // `group/item relative` only does anything when an action sibling
    // exists (i.e. the wrapper div is rendered); inert otherwise.
    "group/item relative",
    // Layout
    "flex h-8 w-full items-center gap-2",
    // Sizing / shape
    "rounded-lg px-2",
    // Appearance
    "cursor-pointer select-none outline-hidden",
    "transition-colors",
    // Typography (default brand) + studio override
    "text-sm text-neutral-1",
    "type-label studio:text-content-secondary",
    // Hover + keyboard-focus (Radix menu sets data-highlighted) highlight
    "hover:bg-highlight-3 data-[highlighted]:bg-highlight-3",
    "studio:hover:bg-surface-hover studio:data-[highlighted]:bg-surface-hover",
    // Visible keyboard-focus indicator (WCAG 2.4.7). A ring rather than a bg
    // change so focus reads distinctly even on a `selected` row (which already
    // carries the highlight bg). This is for the standalone button/link rows
    // (Surface A). Inside a Radix menu (Surface B), roving focus is already
    // shown via the data-[highlighted] bg above, so suppress the ring there to
    // match the rest of the menu's items.
    "focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
    "data-[highlighted]:ring-0",
    // Disabled (Radix/Slot path + native button path)
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    "disabled:pointer-events-none disabled:opacity-50",
    // Optional leading icon column auto-size
    '[&_svg:not([class*="size-"])]:size-4 [&_svg]:shrink-0'
  ],
  {
    variants: {
      selected: {
        true: "bg-highlight-3 studio:bg-surface-hover",
        false: ""
      }
    },
    defaultVariants: {
      selected: false
    }
  }
);
var ActionListItem = React2.forwardRef(
  ({
    selected,
    disabled,
    icon,
    action,
    asChild,
    as = "button",
    href,
    onClick,
    className,
    children,
    label,
    ...props
  }, ref) => {
    const RowComp = asChild ? Slot : as;
    const isNativeButton = as === "button" && !asChild;
    const guardDisabled = Boolean(disabled) && !isNativeButton;
    const rowProps = isNativeButton ? { type: "button", disabled } : {};
    const handleClick = guardDisabled ? (event) => {
      event.preventDefault();
      event.stopPropagation();
    } : onClick;
    const rowClassName = cn(
      actionListItemVariants({ selected }),
      action ? "pr-8" : null,
      className
    );
    const rowContent = asChild ? (
      // Consumer owns inner markup (should include `min-w-0 truncate`).
      children
    ) : /* @__PURE__ */ jsxs2(Fragment, { children: [
      icon,
      /* @__PURE__ */ jsx2("span", { className: "min-w-0 truncate", children: children ?? label })
    ] });
    const row = /* @__PURE__ */ jsx2(
      RowComp,
      {
        ref,
        "data-slot": action ? "action-list-item-row" : "action-list-item",
        "data-disabled": disabled ? "" : void 0,
        href: guardDisabled ? void 0 : href,
        className: rowClassName,
        ...rowProps,
        ...props,
        "aria-disabled": guardDisabled || void 0,
        tabIndex: guardDisabled ? -1 : props.tabIndex,
        onClick: handleClick,
        children: rowContent
      }
    );
    if (!action) {
      return row;
    }
    return /* @__PURE__ */ jsxs2("div", { "data-slot": "action-list-item", className: "group/item relative", children: [
      row,
      /* @__PURE__ */ jsx2("span", { className: "absolute right-1 top-1/2 flex -translate-y-1/2 items-center justify-center opacity-0 transition-opacity group-hover/item:opacity-100 focus-within:opacity-100 has-[[data-state=open]]:opacity-100", children: action })
    ] });
  }
);
ActionListItem.displayName = "ActionListItem";
var ActionListSeparator = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx2(
  "div",
  {
    ref,
    role: "separator",
    "data-slot": "action-list-separator",
    className: cn(
      // Spacing / sizing
      "mx-2 my-1 h-px",
      // Appearance (default brand) + studio edge color
      "bg-neutral-2 studio:bg-[var(--color-edge)]",
      className
    ),
    ...props
  }
));
ActionListSeparator.displayName = "ActionListSeparator";

// src/components/Alert/Alert.tsx
import { cva as cva2 } from "class-variance-authority";
import { jsx as jsx3 } from "react/jsx-runtime";
var alertVariants = cva2(
  [
    // Layout
    "relative grid w-full items-start",
    "grid-cols-[0_1fr] has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr]",
    // Spacing
    "gap-y-2 has-[>svg]:gap-x-2 p-3",
    // Appearance
    "rounded-md border-1",
    // Typography
    "text-sm",
    // SVG styles (translate-y-1 only when title is present to align with text)
    "[&>svg]:size-4 [&>svg]:text-current has-[>*[data-slot=alert-title]]:[&>svg]:translate-y-1",
    // Button alignment
    "[&>button]:col-start-2 [&>button]:justify-self-end"
  ],
  {
    variants: {
      variant: {
        neutral: "bg-neutral-1 text-neutral-1 border-neutral-1 *:data-[slot=alert-description]:text-neutral-3",
        // Studio `info` resolves through the same `bg-info-1 / text-info-1 /
        // border-info-1` semantic chain — under studio those map to the new
        // submerge ramp (50 / 900 / 200) per BrandAdoption, so no studio:
        // override is needed.
        info: "bg-info-1 text-info-1 border-info-1",
        // Light has no semantic positive-subtle pair (positive-1 is the
        // solid emerald fill), so the light row reaches for emerald
        // primitives directly. Studio uses the new color-positive-subtle
        // / content-positive-primary / positive-soft semantic tokens.
        positive: "bg-[var(--sb-color-emerald-50)] text-[var(--sb-color-emerald-800)] border-[var(--sb-color-emerald-200)] studio:bg-positive-subtle studio:text-content-positive-primary studio:border-positive-soft",
        error: "bg-destructive-3 text-destructive-3 border-destructive-3",
        warning: "bg-alert-1 text-alert-2 border-alert-1"
      }
    },
    defaultVariants: {
      variant: "neutral"
    }
  }
);
function Alert({
  className,
  variant,
  ...props
}) {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      "data-slot": "alert",
      role: "alert",
      className: cn(alertVariants({ variant }), className),
      ...props
    }
  );
}
function AlertTitle({ className, ...props }) {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      "data-slot": "alert-title",
      className: cn(
        // Layout
        "col-start-2",
        // Sizing
        "min-h-4",
        // Typography
        "line-clamp-1 font-semibold",
        className
      ),
      ...props
    }
  );
}
function AlertDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx3(
    "div",
    {
      "data-slot": "alert-description",
      className: cn(
        // Layout
        "col-start-2 grid justify-items-start gap-1",
        // Typography
        "text-xs",
        className
      ),
      ...props
    }
  );
}

// src/components/AutocompleteMenu/AutocompleteMenu.tsx
import React3, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Fragment as Fragment2, jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var GAP = 8;
var AutocompleteMenu = ({
  getAnchorRect,
  search,
  items,
  activeIndex,
  onSelect,
  container
}) => {
  const containerRef = useRef(null);
  const activeRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const anchorRect = getAnchorRect();
    if (!anchorRect) return;
    const menuRect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - anchorRect.bottom - GAP;
    const flipUp = menuRect.height > spaceBelow && anchorRect.top > menuRect.height + GAP;
    const rawLeft = anchorRect.left;
    const maxLeft = window.innerWidth - menuRect.width - GAP;
    setPosition({
      top: flipUp ? anchorRect.top - menuRect.height - GAP : anchorRect.bottom + GAP,
      left: Math.min(rawLeft, maxLeft)
    });
  }, [getAnchorRect, search, items]);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);
  const content = /* @__PURE__ */ jsx4(
    "ul",
    {
      ref: containerRef,
      role: "listbox",
      style: { top: position.top, left: position.left },
      className: cn(
        "fixed z-50 w-56 p-1",
        "max-h-52 overflow-y-auto overflow-x-hidden",
        "bg-neutral-1 rounded-md shadow-md",
        "border border-neutral-1",
        "text-neutral-1 text-sm",
        "list-none m-0"
      ),
      onMouseDown: (e) => e.preventDefault(),
      children: items.length === 0 ? /* @__PURE__ */ jsx4("li", { className: "px-2 py-1.5 text-sm text-neutral-3 select-none", children: "No results found" }) : items.map((item, index) => /* @__PURE__ */ jsx4(
        "li",
        {
          role: "option",
          "aria-selected": index === activeIndex,
          ref: index === activeIndex ? activeRef : null,
          className: cn(
            "relative flex h-8 w-full items-center",
            "gap-2 px-2 py-1.5 rounded-sm",
            "text-sm cursor-pointer select-none outline-none truncate",
            index === activeIndex ? "bg-highlight-1 text-highlight-2" : "hover:bg-highlight-3"
          ),
          onMouseDown: (e) => {
            e.preventDefault();
            onSelect(item.value);
          },
          children: item.prefix ? /* @__PURE__ */ jsxs3(Fragment2, { children: [
            /* @__PURE__ */ jsx4(
              "span",
              {
                className: cn(
                  "shrink-0 flex items-center",
                  !React3.isValidElement(item.prefix) && "text-base leading-none"
                ),
                children: item.prefix
              }
            ),
            /* @__PURE__ */ jsx4("span", { className: "truncate", children: item.label })
          ] }) : item.label
        },
        item.key
      ))
    }
  );
  return ReactDOM.createPortal(content, container ?? document.body);
};

// src/components/Box/Box.tsx
import * as React4 from "react";
import { Slot as Slot2 } from "@radix-ui/react-slot";

// src/components/Box/splitBoxProps.ts
import { cva as cva3 } from "class-variance-authority";
var boxVariants = cva3([], {
  variants: {
    p: {
      none: "p-0",
      xs: "p-1",
      sm: "p-2",
      md: "p-3",
      lg: "p-4",
      xl: "p-6"
    },
    px: {
      none: "px-0",
      xs: "px-1",
      sm: "px-2",
      md: "px-3",
      lg: "px-4",
      xl: "px-6"
    },
    py: {
      none: "py-0",
      xs: "py-1",
      sm: "py-2",
      md: "py-3",
      lg: "py-4",
      xl: "py-6"
    },
    pt: {
      none: "pt-0",
      xs: "pt-1",
      sm: "pt-2",
      md: "pt-3",
      lg: "pt-4",
      xl: "pt-6"
    },
    pr: {
      none: "pr-0",
      xs: "pr-1",
      sm: "pr-2",
      md: "pr-3",
      lg: "pr-4",
      xl: "pr-6"
    },
    pb: {
      none: "pb-0",
      xs: "pb-1",
      sm: "pb-2",
      md: "pb-3",
      lg: "pb-4",
      xl: "pb-6"
    },
    pl: {
      none: "pl-0",
      xs: "pl-1",
      sm: "pl-2",
      md: "pl-3",
      lg: "pl-4",
      xl: "pl-6"
    },
    m: {
      none: "m-0",
      xs: "m-1",
      sm: "m-2",
      md: "m-3",
      lg: "m-4",
      xl: "m-6"
    },
    mx: {
      none: "mx-0",
      xs: "mx-1",
      sm: "mx-2",
      md: "mx-3",
      lg: "mx-4",
      xl: "mx-6",
      auto: "mx-auto"
    },
    my: {
      none: "my-0",
      xs: "my-1",
      sm: "my-2",
      md: "my-3",
      lg: "my-4",
      xl: "my-6",
      auto: "my-auto"
    },
    mt: {
      none: "mt-0",
      xs: "mt-1",
      sm: "mt-2",
      md: "mt-3",
      lg: "mt-4",
      xl: "mt-6",
      auto: "mt-auto"
    },
    mr: {
      none: "mr-0",
      xs: "mr-1",
      sm: "mr-2",
      md: "mr-3",
      lg: "mr-4",
      xl: "mr-6",
      auto: "mr-auto"
    },
    mb: {
      none: "mb-0",
      xs: "mb-1",
      sm: "mb-2",
      md: "mb-3",
      lg: "mb-4",
      xl: "mb-6",
      auto: "mb-auto"
    },
    ml: {
      none: "ml-0",
      xs: "ml-1",
      sm: "ml-2",
      md: "ml-3",
      lg: "ml-4",
      xl: "ml-6",
      auto: "ml-auto"
    }
  }
});
function splitBoxProps(props) {
  const { p, px, py, pt, pr, pb, pl, m, mx, my, mt, mr, mb, ml, ...rest } = props;
  return {
    spacingClassName: boxVariants({
      p,
      px,
      py,
      pt,
      pr,
      pb,
      pl,
      m,
      mx,
      my,
      mt,
      mr,
      mb,
      ml
    }),
    rest
  };
}

// src/components/Box/Box.tsx
import { jsx as jsx5 } from "react/jsx-runtime";
var Box = React4.forwardRef(
  ({ className, children, as: Tag = "div", asChild, ...props }, ref) => {
    const { spacingClassName, rest } = splitBoxProps(props);
    const Comp = asChild ? Slot2 : Tag;
    return /* @__PURE__ */ jsx5(Comp, { ref, className: cn(spacingClassName, className), ...rest, children });
  }
);
Box.displayName = "Box";

// src/components/Button/Button.tsx
import * as React5 from "react";
import { Slot as Slot3 } from "@radix-ui/react-slot";
import { cva as cva4 } from "class-variance-authority";

// src/shared/props.ts
var DEFAULT_COMPONENT_SIZE = "medium";

// src/components/Button/Button.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var buttonVariants = cva4(
  [
    "inline-flex items-center justify-center shrink-0 w-min",
    "gap-2 studio:gap-1.5",
    "rounded-md studio:rounded-xl",
    "whitespace-nowrap font-lato text-xs font-semibold",
    "cursor-pointer transition-all",
    "outline-none ring-offset-transparent focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)] data-[state=focus]:ring-2 data-[state=focus]:ring-[var(--border-focus-ring)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0"
  ],
  {
    variants: {
      variant: {
        // Studio renders the no-variant default as a carbon copy of
        // primary — cascade-aware brand fill so it adapts inside
        // `.pairing-{anchor}` ancestors. Surprising given the "neutral"
        // name, but matches the studio design system's default Button.
        // The studio:hover/focus/data-[state=hover] overrides are
        // required to beat the unprefixed light hover bg/border classes.
        neutral: "bg-neutral-1 text-neutral-1 border-1 border-neutral-1 hover:bg-neutral-2 hover:border-neutral-4 focus-visible:bg-neutral-2 focus-visible:border-neutral-4 data-[state=hover]:bg-neutral-2 data-[state=hover]:border-neutral-4 data-[state=focus]:bg-neutral-2 data-[state=focus]:border-neutral-4 disabled:bg-neutral-0 disabled:border-neutral-4 studio:bg-[var(--pairing-shade,var(--bg-brand-1))] studio:text-[var(--pairing-fg,var(--color-content-inverse-primary))] studio:border-transparent studio:hover:bg-[var(--pairing-shade,var(--bg-brand-1))] studio:hover:border-transparent studio:hover:opacity-90 studio:focus-visible:bg-[var(--pairing-shade,var(--bg-brand-1))] studio:focus-visible:border-transparent studio:data-[state=hover]:bg-[var(--pairing-shade,var(--bg-brand-1))] studio:data-[state=hover]:border-transparent studio:data-[state=hover]:opacity-90 studio:data-[state=focus]:bg-[var(--pairing-shade,var(--bg-brand-1))] studio:data-[state=focus]:border-transparent",
        // Light side is a `neutral` clone — placeholder until a light
        // Secondary spec lands. Studio carries the real Secondary visual.
        secondary: "bg-neutral-1 text-neutral-1 border-1 border-neutral-1 hover:bg-neutral-2 hover:border-neutral-4 focus-visible:bg-neutral-2 focus-visible:border-neutral-4 data-[state=hover]:bg-neutral-2 data-[state=hover]:border-neutral-4 data-[state=focus]:bg-neutral-2 data-[state=focus]:border-neutral-4 disabled:bg-neutral-0 disabled:border-neutral-4 studio:bg-surface-secondary studio:text-content-secondary studio:border-transparent studio:hover:bg-surface-secondary-hover studio:hover:border-transparent studio:focus-visible:bg-surface-secondary-hover studio:focus-visible:border-transparent studio:data-[state=hover]:bg-surface-secondary-hover studio:data-[state=hover]:border-transparent studio:data-[state=focus]:bg-surface-secondary-hover studio:data-[state=focus]:border-transparent",
        // Cascade-aware in BOTH themes: inside a `.pairing-{anchor}`
        // ancestor the bg/text adopt the pairing's `shade`/`fg`; outside
        // they fall back to brand-1/highlight-2 under light, brand-1/
        // content-inverse-primary (white) under studio. Hover uses
        // opacity-90 so it works inside or outside a pairing.
        primary: "bg-[var(--pairing-shade,var(--bg-brand-1))] text-[var(--pairing-fg,var(--fg-highlight-2))] hover:opacity-90 data-[state=hover]:opacity-90 disabled:bg-neutral-3 studio:text-[var(--pairing-fg,var(--color-content-inverse-primary))]",
        outline: "bg-transparent text-highlight-1 border-1 border-highlight-3 hover:bg-highlight-2 focus-visible:bg-highlight-2 data-[state=hover]:bg-highlight-2 data-[state=focus]:bg-highlight-2 disabled:border-neutral-3 disabled:text-neutral-4 studio:bg-transparent studio:text-content-secondary studio:border-edge studio:hover:bg-transparent studio:hover:border-edge-hover studio:focus-visible:bg-transparent studio:focus-visible:border-edge-hover studio:data-[state=hover]:bg-transparent studio:data-[state=hover]:border-edge-hover studio:data-[state=focus]:bg-transparent studio:data-[state=focus]:border-edge-hover",
        // Cascade-aware in BOTH themes: text adopts --pairing-fg, hover
        // bg uses --pairing-hover-bg (a 10% currentColor overlay defined
        // on every .pairing-* class — translucent-white on dark
        // pairings, translucent-black on light). The studio overrides
        // shift only the outside-pairing fallbacks: text → content-
        // secondary, hover bg → surface-secondary mortar wash.
        ghost: "text-[var(--pairing-fg,var(--fg-neutral-1))] hover:bg-[var(--pairing-hover-bg,var(--bg-highlight-3))] focus-visible:bg-[var(--pairing-hover-bg,var(--bg-highlight-3))] data-[state=hover]:bg-[var(--pairing-hover-bg,var(--bg-highlight-3))] data-[state=focus]:bg-[var(--pairing-hover-bg,var(--bg-highlight-3))] disabled:bg-transparent disabled:text-neutral-4 studio:text-[var(--pairing-fg,var(--color-content-secondary))] studio:hover:bg-[var(--pairing-hover-bg,var(--color-surface-secondary))] studio:focus-visible:bg-[var(--pairing-hover-bg,var(--color-surface-secondary))] studio:data-[state=hover]:bg-[var(--pairing-hover-bg,var(--color-surface-secondary))] studio:data-[state=focus]:bg-[var(--pairing-hover-bg,var(--color-surface-secondary))]",
        builder: "bg-builder-accent-1 text-builder-accent-1 hover:bg-builder-accent-2 hover:text-builder-accent-2 hover:scale-110 focus-visible:bg-builder-accent-2 focus-visible:text-builder-accent-2 data-[state=hover]:bg-builder-accent-2 data-[state=hover]:text-builder-accent-2 data-[state=focus]:bg-builder-accent-2 data-[state=focus]:text-builder-accent-2 disabled:bg-builder-accent-1 disabled:text-neutral-4 disabled:scale-100"
      },
      size: {
        // Studio sizes are content-driven (h-auto + type-button line
        // height); light uses fixed h-8/10/12. `type-button(-sm)` is a
        // raw CSS class scoped to `[data-theme='studio']` internally —
        // do NOT add a `studio:` prefix to it (it isn't a Tailwind
        // utility, the prefix wouldn't compile).
        small: "px-4 py-2 h-8 max-h-8 [&>svg]:!w-[14px] [&>svg]:!h-[14px] text-xs studio:px-3 studio:py-2 studio:h-auto studio:max-h-none type-button-sm",
        medium: "px-4 py-3 h-10 max-h-10 [&>svg]:!w-[16px] [&>svg]:!h-[16px] text-sm studio:px-3 studio:py-2 studio:h-auto studio:max-h-none type-button",
        large: "px-6 py-3 h-12 max-h-12 [&>svg]:!w-[16px] [&>svg]:!h-[16px] text-sm studio:px-4 studio:py-2.5 studio:h-auto studio:max-h-none type-button"
      },
      modifier: {
        default: "",
        icon: "",
        destructive: ""
      },
      active: {
        true: "",
        false: ""
      },
      loading: {
        true: "opacity-85 pointer-events-none",
        false: ""
      },
      fullWidth: {
        true: "w-full",
        false: ""
      }
    },
    defaultVariants: {
      variant: "neutral",
      modifier: "default",
      size: DEFAULT_COMPONENT_SIZE,
      active: false,
      loading: false
    },
    compoundVariants: [
      {
        modifier: "icon",
        size: "small",
        class: "p-2"
      },
      {
        modifier: "icon",
        size: "medium",
        class: "p-3"
      },
      {
        modifier: "icon",
        size: "large",
        class: "px-4 py-3"
      },
      {
        variant: "ghost",
        class: "bg-transparent"
      },
      {
        variant: "ghost",
        active: true,
        class: "bg-highlight-4 studio:bg-[var(--pairing-hover-bg,var(--color-surface-secondary-hover))]"
      },
      {
        variant: "ghost",
        modifier: "destructive",
        active: true,
        class: "bg-transparent"
      },
      {
        variant: "ghost",
        modifier: "destructive",
        class: "text-destructive-1 hover:bg-destructive-3 focus-visible:bg-destructive-3 data-[state=hover]:bg-destructive-3 data-[state=focus]:bg-destructive-3 disabled:bg-transparent disabled:text-neutral-4"
      },
      {
        variant: "neutral",
        active: true,
        // Studio: cascade-aware pressed state. Outside a pairing falls
        // back to brand-hover (aubergine-950); inside, picks up the
        // pairing's shade. opacity-90 dims so the active state stays
        // visible inside pairings (where bg matches the resting color).
        class: "bg-neutral-1 border-neutral-4 studio:bg-[var(--pairing-shade,var(--color-brand-hover))] studio:border-transparent studio:opacity-90"
      },
      {
        variant: "secondary",
        active: true,
        class: "bg-neutral-1 border-neutral-4"
      },
      {
        variant: "primary",
        modifier: "destructive",
        // Studio overrides REQUIRED — without them, primary's
        // `studio:bg-brand` from the variant block wins the cascade and
        // destructive renders aubergine instead of red. Same gotcha
        // applies to any future primary+modifier compound under studio.
        class: "bg-destructive-1 hover:bg-destructive-2 focus-visible:bg-destructive-2 data-[state=hover]:bg-destructive-2 data-[state=focus]:bg-destructive-2 disabled:bg-neutral-3 studio:bg-danger studio:hover:bg-danger-hover studio:focus-visible:bg-danger-hover studio:data-[state=hover]:bg-danger-hover studio:data-[state=focus]:bg-danger-hover"
      },
      {
        variant: "outline",
        active: true,
        class: "bg-highlight-4 studio:bg-transparent studio:border-edge-hover"
      },
      {
        variant: "outline",
        modifier: "destructive",
        class: "border-destructive-1 hover:border-destructive-2 hover:bg-destructive-3 text-destructive-1 focus-visible:bg-destructive-3 focus-visible:border-destructive-2 data-[state=hover]:border-destructive-2 data-[state=hover]:bg-destructive-3 data-[state=focus]:border-destructive-2 data-[state=focus]:bg-destructive-3 disabled:border-neutral-3 disabled:text-neutral-4"
      },
      {
        variant: "outline",
        modifier: "destructive",
        active: true,
        class: "bg-transparent"
      }
    ]
  }
);
var Button = React5.forwardRef(
  ({
    className,
    variant,
    size = DEFAULT_COMPONENT_SIZE,
    asChild = false,
    modifier,
    active,
    disabled,
    loading,
    fullWidth,
    "data-state": dataState,
    ...props
  }, ref) => {
    const Comp = asChild ? Slot3 : "button";
    return /* @__PURE__ */ jsx6(
      Comp,
      {
        "data-slot": "button",
        "data-state": dataState,
        className: cn(
          buttonVariants({
            variant,
            size,
            modifier,
            active,
            className,
            loading,
            fullWidth
          })
        ),
        disabled,
        ref,
        ...props
      }
    );
  }
);
Button.displayName = "Button";

// src/components/ButtonGroup/ButtonGroup.tsx
import { Slot as Slot4 } from "@radix-ui/react-slot";
import { cva as cva6 } from "class-variance-authority";

// src/components/Separator/Separator.tsx
import * as React6 from "react";
import * as SeparatorPrimitive from "@radix-ui/react-separator";
import { cva as cva5 } from "class-variance-authority";
import { jsx as jsx7 } from "react/jsx-runtime";
var separatorVariants = cva5(
  [
    // Appearance
    "shrink-0 bg-neutral-2"
  ],
  {
    variants: {
      orientation: {
        horizontal: "h-px w-full",
        vertical: "h-full w-px"
      }
    },
    defaultVariants: {
      orientation: "horizontal"
    }
  }
);
var Separator = React6.forwardRef(
  ({ className, orientation = "horizontal", decorative = true, ...props }, ref) => /* @__PURE__ */ jsx7(
    SeparatorPrimitive.Root,
    {
      ref,
      decorative,
      orientation,
      className: cn(separatorVariants({ orientation }), className),
      ...props
    }
  )
);
Separator.displayName = SeparatorPrimitive.Root.displayName;

// src/components/ButtonGroup/ButtonGroup.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var buttonGroupVariants = cva6(
  [
    // Layout
    "flex w-fit items-stretch",
    // Focus management
    "[&>*]:focus-visible:z-10 [&>*]:focus-visible:relative",
    // Select trigger width handling
    "[&>[data-slot=select-trigger]:not([class*='w-'])]:w-fit",
    // Input flex
    "[&>input]:flex-1",
    // Select visibility handling
    "has-[select[aria-hidden=true]:last-child]:[&>[data-slot=select-trigger]:last-of-type]:rounded-r-md",
    // Nested button group gap
    "has-[>[data-slot=button-group]]:gap-2"
  ],
  {
    variants: {
      orientation: {
        horizontal: "[&>*:not(:first-child)]:rounded-l-none [&>*:not(:first-child)]:border-l-0 [&>*:not(:last-child)]:rounded-r-none",
        // `min-w-full` on every child equalizes widths under vertical
        // orientation. Children carry `w-min shrink-0` (from Button)
        // which overrides the group's `items-stretch`, so without
        // this each segment sizes to its own content and the column
        // staircases. min-w-full lets explicit widths still win when
        // a caller sets one.
        vertical: "flex-col [&>*]:min-w-full [&>*:not(:first-child)]:rounded-t-none [&>*:not(:first-child)]:border-t-0 [&>*:not(:last-child)]:rounded-b-none"
      },
      fullWidth: {
        true: "w-full"
      }
    },
    defaultVariants: {
      orientation: "horizontal",
      fullWidth: false
    }
  }
);
function ButtonGroup({
  className,
  orientation = "horizontal",
  fullWidth,
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    "div",
    {
      role: "group",
      "data-slot": "button-group",
      "data-orientation": orientation,
      className: cn(buttonGroupVariants({ orientation, fullWidth }), className),
      ...props
    }
  );
}
function ButtonGroupText({
  className,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot4 : "div";
  return /* @__PURE__ */ jsx8(
    Comp,
    {
      className: cn(
        // Layout
        "flex items-center gap-2",
        // Appearance
        "bg-neutral-1 border-1 border-neutral-2 rounded-md",
        // Spacing
        "px-4",
        // Typography
        "text-neutral-1 text-sm font-medium",
        // Shadow
        "shadow-xs",
        // SVG styles
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className
      ),
      ...props
    }
  );
}
function ButtonGroupSeparator({
  className,
  orientation = "vertical",
  ...props
}) {
  return /* @__PURE__ */ jsx8(
    Separator,
    {
      "data-slot": "button-group-separator",
      orientation,
      className: cn(
        // Appearance
        "bg-neutral-2",
        // Spacing
        "relative !m-0 self-stretch",
        // Orientation-based height
        "data-[orientation=vertical]:h-auto",
        className
      ),
      ...props
    }
  );
}

// src/components/Calendar/Calendar.tsx
import {
  DayPicker
} from "react-day-picker";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faChevronUp } from "@fortawesome/pro-regular-svg-icons/faChevronUp";

// src/components/Icon/Icon.tsx
import {
  FontAwesomeIcon as FontAwesomeIcon2
} from "@fortawesome/react-fontawesome";
import { cva as cva7 } from "class-variance-authority";
import { jsx as jsx9 } from "react/jsx-runtime";
var COLOR_TOKEN_MAP = {
  "foreground-primary": "#1f2f4f",
  "foreground-error": "#dd2270",
  "foreground-warning": "#a66300",
  "foreground-disabled": "#8492ae",
  "foreground-success": "#108484",
  "color-neutral-700": "#425678",
  "color-neutral-0": "#ffffff",
  "background-button-destructive-default": "#dd2270"
};
var iconVariants = cva7("antialiased", {
  variants: {
    size: {
      "x-small": "text-xs",
      // ~12px
      small: "text-sm",
      // ~14px
      regular: "text-base",
      // ~16px
      large: "text-lg",
      // ~18px
      "x-large": "text-xl"
      // ~20px
    }
  },
  defaultVariants: {
    size: "regular"
  }
});
var Icon = ({
  className,
  colorToken,
  size = "regular",
  icon,
  style,
  ...props
}) => {
  if (!icon) {
    return null;
  }
  const colorStyle = colorToken && COLOR_TOKEN_MAP[colorToken] ? { color: COLOR_TOKEN_MAP[colorToken], ...style } : style;
  return /* @__PURE__ */ jsx9(
    FontAwesomeIcon2,
    {
      icon,
      className: cn(iconVariants({ size }), className),
      style: colorStyle,
      ...props
    }
  );
};
Icon.displayName = "Icon";

// src/components/Calendar/Calendar.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
function CalendarChevron({
  orientation = "right",
  className,
  disabled
}) {
  const icon = orientation === "left" ? faChevronLeft : orientation === "up" ? faChevronUp : orientation === "down" ? faChevronDown : faChevronRight;
  return /* @__PURE__ */ jsx10(
    Icon,
    {
      icon,
      size: "small",
      className,
      "aria-hidden": "true",
      style: disabled ? { opacity: 0.5 } : void 0
    }
  );
}
function Calendar({
  className,
  classNames,
  components,
  showOutsideDays = true,
  ...props
}) {
  return /* @__PURE__ */ jsx10(
    DayPicker,
    {
      "data-slot": "calendar",
      showOutsideDays,
      className: cn("p-3", className),
      classNames: {
        months: "relative flex flex-col sm:flex-row gap-4",
        month: "flex flex-col gap-4",
        month_caption: "flex justify-center items-center h-7 relative w-full pt-1",
        caption_label: "font-lato text-sm font-semibold text-neutral-1",
        nav: "absolute inset-x-1 top-1 flex items-center justify-between z-10",
        button_previous: cn(
          "inline-flex items-center justify-center size-7 rounded-md p-0",
          "bg-transparent text-neutral-2",
          "hover:bg-highlight-3 hover:text-neutral-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        button_next: cn(
          "inline-flex items-center justify-center size-7 rounded-md p-0",
          "bg-transparent text-neutral-2",
          "hover:bg-highlight-3 hover:text-neutral-1",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        month_grid: "w-full border-collapse",
        weekdays: "flex",
        weekday: "text-neutral-3 font-lato text-[0.7rem] font-normal w-9 h-9 flex items-center justify-center",
        week: "flex w-full mt-2",
        day: cn(
          "relative p-0 text-center text-sm h-9 w-9 font-lato",
          "focus-within:relative focus-within:z-20"
        ),
        day_button: cn(
          "inline-flex items-center justify-center size-9 rounded-md font-normal",
          "text-neutral-1 cursor-pointer",
          "hover:bg-highlight-3",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
          "aria-selected:opacity-100"
        ),
        selected: cn(
          "[&>button]:bg-brand-1 [&>button]:text-highlight-2",
          "[&>button]:hover:bg-brand-2 [&>button]:hover:text-highlight-2",
          "[&>button]:focus-visible:bg-brand-2"
        ),
        // "Today" gets a brand-colored inset ring + bold weight instead of a
        // background fill. `bg-highlight-4` resolves to `oyster-50` in studio
        // (= the neutral surface), so a fill would be invisible there; a ring
        // also reads more clearly as a "current day" affordance and stays
        // distinct from the range-middle band styling.
        today: "[&>button]:ring-1 [&>button]:ring-inset [&>button]:ring-[var(--bg-brand-1)] [&>button]:font-semibold",
        outside: "[&>button]:text-neutral-3",
        disabled: "[&>button]:text-neutral-4 [&>button]:opacity-50 [&>button]:cursor-not-allowed [&>button]:pointer-events-none",
        // Range band uses a translucent tint of `bg-brand-1` so it stays
        // visible against the popover/neutral surface in both themes.
        // (`bg-highlight-3` resolves to `oyster-50` in studio — the same
        // value as `bg-neutral-1`, which made the band invisible there.)
        range_start: "bg-brand-1/15 rounded-l-md",
        range_middle: cn(
          // Band background sits on the cell so cells join edge-to-edge.
          "bg-brand-1/15 rounded-none",
          // Button is transparent so the cell band shows through, with dark
          // text for readable contrast in both light and studio themes.
          // `!` overrides the `selected` slot's white text + brand-1 fill,
          // which v9 also applies to middle-range days.
          "[&>button]:!bg-transparent [&>button]:!text-neutral-1",
          "[&>button]:hover:!bg-brand-1/25"
        ),
        range_end: "bg-brand-1/15 rounded-r-md",
        hidden: "invisible",
        dropdowns: "flex items-center gap-2",
        dropdown: "bg-transparent text-neutral-1 font-lato text-sm font-semibold cursor-pointer",
        dropdown_root: "relative inline-flex items-center rounded-md hover:bg-highlight-3 px-1",
        ...classNames
      },
      components: {
        Chevron: CalendarChevron,
        ...components
      },
      ...props
    }
  );
}
Calendar.displayName = "Calendar";

// src/components/Checkbox/Checkbox.tsx
import * as React7 from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { FontAwesomeIcon as FontAwesomeIcon3 } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { jsx as jsx11, jsxs as jsxs4 } from "react/jsx-runtime";
var Checkbox = React7.forwardRef(({ className, children, label, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  /* @__PURE__ */ jsxs4("label", { className: "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 flex min-w-0 cursor-pointer flex-row items-center gap-2", children: [
    /* @__PURE__ */ jsx11(
      CheckboxPrimitive.Root,
      {
        ref,
        className: cn(
          // Layout
          "peer shrink-0",
          // Sizing
          "size-4",
          // Appearance
          "rounded",
          // Borders - use neutral-4 for better visibility in dark mode
          "border-neutral-4 border-2",
          // Focus — `data-[focus=true]:` mirrors `:focus-visible` so
          // stories can force the visual state without a play function.
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
          "data-[focus=true]:ring-2 data-[focus=true]:ring-[var(--border-focus-ring)]",
          // Disabled
          "disabled:cursor-not-allowed",
          // Checked state
          "data-[state=checked]:bg-brand-1 data-[state=checked]:border-brand-1",
          // Studio overrides — align with the Figma studio checkbox spec.
          // Unchecked default: aubergine border instead of light neutral.
          // Disabled unchecked: light mortar edge instead of opacity-dimmed
          // aubergine. Disabled + checked: surface-tertiary mortar fill
          // (with matching border) instead of opacity-dimmed aubergine.
          // `border-brand-1` / `border-surface-tertiary` tokens aren't in
          // the studio Tailwind surface, so arbitrary values point at the
          // same semantic CSS vars.
          "studio:border-[var(--bg-brand-1)]",
          "studio:disabled:border-edge",
          "studio:disabled:data-[state=checked]:bg-surface-tertiary",
          "studio:disabled:data-[state=checked]:border-[var(--color-surface-tertiary)]",
          className
        ),
        ...props,
        children: /* @__PURE__ */ jsx11(CheckboxPrimitive.Indicator, { className: "flex items-center justify-center text-[#ffffff]", children: /* @__PURE__ */ jsx11(FontAwesomeIcon3, { icon: faCheck, className: "size-2.5" }) })
      }
    ),
    (children || label) && /* @__PURE__ */ jsx11("span", { className: "text-neutral-1 flex min-w-0 items-center gap-2 overflow-hidden text-sm", children: children || label })
  ] })
));
Checkbox.displayName = "Checkbox";

// src/components/Chip/Chip.tsx
import * as React8 from "react";
import { cva as cva8 } from "class-variance-authority";
import { FontAwesomeIcon as FontAwesomeIcon4 } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { jsx as jsx12, jsxs as jsxs5 } from "react/jsx-runtime";
var LIGHT_BRAND_FALLBACK = "bg-brand-1 text-over-primary";
var chipVariants = cva8(
  [
    "inline-flex items-center justify-center shrink-0",
    "whitespace-nowrap rounded-full",
    // Typography — `text-xs font-semibold` is the light-theme baseline
    // (Lato semibold). The studio type ramp is applied via the
    // `typography` variant below; those `.type-*` CSS rules are scoped to
    // `[data-theme='studio']`, so they're inert under light (no `studio:`
    // prefix needed — and it wouldn't compile for a non-Tailwind class).
    "text-xs font-semibold"
  ],
  {
    variants: {
      variant: {
        // ---- Semantic role variants ----
        // The default variant. Reads from the `--pairing-*` cascade vars
        // when the chip is inside a `.pairing-{anchor}` container so a
        // naked `<Chip>` auto-styles to the parent's pairing without any
        // prop. Outside a pairing the cascade vars are unset and the
        // chip falls back to its baked-in neutral colors.
        //
        // The "inverted" mapping — bg = pairing-fg, text = pairing-bg —
        // gives high contrast on every pairing (a dark chip on a light
        // card, a light chip on a dark card) regardless of anchor.
        neutral: cn(
          // Light: cascade vars never resolve here (pairing classes are
          // studio-only), so this row always lands on legacy neutral.
          "bg-[var(--pairing-fg,var(--bg-neutral-1))] text-[var(--pairing-bg,var(--fg-neutral-3))]",
          // Studio outside a pairing: fall back to surface-bright-tertiary
          // + content-tertiary. Inside: pick up pairing-fg / pairing-bg.
          "studio:bg-[var(--pairing-fg,var(--color-surface-bright-tertiary))] studio:text-[var(--pairing-bg,var(--color-content-tertiary))]"
        ),
        brand: cn(
          // Light: solid primary brand fill (blurple-700) + white text.
          LIGHT_BRAND_FALLBACK,
          // Studio: Aubergine 50 / 700 (the hyperlink-compliant pair).
          "studio:bg-brand-subtle studio:text-content-brand-secondary"
        ),
        positive: cn(
          // Light: no positive-subtle semantic exists; reach for the
          // Emerald 50 / 800 primitives via arbitrary values.
          "bg-[var(--sb-color-emerald-50)] text-[var(--sb-color-emerald-800)]",
          // Studio: Sprout 50 / 900.
          "studio:bg-positive-subtle studio:text-content-positive-primary"
        ),
        warning: cn(
          // Light: Amber 50 / 900 via the existing alert semantic pair.
          "bg-alert-1 text-alert-2",
          // Studio: Ember 50 / 900.
          "studio:bg-warning-subtle studio:text-content-warning-primary"
        ),
        danger: cn(
          // Light: Rose 50 / 700 via the existing destructive pair.
          "bg-destructive-3 text-destructive-1",
          // Studio: Vermillion 50 / 900.
          "studio:bg-danger-subtle studio:text-content-danger-primary"
        ),
        // ---- Tag ----
        // The token/option pill used inside MultiSelect (and similar
        // tag pickers). It deliberately diverges from the semantic chips
        // on every studio axis — secondary (mortar) surface, primary
        // (near-black) content, the `label` type ramp (applied via the
        // typography resolver in the component), and a tighter radius —
        // so it lives as its own variant instead of a stack of per-call
        // overrides. Light falls back to a plain neutral pill.
        tag: cn(
          // Tighter corners than the default `rounded-full` pill. The studio
          // design specifies a 5px radius; there is no radius token at that
          // step, so this mirrors the design's own arbitrary `rounded-[5px]`.
          "rounded-[5px]",
          // Light fallback — neutral surface + tertiary text.
          "bg-[var(--bg-neutral-1)] text-[var(--fg-neutral-3)]",
          // Studio — secondary surface (mortar-200) + primary content (#0f0f0f).
          "studio:bg-surface-secondary studio:text-content-primary"
        ),
        // ---- Pairing anchor variants — soft / light (6) ----
        // Studio renders the pairing's bg + fg as the chip colors. Light
        // falls back to neutral since the pairings are studio-only.
        glacier: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-glacier-bg studio:text-pairing-soft-light-glacier-fg"
        ),
        petal: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-petal-bg studio:text-pairing-soft-light-petal-fg"
        ),
        thistle: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-thistle-bg studio:text-pairing-soft-light-thistle-fg"
        ),
        peach: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-peach-bg studio:text-pairing-soft-light-peach-fg"
        ),
        sandstone: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-sandstone-bg studio:text-pairing-soft-light-sandstone-fg"
        ),
        silt: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-light-silt-bg studio:text-pairing-soft-light-silt-fg"
        ),
        // ---- Pairing anchor variants — soft / dark (5) ----
        submerge: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-dark-submerge-bg studio:text-pairing-soft-dark-submerge-fg"
        ),
        aubergine: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-dark-aubergine-bg studio:text-pairing-soft-dark-aubergine-fg"
        ),
        raspberry: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-dark-raspberry-bg studio:text-pairing-soft-dark-raspberry-fg"
        ),
        garnet: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-dark-garnet-bg studio:text-pairing-soft-dark-garnet-fg"
        ),
        loam: cn(
          LIGHT_BRAND_FALLBACK,
          "studio:bg-pairing-soft-dark-loam-bg studio:text-pairing-soft-dark-loam-fg"
        )
      },
      size: {
        md: "px-2 py-1.5",
        sm: "px-1.5 py-1"
      },
      // Studio type ramp. Kept as its own axis so exactly one `.type-*`
      // class is ever present — those rules are unlayered + equal
      // specificity, so two on one element would resolve by source order
      // rather than intent. `button` is the chip default; `tag` resolves
      // to `label` in the component.
      typography: {
        button: "type-button-sm",
        label: "type-label"
      }
    },
    defaultVariants: {
      variant: "neutral",
      size: "md"
    }
  }
);
var Chip = React8.forwardRef(
  ({
    className,
    variant,
    size,
    typography,
    children,
    onRemove,
    removeLabel,
    ...props
  }, ref) => {
    const resolvedTypography = typography ?? (variant === "tag" ? "label" : "button");
    return /* @__PURE__ */ jsxs5(
      "span",
      {
        ref,
        className: cn(
          chipVariants({ variant, size, typography: resolvedTypography }),
          // Tighten the trailing edge when a remove button follows so the
          // × sits closer to the pill edge than the label padding.
          onRemove && "gap-1 pr-1",
          className
        ),
        ...props,
        children: [
          children,
          onRemove ? /* @__PURE__ */ jsx12(
            "button",
            {
              type: "button",
              "aria-label": removeLabel ?? "Remove",
              onClick: (e) => {
                e.stopPropagation();
                onRemove();
              },
              onKeyDown: (e) => e.stopPropagation(),
              className: cn(
                // Layout
                "flex items-center justify-center",
                // Sizing
                "size-3.5 shrink-0 rounded-full",
                // Appearance
                "opacity-60 hover:opacity-100",
                // Interactions
                "cursor-pointer outline-none focus-visible:opacity-100"
              ),
              children: /* @__PURE__ */ jsx12(FontAwesomeIcon4, { icon: faXmark, className: "size-3" })
            }
          ) : null
        ]
      }
    );
  }
);
Chip.displayName = "Chip";

// src/components/ColorPicker/ColorPicker.tsx
import { useState as useState15, useEffect as useEffect14, useRef as useRef6, useMemo as useMemo5, forwardRef as forwardRef11 } from "react";
import debounce from "lodash.debounce";
import { Portal as Portal3 } from "@radix-ui/react-portal";

// src/components/FloatingPanel/FloatingPanel.tsx
import React10, {
  useEffect as useEffect6,
  useLayoutEffect as useLayoutEffect2,
  useState as useState5,
  useCallback as useCallback3
} from "react";
import { Presence } from "@radix-ui/react-presence";
import { faAngleRight } from "@fortawesome/pro-regular-svg-icons/faAngleRight";
import { faAngleLeft } from "@fortawesome/pro-regular-svg-icons/faAngleLeft";

// src/hooks/use-floating-window/use-floating-window.ts
import { useState as useState2, useEffect as useEffect2, useRef as useRef2, useCallback } from "react";
import {
  useFloating,
  flip,
  shift,
  offset as offsetMiddleware,
  autoPlacement,
  detectOverflow,
  autoUpdate
} from "@floating-ui/react";

// src/hooks/use-floating-window/animation-throttle.ts
function animationThrottle(callback) {
  let token = null;
  let lastArgs = null;
  const invoke = () => {
    callback(...lastArgs ?? []);
    token = null;
  };
  const result = (...args) => {
    lastArgs = args;
    if (!token) {
      token = requestAnimationFrame(invoke);
    }
  };
  result.cancel = () => {
    if (token) {
      cancelAnimationFrame(token);
    }
  };
  return result;
}
var animation_throttle_default = animationThrottle;

// src/hooks/use-floating-window/use-floating-window.ts
var EVENT_NAME_DRAG_START = "floating-window-drag-start";
var EVENT_NAME_DRAG_END = "floating-window-drag-end";
var BASE_Z_INDEX = 10;
var DRAG_Z_INDEX_OFFSET = 10;
var panelOrder = [];
function renumber() {
  panelOrder.forEach((entry, i) => {
    entry.zIndex = BASE_Z_INDEX + i;
    if (entry.el) {
      entry.el.style.zIndex = String(entry.zIndex);
    }
  });
}
function bringPanelToFront(entry) {
  const idx = panelOrder.indexOf(entry);
  if (idx === -1) {
    return;
  }
  panelOrder.splice(idx, 1);
  panelOrder.push(entry);
  renumber();
}
function register(entry) {
  panelOrder.push(entry);
  renumber();
}
function unregister(entry) {
  const idx = panelOrder.indexOf(entry);
  if (idx !== -1) {
    panelOrder.splice(idx, 1);
  }
}
function syncInitialPosition(pos = {}) {
  return {
    name: "syncInitialPosition",
    fn({ x, y, ...rest }) {
      return {
        x: pos?.x ?? x,
        y: pos?.y ?? y,
        ...rest
      };
    }
  };
}
function checkOverflowBoundary(boundary) {
  return {
    name: "checkOverflowBoundary",
    async fn(state) {
      const overflowOptions = boundary || document?.documentElement ? { boundary: boundary || document.documentElement } : {};
      const overflow = await detectOverflow(state, overflowOptions);
      let { x, y } = state;
      const boundaryRect = boundary?.getBoundingClientRect();
      if (overflow.left > 0) {
        x = boundaryRect?.left ?? 0;
      } else if (overflow.right > 0) {
        x = state.x - overflow.right;
      }
      if (overflow.top > 0) {
        y = boundaryRect?.top ?? 0;
      } else if (overflow.bottom > 0) {
        y = state.y - overflow.bottom;
      }
      if (x !== state.x || y !== state.y) {
        return { ...state, x, y };
      }
      return {};
    }
  };
}
var isOverlapping = (ref, floating) => {
  if (!ref || !floating) return false;
  const refElement = ref.getBoundingClientRect();
  const floatingElement = floating.getBoundingClientRect();
  return !(refElement.top > floatingElement.bottom || refElement.right < floatingElement.left || refElement.bottom < floatingElement.top || refElement.left > floatingElement.right);
};
var useFloatingWindow = ({
  initialPosition,
  offset = 0,
  strategy = "absolute",
  parentEl,
  placement,
  disableAutoUpdate = false,
  disableOverlapCheck = true,
  disableOverflowBoundary = false
} = {}) => {
  const currentPosition = useRef2(initialPosition);
  const draggedPosition = useRef2(null);
  const entryRef = useRef2({ el: null, zIndex: BASE_Z_INDEX });
  const [pressed, setPressed] = useState2(false);
  useEffect2(() => {
    const entry = entryRef.current;
    register(entry);
    return () => {
      unregister(entry);
    };
  }, []);
  const middleware = [offsetMiddleware(offset)];
  if (!disableOverflowBoundary) {
    middleware.push(checkOverflowBoundary(parentEl));
  }
  if (initialPosition) {
    middleware.push(syncInitialPosition(initialPosition));
  }
  if (placement) {
    middleware.push(
      autoPlacement({
        ...placement,
        ...parentEl && { boundary: parentEl }
      })
    );
  } else {
    middleware.push(flip(parentEl ? { boundary: parentEl } : {}));
  }
  if (parentEl || !disableOverflowBoundary) {
    middleware.push(shift(parentEl ? { boundary: parentEl } : {}));
  }
  const floating = useFloating({
    placement: "bottom",
    middleware,
    strategy,
    whileElementsMounted: disableAutoUpdate ? void 0 : autoUpdate
  });
  if (floating.x === null && floating.y === null) {
    floating.x = initialPosition?.x ?? 0;
    floating.y = initialPosition?.y ?? 0;
  }
  if (draggedPosition.current) {
    floating.x = draggedPosition.current.x;
    floating.y = draggedPosition.current.y;
  }
  const bringToFront = useCallback(() => {
    bringPanelToFront(entryRef.current);
  }, []);
  const containerRef = useCallback(
    (node) => {
      entryRef.current.el = node;
      floating.refs.setFloating(node);
      if (node) {
        node.style.zIndex = String(entryRef.current.zIndex);
      }
    },
    [floating.refs]
  );
  const handleMouseDown = () => {
    bringToFront();
    currentPosition.current = {
      x: floating.x ?? void 0,
      y: floating.y ?? void 0
    };
    setPressed(true);
  };
  const handleMouseUp = () => {
    const floatingElement = floating.refs.floating.current;
    const referenceElement = floating.refs.reference.current;
    if (!disableOverlapCheck && isOverlapping(referenceElement, floatingElement) && floatingElement) {
      floating.x = currentPosition.current?.x ?? 0;
      floating.y = currentPosition.current?.y ?? 0;
      floatingElement.style.top = `${floating.y}px`;
      floatingElement.style.left = `${floating.x}px`;
    }
    draggedPosition.current = {
      x: floating.x ?? 0,
      y: floating.y ?? 0
    };
    setPressed(false);
  };
  const getBoundedPositions = (container, event) => {
    const containerRect = container.getBoundingClientRect();
    let minX = 0;
    let minY = 0;
    let maxX;
    let maxY;
    if (parentEl && strategy === "fixed") {
      const parentRect = parentEl.getBoundingClientRect();
      minX = parentRect.left;
      minY = parentRect.top;
      maxX = parentRect.left + parentRect.width - containerRect.width;
      maxY = parentRect.top + parentRect.height - containerRect.height;
    } else if (parentEl) {
      maxX = parentEl.clientWidth - containerRect.width;
      maxY = parentEl.clientHeight - containerRect.height;
    } else {
      maxX = window.innerWidth - containerRect.width;
      maxY = window.innerHeight - containerRect.height;
    }
    return {
      x: Math.max(minX, Math.min((floating.x ?? 0) + event.movementX, maxX)),
      y: Math.max(minY, Math.min((floating.y ?? 0) + event.movementY, maxY))
    };
  };
  const handleMouseMove = animation_throttle_default((event) => {
    if (!floating.refs.floating.current) {
      return;
    }
    const container = floating.refs.floating.current;
    const pos = getBoundedPositions(container, event);
    floating.x = pos.x;
    floating.y = pos.y;
    draggedPosition.current = { x: pos.x, y: pos.y };
    container.style.top = `${floating.y}px`;
    container.style.left = `${floating.x}px`;
  });
  useEffect2(() => {
    if (!pressed) {
      return;
    }
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      handleMouseMove.cancel();
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [pressed]);
  useEffect2(() => {
    window.dispatchEvent(
      new CustomEvent(pressed ? EVENT_NAME_DRAG_START : EVENT_NAME_DRAG_END)
    );
  }, [pressed]);
  const baseZIndex = entryRef.current.zIndex;
  return {
    pressed,
    position: { x: floating.x, y: floating.y },
    positionReference: floating.refs.setPositionReference,
    containerRef,
    containerElement: floating.refs.floating?.current ?? null,
    bringToFront,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp
    },
    dragStyle: {
      userSelect: pressed ? "none" : "auto",
      cursor: "grab",
      ...pressed && { cursor: "grabbing" }
    },
    containerStyle: {
      position: floating.strategy,
      top: floating.y ?? 0,
      left: floating.x ?? 0,
      zIndex: baseZIndex,
      ...pressed && { zIndex: baseZIndex + DRAG_Z_INDEX_OFFSET }
    }
  };
};
var use_floating_window_default = useFloatingWindow;

// src/hooks/use-floating-window/use-floating-window-listener.ts
import { useState as useState3, useEffect as useEffect3 } from "react";
var useFloatingWindowListener = () => {
  const [isFloating, setIsFloating] = useState3(false);
  useEffect3(() => {
    const handleDragStart = () => setIsFloating(true);
    const handleDragEnd = () => setIsFloating(false);
    window.addEventListener("floating-window-drag-start", handleDragStart);
    window.addEventListener("floating-window-drag-end", handleDragEnd);
    return () => {
      window.removeEventListener("floating-window-drag-start", handleDragStart);
      window.removeEventListener("floating-window-drag-end", handleDragEnd);
    };
  }, []);
  return isFloating;
};

// src/hooks/use-click-outside.ts
import { useEffect as useEffect4 } from "react";
var OVERLAY_SELECTOR = '[data-radix-popper-content-wrapper],[role="dialog"]:not([aria-modal="false"])';
var useClickOutside = (refs, onClickOutside) => {
  useEffect4(() => {
    if (!refs || refs.length === 0) {
      return;
    }
    const validRefs = refs.filter((ref) => ref !== null);
    if (validRefs.length === 0) {
      return;
    }
    const handler = (event) => {
      const path = event.composedPath();
      const [target] = path;
      const inside = validRefs.some((ref) => {
        if (!(target instanceof Node)) return false;
        return target === ref || ref.contains(target);
      });
      const inOverlay = path.some(
        (el) => el instanceof Element && el.matches(OVERLAY_SELECTOR)
      );
      if (!inside && !inOverlay) {
        onClickOutside?.(event);
      }
    };
    document.addEventListener("click", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
    };
  }, [refs, onClickOutside]);
};

// src/components/FloatingPanel/DragHandlersContext.ts
import { createContext } from "react";
var DragHandlersContext = createContext(null);

// src/components/FloatingPanel/Navigation.tsx
import * as React9 from "react";
import { jsx as jsx13 } from "react/jsx-runtime";
var NavigationContext = React9.createContext({
  toPage: () => null,
  goBack: () => null,
  getData: () => ({})
});
var useNavigation = () => {
  const navigationContext = React9.useContext(NavigationContext);
  return navigationContext;
};
var Navigation = ({ children, innerRef }) => {
  const [pageKey, setPageKey] = React9.useState(crypto.randomUUID());
  const [activePage, setActivePage] = React9.useState(0);
  const [previousPages, setPreviousPage] = React9.useState([]);
  const [activePageData, setPageData] = React9.useState({});
  const toPage = React9.useCallback(
    (index, pageData) => {
      setActivePage(index);
      setPageData(pageData ?? {});
      if (index === activePage) setPageKey(crypto.randomUUID());
    },
    [activePage]
  );
  const goBack = React9.useCallback(() => {
    const newHistory = previousPages.slice(0, -1);
    setPreviousPage(newHistory);
    toPage(newHistory.at(-1) ?? 0);
  }, [previousPages, toPage]);
  const getData = React9.useCallback(() => activePageData, [activePageData]);
  React9.useEffect(() => {
    if (activePage !== previousPages.at(-1)) {
      setPreviousPage([...previousPages, activePage]);
    }
  }, [activePage, previousPages]);
  const childrenArray = React9.useMemo(
    () => Array.isArray(children) ? children : [children],
    [children]
  );
  const value = React9.useMemo(() => {
    return {
      goBack,
      toPage,
      getData
    };
  }, [goBack, toPage, getData]);
  React9.useImperativeHandle(innerRef, () => value);
  return /* @__PURE__ */ jsx13(NavigationContext.Provider, { value, children: React9.cloneElement(childrenArray[activePage], {
    key: pageKey
  }) });
};

// src/components/FloatingPanel/FloatingPanel.tsx
import { Fragment as Fragment3, jsx as jsx14, jsxs as jsxs6 } from "react/jsx-runtime";
var mergeRefs = (refs) => {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };
};
var panelSizeToWidth = {
  small: "w-[350px]",
  medium: "w-[376px]",
  large: "w-[460px]"
};
var FloatingPanelLabelContext = React10.createContext(() => {
});
var BasePanel = React10.forwardRef(
  ({
    open: open2,
    openAnimation = true,
    size,
    className,
    style,
    children,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    ...props
  }, ref) => {
    const [headerTitleId, setHeaderTitleId] = useState5();
    const resolvedLabelledBy = ariaLabelledBy ?? (ariaLabel ? void 0 : headerTitleId);
    return /* @__PURE__ */ jsx14(
      "div",
      {
        ref,
        "data-state": open2 ? "open" : "closed",
        role: "dialog",
        "aria-modal": "false",
        "aria-label": ariaLabel,
        "aria-labelledby": resolvedLabelledBy,
        className: cn(
          // Layout
          "flex flex-col",
          // Sizing
          "min-w-80 h-fit max-h-[90vh] max-w-lg",
          // Appearance
          "bg-neutral-0 rounded-md shadow-md",
          // Borders
          "border-neutral-1 border",
          // Animations - Enter
          openAnimation && "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          // Animations - Exit
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          // Transitions
          "opacity-100 transition-opacity duration-200",
          panelSizeToWidth[size],
          className
        ),
        style,
        ...props,
        children: /* @__PURE__ */ jsx14(FloatingPanelLabelContext.Provider, { value: setHeaderTitleId, children })
      }
    );
  }
);
var FloatingVariantPanel = React10.forwardRef(
  ({
    dragOptions,
    children,
    className = "",
    open: open2 = false,
    openAnimation = true,
    size = "medium",
    style: styleProps = {},
    targetZIndex = 0,
    onClickOutside,
    onPointerDownCapture,
    ...unhandledProps
  }, ref) => {
    const {
      dragHandlers,
      dragStyle,
      positionReference,
      containerRef,
      containerStyle,
      containerElement,
      bringToFront
    } = use_floating_window_default({
      ...dragOptions,
      parentEl: dragOptions?.getParentEl?.() || void 0
    });
    const handlePointerDownCapture = useCallback3(
      (event) => {
        bringToFront();
        onPointerDownCapture?.(event);
      },
      [bringToFront, onPointerDownCapture]
    );
    const contextValue = React10.useMemo(
      () => ({ dragHandlers, dragStyle }),
      [dragHandlers, dragStyle]
    );
    const positionReferenceEl = dragOptions?.getPositionReferenceElement?.() ?? null;
    useLayoutEffect2(() => {
      if (positionReferenceEl) {
        positionReference(positionReferenceEl);
      }
    }, [positionReference, positionReferenceEl]);
    useLayoutEffect2(() => {
      if (open2) {
        bringToFront();
      }
    }, [open2, bringToFront]);
    const bottomGap = "var(--spacing-large, 16px)";
    const topPosition = containerStyle?.top ?? 0;
    const maxHeight = typeof topPosition === "number" ? `calc(100vh - ${topPosition}px - ${bottomGap})` : void 0;
    const style = {
      zIndex: targetZIndex + 1,
      ...containerStyle,
      maxHeight,
      ...styleProps
    };
    useClickOutside([containerElement, positionReferenceEl], onClickOutside);
    return /* @__PURE__ */ jsx14(DragHandlersContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx14(Presence, { present: open2, children: /* @__PURE__ */ jsx14(
      BasePanel,
      {
        ref: mergeRefs([ref, containerRef]),
        style,
        className,
        open: open2,
        openAnimation,
        size,
        onPointerDownCapture: handlePointerDownCapture,
        ...unhandledProps,
        children
      }
    ) }) });
  }
);
function getCollapseButtonIcon(expanded, placement) {
  if (placement === "left") {
    return expanded ? faAngleLeft : faAngleRight;
  }
  return expanded ? faAngleRight : faAngleLeft;
}
var FixedVariantPanel = React10.forwardRef(
  ({
    children,
    className = "",
    collapsible = false,
    open: open2 = false,
    openAnimation = true,
    expanded: expandedProp = true,
    onExpandedChange,
    placement = { x: "left", y: "top" },
    position = { x: 0, y: 0 },
    size = "medium",
    style: styleProps = {},
    targetZIndex = 0,
    ...unhandledProps
  }, ref) => {
    const [expanded, setExpanded] = useState5(expandedProp);
    useEffect6(() => {
      setExpanded(expandedProp);
    }, [expandedProp, setExpanded]);
    const toggleExpanded = useCallback3(() => {
      setExpanded(!expanded);
      onExpandedChange?.(!expanded);
    }, [setExpanded, onExpandedChange, expanded]);
    const xPosition = typeof position.x === "string" ? position.x : `${position.x}px`;
    const yPosition = typeof position.y === "string" ? position.y : `${position.y}px`;
    const bottomGap = "var(--spacing-large, 16px)";
    const maxHeight = placement.y === "top" ? `calc(100vh - ${yPosition} - ${bottomGap})` : void 0;
    const style = {
      zIndex: targetZIndex + 1,
      [placement.x]: xPosition,
      [placement.y]: yPosition,
      maxHeight,
      ...styleProps,
      ...expanded ? {} : { minWidth: "unset", width: 0, borderWidth: 0, [placement.x]: 0 }
    };
    return /* @__PURE__ */ jsx14(Presence, { present: open2, children: /* @__PURE__ */ jsx14(
      BasePanel,
      {
        ref,
        className: cn("absolute", className),
        style,
        open: open2,
        openAnimation,
        size,
        ...unhandledProps,
        children: /* @__PURE__ */ jsxs6(Fragment3, { children: [
          collapsible ? /* @__PURE__ */ jsx14(
            Button,
            {
              "aria-label": expanded ? "Collapse" : "Expand",
              onClick: toggleExpanded,
              modifier: "icon",
              className: cn(
                "absolute",
                "bg-neutral-0",
                "w-[30px]",
                "top-[10px]",
                "p-[10px]",
                placement.x === "left" ? "rounded-l-none" : "rounded-r-none",
                placement.x === "left" ? "left-[100%]" : "right-[100%]"
              ),
              children: /* @__PURE__ */ jsx14(Icon, { icon: getCollapseButtonIcon(expanded, placement.x) })
            }
          ) : null,
          expanded ? children : null
        ] })
      }
    ) });
  }
);
var FloatingPanel = React10.forwardRef(
  ({ variant = "floating", children, ...props }, ref) => {
    return variant === "floating" ? /* @__PURE__ */ jsx14(FloatingVariantPanel, { ref, ...props, children }) : /* @__PURE__ */ jsx14(FixedVariantPanel, { ref, ...props, children });
  }
);
FloatingPanel.displayName = "FloatingPanel";
var FloatingPanelNavigation = Navigation;
var useFloatingPanelNavigation = useNavigation;

// src/components/FloatingPanel/FloatingPanelHeader.tsx
import * as React11 from "react";
import { FontAwesomeIcon as FontAwesomeIcon5 } from "@fortawesome/react-fontawesome";
import { faXmarkLarge } from "@fortawesome/pro-regular-svg-icons/faXmarkLarge";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons/faArrowLeft";
import { jsx as jsx15, jsxs as jsxs7 } from "react/jsx-runtime";
var FloatingPanelHeader = ({
  title,
  onBack,
  onClose,
  actions
}) => {
  const dragContext = React11.useContext(DragHandlersContext);
  const dragProps = dragContext ? {
    ...dragContext.dragHandlers,
    style: dragContext.dragStyle
  } : {};
  const [titleId] = React11.useState(
    () => `floating-panel-title-${Math.random().toString(36).slice(2, 10)}`
  );
  const registerTitleId = React11.useContext(FloatingPanelLabelContext);
  React11.useEffect(() => {
    registerTitleId(titleId);
    return () => registerTitleId(void 0);
  }, [titleId, registerTitleId]);
  return /* @__PURE__ */ jsxs7(
    "div",
    {
      className: cn(
        // Layout
        "flex items-center",
        // Spacing
        "gap-1 p-3",
        // Typography
        "font-lato text-neutral-1",
        // Conditional spacing
        {
          "p-1": onBack
        }
      ),
      ...dragProps,
      "aria-label": "Floating panel header",
      children: [
        onBack && /* @__PURE__ */ jsx15(
          Button,
          {
            onClick: onBack,
            "aria-label": "Go back",
            variant: "ghost",
            size: "small",
            modifier: "icon",
            children: /* @__PURE__ */ jsx15(FontAwesomeIcon5, { icon: faArrowLeft })
          }
        ),
        /* @__PURE__ */ jsx15(
          "h2",
          {
            id: titleId,
            className: cn(
              // Layout
              "grow content-center overflow-hidden",
              // Typography (string titles only — ReactNode titles bring their own)
              typeof title === "string" && "text-ellipsis whitespace-nowrap text-base font-semibold leading-normal"
            ),
            children: title
          }
        ),
        /* @__PURE__ */ jsxs7(
          "div",
          {
            className: cn(
              // Layout
              "flex content-center justify-end",
              // Interactions
              "cursor-default"
            ),
            children: [
              actions,
              onClose && /* @__PURE__ */ jsx15(
                Button,
                {
                  "aria-label": "Close",
                  onClick: onClose,
                  variant: "ghost",
                  modifier: "icon",
                  size: "small",
                  children: /* @__PURE__ */ jsx15(FontAwesomeIcon5, { icon: faXmarkLarge })
                }
              )
            ]
          }
        )
      ]
    }
  );
};

// src/components/FloatingPanel/FloatingPanelContent.tsx
import { jsx as jsx16 } from "react/jsx-runtime";
var FloatingPanelContent = ({ children }) => {
  return /* @__PURE__ */ jsx16("div", { className: "min-h-0 flex-1 overflow-y-auto overflow-x-clip px-3 pb-4 pt-2", children });
};

// src/components/FloatingPanel/FloatingPanelStickyHeader.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var FloatingPanelStickyHeader = ({
  children
}) => {
  return /* @__PURE__ */ jsx17("div", { className: "sticky -top-2 -mt-2 -mx-3 px-3 pt-2 z-10 bg-neutral-0", children });
};

// src/components/ColorPicker/ColorSpaces/conversions.ts
import tinycolor from "tinycolor2";
var toRgb = (color) => tinycolor(color).toRgb();
var toHex = (colorRgb) => tinycolor(colorRgb).toHex();
var toHex8 = (colorRgb) => tinycolor(colorRgb).toHex8String();
var toHsl = (colorRgb) => tinycolor(colorRgb).toHsl();
var { round } = Math;
var roundAlpha = (a = 0) => round(a * 100);
var hexToRgb = (hex, alpha) => ({
  ...tinycolor(`#${hex}`).toRgb(),
  a: alpha / 100
});

// src/components/ColorPicker/ColorPickerContent.tsx
import { useState as useState14, useEffect as useEffect13 } from "react";

// src/components/Flex/Flex.tsx
import * as React12 from "react";
import { cva as cva9 } from "class-variance-authority";
import { jsx as jsx18 } from "react/jsx-runtime";
var flexVariants = cva9([], {
  variants: {
    direction: {
      row: "flex-row",
      column: "flex-col",
      "row-reverse": "flex-row-reverse",
      "column-reverse": "flex-col-reverse"
    },
    align: {
      start: "items-start",
      center: "items-center",
      end: "items-end",
      stretch: "items-stretch",
      baseline: "items-baseline"
    },
    justify: {
      start: "justify-start",
      center: "justify-center",
      end: "justify-end",
      between: "justify-between",
      around: "justify-around",
      evenly: "justify-evenly"
    },
    gap: {
      none: "gap-0",
      xs: "gap-1",
      sm: "gap-2",
      md: "gap-3",
      lg: "gap-4",
      xl: "gap-6"
    },
    wrap: {
      true: "flex-wrap",
      false: "flex-nowrap"
    },
    inline: {
      true: "inline-flex",
      false: "flex"
    }
  },
  defaultVariants: {
    direction: "row",
    align: "stretch",
    justify: "start",
    gap: "none",
    wrap: false,
    inline: false
  }
});
var Flex = React12.forwardRef(
  ({
    className,
    direction,
    align,
    justify,
    gap,
    wrap,
    inline,
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx18(
      Box,
      {
        ref,
        className: cn(
          flexVariants({ direction, align, justify, gap, wrap, inline }),
          className
        ),
        ...props,
        children
      }
    );
  }
);
Flex.displayName = "Flex";

// src/components/ColorPicker/CustomColorPicker.tsx
import { Alpha, Hue, Saturation } from "react-color/lib/components/common";
import { CustomPicker as custom } from "react-color";

// src/components/ColorPicker/ColorSpaces/types.ts
var DEFAULT_SPACE = "HEX";

// src/components/ColorPicker/ColorSpaces/Selector.tsx
import { useState as useState12 } from "react";

// src/components/Select/Select.tsx
import * as React13 from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { FontAwesomeIcon as FontAwesomeIcon6 } from "@fortawesome/react-fontawesome";
import { faCheck as faCheck2 } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faCaretDown as faCaretDown2 } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { faChevronUp as faChevronUp2 } from "@fortawesome/pro-regular-svg-icons/faChevronUp";
import { faChevronDown as faChevronDown2 } from "@fortawesome/pro-regular-svg-icons/faChevronDown";

// src/hooks/use-shadow-dismiss.ts
import { useCallback as useCallback4, useEffect as useEffect8, useState as useState7 } from "react";
var DATA_ATTR = "radixInstance";
var nextId = 0;
function useShadowDismiss({
  open: controlledOpen,
  onOpenChange,
  defaultOpen
}) {
  const [instanceId] = useState7(() => {
    nextId += 1;
    return `radix-${nextId}`;
  });
  const [internalOpen, setInternalOpen] = useState7(defaultOpen ?? false);
  const handleOpenChange = useCallback4(
    (next) => {
      if (controlledOpen === void 0) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange]
  );
  const open2 = controlledOpen ?? internalOpen;
  useEffect8(() => {
    if (!open2) return void 0;
    const handlePointerDown = (e) => {
      const path = e.composedPath();
      const isInside = path.some(
        (el) => el instanceof HTMLElement && el.dataset[DATA_ATTR] === instanceId
      );
      if (!isInside) {
        handleOpenChange(false);
      }
    };
    const timer = window.setTimeout(() => {
      document.body.style.pointerEvents = "";
      document.addEventListener("pointerdown", handlePointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [open2, handleOpenChange, instanceId]);
  return { open: open2, onOpenChange: handleOpenChange, instanceId };
}

// src/components/Select/Select.tsx
import { jsx as jsx19, jsxs as jsxs8 } from "react/jsx-runtime";
var SelectInstanceContext = React13.createContext(null);
function Select({
  open: controlledOpen,
  onOpenChange,
  defaultOpen,
  ...props
}) {
  const {
    open: open2,
    onOpenChange: handleOpenChange,
    instanceId
  } = useShadowDismiss({
    open: controlledOpen,
    onOpenChange,
    defaultOpen
  });
  const contextValue = React13.useMemo(
    () => ({ setOpen: handleOpenChange, instanceId }),
    [handleOpenChange, instanceId]
  );
  return /* @__PURE__ */ jsx19(SelectInstanceContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx19(
    SelectPrimitive.Root,
    {
      "data-slot": "select",
      open: open2,
      onOpenChange: handleOpenChange,
      ...props
    }
  ) });
}
function SelectGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx19(SelectPrimitive.Group, { "data-slot": "select-group", ...props });
}
function SelectValue({
  ...props
}) {
  return /* @__PURE__ */ jsx19(SelectPrimitive.Value, { "data-slot": "select-value", ...props });
}
function SelectTrigger({
  className,
  size = DEFAULT_COMPONENT_SIZE,
  children,
  onPointerDown,
  ...props
}) {
  const sizeClasses = {
    small: "px-4 py-2 h-8 max-h-8",
    medium: "px-4 py-3 max-h-10"
  };
  const ctx = React13.useContext(SelectInstanceContext);
  const handlePointerDown = (e) => {
    onPointerDown?.(e);
    if (e.currentTarget.dataset.state === "open") {
      e.preventDefault();
      ctx?.setOpen(false);
    }
  };
  return /* @__PURE__ */ jsxs8(
    SelectPrimitive.Trigger,
    {
      "data-slot": "select-trigger",
      "data-radix-instance": ctx?.instanceId,
      "data-size": size,
      onPointerDown: handlePointerDown,
      className: cn(
        // Layout
        "flex w-full items-center justify-between",
        // Spacing
        "gap-2",
        // Appearance
        "bg-neutral-1 rounded-md studio:rounded-xl",
        // Borders
        "border-neutral-1 border",
        "data-[state=open]:border-highlight-2",
        // Typography
        "text-neutral-1 whitespace-nowrap text-sm",
        "[&>span]:inline [&>span]:text-ellipsis",
        // Interactions
        "cursor-pointer outline-none",
        "transition-[color,box-shadow]",
        "focus-within:ring-2 focus-within:ring-[var(--border-focus-ring)]",
        "disabled:cursor-not-allowed disabled:border-none disabled:opacity-50",
        // Icons
        '[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
        // Select value styling
        "*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2",
        // Size variants
        "data-[size=default]:h-10 data-[size=small]:h-8",
        sizeClasses[size],
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx19(SelectPrimitive.Icon, { asChild: true, children: /* @__PURE__ */ jsx19(FontAwesomeIcon6, { icon: faCaretDown2 }) })
      ]
    }
  );
}
function SelectScrollUpButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    SelectPrimitive.ScrollUpButton,
    {
      "data-slot": "select-scroll-up-button",
      className: cn(
        // Layout
        "flex items-center justify-center",
        // Spacing
        "py-1",
        // Interactions
        "cursor-default",
        // Icons
        "[&_svg]:size-3",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx19(FontAwesomeIcon6, { icon: faChevronUp2 })
    }
  );
}
function SelectScrollDownButton({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    SelectPrimitive.ScrollDownButton,
    {
      "data-slot": "select-scroll-down-button",
      className: cn(
        // Layout
        "flex items-center justify-center",
        // Spacing
        "py-1",
        // Interactions
        "cursor-default",
        // Icons
        "[&_svg]:size-3",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx19(FontAwesomeIcon6, { icon: faChevronDown2 })
    }
  );
}
function SelectPortal({
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx19(SelectPrimitive.Portal, { ...props, children });
}
var SelectContent = React13.forwardRef(({ className, children, position = "popper", ...props }, ref) => {
  const ctx = React13.useContext(SelectInstanceContext);
  return /* @__PURE__ */ jsxs8(
    SelectPrimitive.Content,
    {
      ref,
      "data-slot": "select-content",
      "data-radix-instance": ctx?.instanceId,
      className: cn(
        // Position
        "relative z-50",
        "origin-(--radix-select-content-transform-origin)",
        // Sizing
        "min-w-[8rem]",
        "max-h-(--radix-select-content-available-height)",
        // Overflow
        "overflow-y-auto overflow-x-hidden",
        // Appearance
        "bg-neutral-1 rounded-md shadow-md studio:rounded-xl",
        "studio:bg-neutral-0",
        // Borders
        "border-neutral-1 border",
        // Typography
        "text-neutral-1",
        // Animations
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        // Position-specific translations
        position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      ),
      position,
      ...props,
      children: [
        /* @__PURE__ */ jsx19(SelectScrollUpButton, {}),
        /* @__PURE__ */ jsx19(
          SelectPrimitive.Viewport,
          {
            className: cn(
              // Spacing
              "p-1",
              // Position-specific sizing
              position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1"
            ),
            children
          }
        ),
        /* @__PURE__ */ jsx19(SelectScrollDownButton, {})
      ]
    }
  );
});
SelectContent.displayName = "SelectContent";
function SelectLabel({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    SelectPrimitive.Label,
    {
      "data-slot": "select-label",
      className: cn(
        // Spacing
        "px-2 py-1.5",
        // Typography
        "text-xs",
        className
      ),
      ...props
    }
  );
}
function SelectItem({
  className,
  children,
  description,
  title = typeof children === "string" ? children : void 0,
  hideIndicator = false,
  ...props
}) {
  const hasDescription = Boolean(description);
  return /* @__PURE__ */ jsxs8(
    SelectPrimitive.Item,
    {
      "data-slot": "select-item",
      className: cn(
        // Layout
        "relative flex w-full items-center",
        hasDescription ? "h-auto flex-col items-start" : "h-8",
        // Spacing
        "gap-2 pl-2 pr-8",
        hasDescription ? "py-2" : "py-1.5",
        // Appearance
        "rounded-sm",
        // Typography
        "text-sm",
        // Interactions
        "cursor-pointer select-none outline-none",
        "hover:bg-highlight-3",
        "focus:bg-highlight-3",
        // Checked state
        "data-[state=checked]:bg-highlight-1 data-[state=checked]:text-highlight-2",
        "studio:data-[state=checked]:bg-surface-bright-secondary studio:data-[state=checked]:text-neutral-1",
        // Disabled state
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // Span styling (for ItemText)
        hasDescription ? "[&>span:first-child]:font-bold" : "[&>span]:truncate [&>span]:first:inline [&>span]:first:w-fit",
        // Icons
        '[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
        // Group for description state styling
        "group",
        className
      ),
      title,
      ...props,
      children: [
        /* @__PURE__ */ jsx19(SelectPrimitive.ItemText, { children }),
        description && /* @__PURE__ */ jsx19("span", { className: "text-neutral-1 block text-xs font-normal group-data-[state=checked]:text-current", children: description }),
        !hideIndicator && /* @__PURE__ */ jsx19(
          SelectPrimitive.ItemIndicator,
          {
            className: cn(
              "size-3.5 absolute right-2 flex items-center justify-center",
              !hasDescription && "top-1/2 -translate-y-1/2"
            ),
            children: /* @__PURE__ */ jsx19(FontAwesomeIcon6, { icon: faCheck2 })
          }
        )
      ]
    }
  );
}
function SelectButton({
  children,
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    "button",
    {
      type: "button",
      "data-slot": "select-item",
      className: cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground outline-hidden [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2 hover:bg-highlight-3 data-[state=checked]:bg-highlight-1 data-[state=checked]:text-highlight-2 studio:data-[state=checked]:bg-surface-bright-secondary studio:data-[state=checked]:text-neutral-1 relative flex h-8 w-full cursor-pointer select-none items-center gap-2 rounded-sm py-1.5 pl-2 pr-8 text-sm data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&>span]:truncate [&>span]:first:inline [&>span]:first:w-fit [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      ),
      ...props,
      children: /* @__PURE__ */ jsx19("span", { children })
    }
  );
}
function SelectSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx19(
    SelectPrimitive.Separator,
    {
      "data-slot": "select-separator",
      className: cn(
        // Sizing
        "w-45 h-px",
        // Spacing
        "mx-auto my-2",
        // Appearance
        "bg-neutral-2",
        // Interactions
        "pointer-events-none",
        className
      ),
      ...props
    }
  );
}

// src/components/ColorPicker/ColorSpaces/HEX.tsx
import { useState as useState9, useEffect as useEffect10, useRef as useRef5 } from "react";

// src/components/Fieldset/Fieldset.tsx
import { jsx as jsx20 } from "react/jsx-runtime";
var Fieldset = ({
  children,
  className,
  showNumberControls = false
}) => /* @__PURE__ */ jsx20(
  "fieldset",
  {
    className: cn(
      // Container styles
      "flex rounded-md",
      "border-1 border-neutral-1 bg-neutral-1",
      // Input wrapper (div) styles
      "[&>div]:border-0 [&>div]:px-2 [&>div]:py-1",
      "[&>div]:rounded-l-none [&>div]:rounded-r-none",
      "[&>div:first-child]:rounded-l-md [&>div:last-child]:rounded-r-md",
      "[&>div:not(:last-child)]:border-r-1",
      // Button styles
      "[&>button]:rounded-l-none",
      "[&>button:first-child]:rounded-l-md [&>button:last-child]:rounded-r-md",
      // Hide number input controls by default
      !showNumberControls && "[&_.number-controls]:hidden",
      className
    ),
    children
  }
);

// src/components/Input/Input.tsx
import { useImperativeHandle as useImperativeHandle2, useRef as useRef4, forwardRef as forwardRef10 } from "react";

// src/components/Input/Icons.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var CustomChevronDown = () => {
  return /* @__PURE__ */ jsx21(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "8",
      height: "6",
      viewBox: "0 0 8 6",
      fill: "none",
      "aria-hidden": true,
      children: /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M7.78037 1.96282L4.59625 5.16949C4.41838 5.32732 4.21295 5.40498 4.00752 5.40498C3.80209 5.40498 3.59717 5.32669 3.44059 5.17011L0.256467 1.96344C0.00480378 1.73484 -0.0638491 1.38912 0.0602592 1.091C0.184367 0.792883 0.476926 0.594971 0.80085 0.594971L7.19165 0.594971C7.51582 0.594971 7.80843 0.789901 7.93269 1.08975C8.05695 1.3896 8.01085 1.73484 7.78037 1.96282Z",
          fill: "currentColor"
        }
      )
    }
  );
};
var CustomChevronUp = () => {
  return /* @__PURE__ */ jsx21(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "8",
      height: "6",
      viewBox: "0 0 8 6",
      fill: "none",
      "aria-hidden": true,
      children: /* @__PURE__ */ jsx21(
        "path",
        {
          d: "M0.235526 4.0379L3.42183 0.829035C3.60007 0.671098 3.80564 0.593384 4.01121 0.593384C4.21678 0.593384 4.42184 0.671725 4.57852 0.828408L7.76483 4.03727C7.99436 4.26681 8.06315 4.61161 7.93888 4.91169C7.81462 5.21177 7.54347 5.40668 7.19751 5.40668L0.802592 5.40668C0.478196 5.40668 0.185387 5.21162 0.0610436 4.91156C-0.0633 4.61151 0.00597392 4.26603 0.235526 4.0379Z",
          fill: "currentColor"
        }
      )
    }
  );
};

// src/components/Input/Custom/Color/Color.tsx
import { useState as useState8, useRef as useRef3, useEffect as useEffect9 } from "react";

// src/lib/css-variable.ts
var CSS_VAR_REGEX = /^var\((--[^,)]+)(?:,\s*(.+))?\)$/;
function isCssVariable(value) {
  return CSS_VAR_REGEX.test(value);
}
function parseCssVariable(value) {
  const match = value.match(CSS_VAR_REGEX);
  if (!match) return null;
  const fallback = match[2]?.trim();
  return fallback ? { name: match[1], fallback } : { name: match[1] };
}
function themeVariableToLabel(name) {
  const segments = name.replace(/^--/, "").split("__");
  return segments.length > 2 ? segments.slice(2).join(".") : segments.join(".");
}

// src/components/Input/Custom/Color/Swatch.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var TRANSPARENT_CHECKERBOARD = {
  backgroundImage: "linear-gradient(45deg, #d4d4d8 25%, transparent 25%),linear-gradient(-45deg, #d4d4d8 25%, transparent 25%),linear-gradient(45deg, transparent 75%, #d4d4d8 75%),linear-gradient(-45deg, transparent 75%, #d4d4d8 75%)",
  backgroundSize: "8px 8px",
  backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0"
};
var backgroundFor = ({ r, g, b, a = 1 }) => {
  if (a === 0) return TRANSPARENT_CHECKERBOARD;
  if (a < 1) {
    return {
      background: `linear-gradient(to right, rgba(${r}, ${g}, ${b}) 50%, rgba(${r}, ${g}, ${b}, ${a}) 50%)`
    };
  }
  return { background: `rgba(${r}, ${g}, ${b})` };
};
var Swatch = ({
  color,
  disabled = false
}) => /* @__PURE__ */ jsx22("span", { className: cn("block h-8 w-8 rounded-md px-1 py-1"), children: /* @__PURE__ */ jsx22(
  "span",
  {
    className: cn("block h-full w-full rounded-sm", disabled && "opacity-50"),
    style: backgroundFor(color.rgb)
  }
) });

// src/components/Input/Custom/Color/Color.tsx
import { jsx as jsx23, jsxs as jsxs9 } from "react/jsx-runtime";
var DEFAULT_RGB = { r: 65, g: 127.5, b: 127.5, a: 0.5 };
function parseColor(value) {
  if (!value) {
    return {
      css: toHex8(DEFAULT_RGB),
      hex: toHex8(DEFAULT_RGB),
      hsl: toHsl(DEFAULT_RGB),
      rgb: DEFAULT_RGB
    };
  }
  if (typeof value === "object") {
    const rgb2 = "r" in value ? value : toRgb(value);
    return {
      css: toHex8(rgb2),
      hex: toHex8(rgb2),
      hsl: toHsl(rgb2),
      rgb: rgb2
    };
  }
  const cssVar = parseCssVariable(value);
  if (cssVar?.fallback) {
    const rgb2 = toRgb(cssVar.fallback);
    return {
      css: value,
      hex: toHex8(rgb2),
      hsl: toHsl(rgb2),
      rgb: rgb2,
      swatch: cssVar.name
    };
  }
  const rgb = toRgb(value);
  return {
    css: value,
    hex: toHex8(rgb),
    hsl: toHsl(rgb),
    rgb
  };
}
var ColorInput = ({
  value,
  onChange,
  display,
  alpha = false,
  className,
  container,
  disabled = false,
  prefix,
  suffix,
  "aria-label": ariaLabel = "Color"
}) => {
  const ref = useRef3(null);
  const [visible, setVisible] = useState8(false);
  const [currentColor, setCurrentColor] = useState8(
    () => parseColor(value)
  );
  useEffect9(() => {
    setCurrentColor(parseColor(value));
  }, [value]);
  const toggle = () => {
    if (disabled) return;
    setVisible((prev) => !prev);
  };
  const close = () => {
    if (disabled) return;
    setVisible(false);
  };
  const handleChange = (colorResult) => {
    if (disabled) return;
    setCurrentColor(colorResult);
    onChange?.(colorResult);
  };
  const displayHex = currentColor.hex.replace("#", "").slice(0, 6);
  const swatchLabel = currentColor.swatch ? themeVariableToLabel(currentColor.swatch) : void 0;
  return /* @__PURE__ */ jsxs9("div", { className: cn(className), children: [
    /* @__PURE__ */ jsxs9(
      "button",
      {
        type: "button",
        className: cn(
          "font-lato text-neutral-1 border-1 border-neutral-1 bg-neutral-1 flex h-8 w-full cursor-pointer items-center justify-start rounded-md font-semibold",
          "disabled:text-neutral-4 disabled:*:pointer-events-none disabled:*:cursor-not-allowed disabled:cursor-not-allowed disabled:border-transparent"
        ),
        onClick: toggle,
        "aria-label": ariaLabel,
        ref,
        disabled,
        children: [
          prefix ? /* @__PURE__ */ jsx23("div", { className: cn("text-neutral-1 px-2"), children: prefix }) : null,
          /* @__PURE__ */ jsx23(Swatch, { color: currentColor, disabled }),
          display && /* @__PURE__ */ jsxs9("span", { className: cn("flex h-full pr-1 text-xs/[30px]"), children: [
            display === "HEX" && /* @__PURE__ */ jsx23("span", { className: "min-w-[58px] truncate", children: swatchLabel ?? (currentColor.rgb.a === 0 ? "None" : `#${displayHex}`) }),
            display === "HSL" && /* @__PURE__ */ jsxs9("div", { className: "flex flex-row gap-1", children: [
              /* @__PURE__ */ jsx23("span", { children: "H" }),
              currentColor.hsl.h.toFixed(0),
              /* @__PURE__ */ jsx23("span", { className: "border-neutral-1 border-l pl-1", children: "S" }),
              Math.round(currentColor.hsl.s * 100),
              "%",
              /* @__PURE__ */ jsx23("span", { className: "border-neutral-1 border-l pl-1", children: "L" }),
              Math.round(currentColor.hsl.l * 100),
              "%"
            ] }),
            display === "RGB" && /* @__PURE__ */ jsxs9("div", { className: "flex flex-row gap-1", children: [
              /* @__PURE__ */ jsx23("span", { children: "R" }),
              currentColor.rgb.r,
              /* @__PURE__ */ jsx23("span", { className: "border-neutral-1 border-l pl-1", children: "G" }),
              currentColor.rgb.g,
              /* @__PURE__ */ jsx23("span", { className: "border-neutral-1 border-l pl-1", children: "B" }),
              currentColor.rgb.b
            ] })
          ] }),
          display && alpha && /* @__PURE__ */ jsxs9(
            "span",
            {
              className: cn(
                "border-1 border-neutral-1 flex h-8 items-center justify-center rounded-r-md border-r-0 px-1 text-xs"
              ),
              children: [
                /* @__PURE__ */ jsx23("span", { children: Math.round((currentColor.rgb.a ?? 0) * 100) }),
                /* @__PURE__ */ jsx23("span", { className: cn("text-neutral-3 pl-1"), children: "%" })
              ]
            }
          ),
          suffix ? /* @__PURE__ */ jsx23("div", { className: cn("text-neutral-1 px-2"), children: suffix }) : null
        ]
      }
    ),
    /* @__PURE__ */ jsx23(
      ColorPicker,
      {
        color: currentColor,
        container,
        onClose: close,
        onChange: handleChange,
        ref,
        open: visible
      }
    )
  ] });
};

// src/components/Input/Custom/index.ts
var CUSTOM_TYPE_LIST = ["color"];
var CUSTOM_TYPE_MAP = {
  color: ColorInput
};
var isCustomType = (type) => CUSTOM_TYPE_LIST.includes(type);

// src/components/Input/Input.tsx
import { jsx as jsx24, jsxs as jsxs10 } from "react/jsx-runtime";
var isTextAffix = (affix) => {
  return typeof affix === "string";
};
var Input = forwardRef10(
  ({
    className,
    type = "",
    prefix,
    suffix,
    disabled,
    size = DEFAULT_COMPONENT_SIZE,
    onChange,
    "data-focus": dataFocus,
    ...props
  }, ref) => {
    const inputRef = useRef4(null);
    const hasPrefixOrSuffix = Boolean(prefix || suffix);
    useImperativeHandle2(ref, () => inputRef.current);
    const handleArrowClick = (direction) => {
      if (!inputRef.current || disabled) return;
      inputRef.current.focus();
      const { step = 1, min, max } = props;
      const currentValue = Number(inputRef.current.value) || 0;
      const stepValue = Number(step);
      const stepStr = stepValue.toString();
      const decimalPlaces = stepStr.includes(".") ? stepStr.split(".")[1].length : 0;
      let value = direction === "up" ? currentValue + stepValue : currentValue - stepValue;
      if (decimalPlaces > 0) {
        value = Number(value.toFixed(decimalPlaces));
      }
      if (min !== void 0) value = Math.max(value, Number(min));
      if (max !== void 0) value = Math.min(value, Number(max));
      inputRef.current.value = value.toString();
      if (onChange) {
        const event = new Event("change", { bubbles: true });
        inputRef.current.dispatchEvent(event);
        onChange({
          ...event,
          target: inputRef.current,
          currentTarget: inputRef.current
        });
      }
    };
    const sizeClasses = {
      small: "px-4 py-2 h-8 max-h-8",
      medium: "px-4 py-3 max-h-10"
    };
    const CustomInputType = CUSTOM_TYPE_MAP[type];
    return isCustomType(type) ? /* @__PURE__ */ jsx24(
      CustomInputType,
      {
        onChange,
        ...props,
        prefix,
        suffix,
        disabled
      }
    ) : /* @__PURE__ */ jsxs10(
      "div",
      {
        "data-focus": dataFocus,
        className: cn(
          // Layout
          "flex w-full items-center",
          // Appearance — studio bumps to 12px (rounded-xl, interactive
          // radius tier). Light keeps the existing 6px so legacy chrome
          // is unaffected.
          "bg-neutral-1 rounded-md studio:rounded-xl",
          // Borders
          "border-1 border-neutral-1",
          // Typography
          "font-lato text-neutral-1 text-sm font-semibold leading-5",
          // Studio: the `type-field` brand style renders at regular
          // weight (400). Drop the semibold so hex/alpha values and field
          // values read as field text, not labels.
          "studio:font-normal",
          // Focus state — `data-[focus=true]:` mirrors `:focus-within`
          // so stories can force the visual state without a play
          // function.
          "ring-offset-transparent focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--border-focus-ring)]",
          "data-[focus=true]:ring-2 data-[focus=true]:ring-[var(--border-focus-ring)]",
          // Disabled state
          "[&:has(input:disabled)]:text-neutral-4 [&:has(input:disabled)]:cursor-not-allowed [&:has(input:disabled)]:border-transparent [&:has(input:disabled)_*]:pointer-events-none [&:has(input:disabled)_*]:cursor-not-allowed",
          // Read-only state
          "[&:has(input:read-only)]:text-neutral-3 [&:has(input:read-only)]:border-transparent",
          // Invalid state
          '[&:has(input[aria-invalid="true"])]:border-destructive-1',
          // Conditional spacing
          hasPrefixOrSuffix && "gap-2",
          sizeClasses[size],
          className
        ),
        children: [
          prefix && /* @__PURE__ */ jsx24(
            "div",
            {
              className: cn(
                // Typography
                "text-neutral-1",
                // Text prefix style
                isTextAffix(prefix) && "text-neutral-3 font-medium leading-4"
              ),
              children: prefix
            }
          ),
          /* @__PURE__ */ jsx24(
            "input",
            {
              type,
              className: cn(
                // Sizing
                "w-full",
                // Appearance
                "bg-transparent",
                // Interactions
                "outline-none",
                // Placeholder
                "placeholder:text-neutral-3",
                // Hide number spinners
                type === "number" && "[&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden"
              ),
              ref: inputRef,
              disabled,
              onChange,
              ...props
            }
          ),
          suffix && /* @__PURE__ */ jsx24(
            "div",
            {
              className: cn(
                // Typography
                "text-neutral-1",
                // Text suffix style
                isTextAffix(suffix) && "text-neutral-3 font-medium leading-4"
              ),
              children: suffix
            }
          ),
          type === "number" && /* @__PURE__ */ jsxs10(
            "div",
            {
              className: cn(
                // Layout
                "number-controls flex w-3 flex-col",
                // Typography
                "text-neutral-3"
              ),
              children: [
                /* @__PURE__ */ jsx24(
                  "button",
                  {
                    type: "button",
                    tabIndex: -1,
                    "aria-label": "Increment",
                    onClick: () => handleArrowClick("up"),
                    className: cn(
                      // Spacing
                      "px-0.5 py-0.5",
                      // Typography
                      "text-xs"
                    ),
                    children: /* @__PURE__ */ jsx24(CustomChevronUp, {})
                  }
                ),
                /* @__PURE__ */ jsx24(
                  "button",
                  {
                    type: "button",
                    tabIndex: -1,
                    "aria-label": "Decrement",
                    onClick: () => handleArrowClick("down"),
                    className: cn(
                      // Spacing
                      "px-0.5 py-0.5",
                      // Typography
                      "text-xs"
                    ),
                    children: /* @__PURE__ */ jsx24(CustomChevronDown, {})
                  }
                )
              ]
            }
          )
        ]
      }
    );
  }
);
Input.displayName = "Input";

// src/components/ColorPicker/ColorSpaces/HEX.tsx
import { jsx as jsx25, jsxs as jsxs11 } from "react/jsx-runtime";
var SpaceHEX = ({ color, onChange }) => {
  const [hex, setHex] = useState9(toHex(color));
  const [alpha, setAlpha] = useState9(roundAlpha(color.a));
  const [isDirty, setIsDirty] = useState9(false);
  const hexInputRef = useRef5(null);
  const onHexChange = ({
    target: { value }
  }) => {
    setHex(value.replace(/^#+/, ""));
    setIsDirty(true);
  };
  const onChangeAlpha = ({
    target: { value }
  }) => {
    setAlpha(Number(value));
    setIsDirty(true);
  };
  const updateColorPicker = () => {
    if (isDirty) {
      onChange(hexToRgb(hex, alpha));
      setIsDirty(false);
    }
  };
  const handleHexKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };
  useEffect10(() => {
    setHex(toHex(color));
    setAlpha(roundAlpha(color.a));
    setIsDirty(false);
  }, [color]);
  useEffect10(() => {
    hexInputRef.current?.focus();
    hexInputRef.current?.select();
  }, []);
  return /* @__PURE__ */ jsxs11(Fieldset, { className: cn("w-full"), children: [
    /* @__PURE__ */ jsx25(
      Input,
      {
        ref: hexInputRef,
        type: "text",
        size: "small",
        value: hex,
        onChange: onHexChange,
        onKeyDown: handleHexKeyDown,
        onBlur: updateColorPicker,
        prefix: "#",
        "aria-label": "HEX",
        className: "flex-3"
      }
    ),
    /* @__PURE__ */ jsx25(
      Input,
      {
        type: "number",
        size: "small",
        suffix: "%",
        value: alpha,
        onChange: onChangeAlpha,
        onBlur: updateColorPicker,
        "aria-label": "Alpha",
        min: 0,
        max: 100,
        className: "flex-1"
      }
    )
  ] });
};

// src/components/ColorPicker/ColorSpaces/RGB.tsx
import { useState as useState10, useEffect as useEffect11 } from "react";
import { jsx as jsx26, jsxs as jsxs12 } from "react/jsx-runtime";
var SpaceRGB = ({ onChange, color }) => {
  const [rgba, setRgba] = useState10({
    r: round(color.r),
    g: round(color.g),
    b: round(color.b),
    a: roundAlpha(color.a)
  });
  const onChangeColor = (key) => ({ target: { value } }) => setRgba({
    ...rgba,
    [key]: Number(value)
  });
  const updateColorPicker = () => onChange({ ...rgba, a: rgba.a / 100 });
  useEffect11(() => {
    setRgba({
      r: round(color.r),
      g: round(color.g),
      b: round(color.b),
      a: roundAlpha(color.a)
    });
  }, [color]);
  return /* @__PURE__ */ jsxs12(Fieldset, { className: cn("w-full"), children: [
    /* @__PURE__ */ jsx26(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "R",
        onChange: onChangeColor("r"),
        onBlur: updateColorPicker,
        value: rgba.r,
        "aria-label": "Red",
        min: 0,
        max: 255
      }
    ),
    /* @__PURE__ */ jsx26(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "G",
        onChange: onChangeColor("g"),
        onBlur: updateColorPicker,
        value: rgba.g,
        "aria-label": "Green",
        min: 0,
        max: 255
      }
    ),
    /* @__PURE__ */ jsx26(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "B",
        onChange: onChangeColor("b"),
        onBlur: updateColorPicker,
        value: rgba.b,
        "aria-label": "Blue",
        min: 0,
        max: 255
      }
    ),
    /* @__PURE__ */ jsx26(
      Input,
      {
        type: "number",
        size: "small",
        suffix: "%",
        onChange: onChangeColor("a"),
        onBlur: updateColorPicker,
        value: rgba.a,
        "aria-label": "Alpha",
        min: 0,
        max: 100
      }
    )
  ] });
};

// src/components/ColorPicker/ColorSpaces/HSL.tsx
import { useState as useState11, useEffect as useEffect12 } from "react";
import { jsx as jsx27, jsxs as jsxs13 } from "react/jsx-runtime";
var SpaceHSL = ({ color, onChange }) => {
  const converted = toHsl(color);
  const [hsl, setHsl] = useState11({
    h: round(converted.h),
    s: round(converted.s * 100),
    l: round(converted.l * 100),
    a: round(converted.a * 100)
  });
  const onChangeColor = (key) => ({ target: { value } }) => setHsl({
    ...hsl,
    [key]: Number(value)
  });
  const updateColorPicker = () => {
    onChange(
      toRgb({
        h: hsl.h,
        s: hsl.s / 100,
        l: hsl.l / 100,
        a: hsl.a / 100
      })
    );
  };
  useEffect12(() => {
    const newColor = toHsl(color);
    setHsl({
      h: round(newColor.h),
      s: round(newColor.s * 100),
      l: round(newColor.l * 100),
      a: round(newColor.a * 100)
    });
  }, [color]);
  return /* @__PURE__ */ jsxs13(Fieldset, { className: cn("w-full"), children: [
    /* @__PURE__ */ jsx27(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "H",
        onChange: onChangeColor("h"),
        onBlur: updateColorPicker,
        value: hsl.h,
        "aria-label": "Hue",
        min: 0,
        max: 360
      }
    ),
    /* @__PURE__ */ jsx27(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "S",
        onChange: onChangeColor("s"),
        onBlur: updateColorPicker,
        value: hsl.s,
        "aria-label": "Saturation",
        min: 0,
        max: 100
      }
    ),
    /* @__PURE__ */ jsx27(
      Input,
      {
        type: "number",
        size: "small",
        prefix: "L",
        onChange: onChangeColor("l"),
        onBlur: updateColorPicker,
        value: hsl.l,
        "aria-label": "Lightness",
        min: 0,
        max: 100
      }
    ),
    /* @__PURE__ */ jsx27(
      Input,
      {
        type: "number",
        size: "small",
        suffix: "%",
        onChange: onChangeColor("a"),
        onBlur: updateColorPicker,
        value: hsl.a,
        "aria-label": "Alpha",
        min: 0,
        max: 100
      }
    )
  ] });
};

// src/components/ColorPicker/ColorSpaces/Selector.tsx
import { Fragment as Fragment4, jsx as jsx28, jsxs as jsxs14 } from "react/jsx-runtime";
var ColorSpaces = {
  HEX: SpaceHEX,
  RGB: SpaceRGB,
  HSL: SpaceHSL
};
var ColorSpaceSelector = ({ color, onChange }) => {
  const [space, setSpace] = useState12(DEFAULT_SPACE);
  const ColorSpaceComponent = ColorSpaces[space];
  return /* @__PURE__ */ jsxs14(Fragment4, { children: [
    /* @__PURE__ */ jsx28("div", { className: cn("flex-1"), children: /* @__PURE__ */ jsxs14(
      Select,
      {
        onValueChange: (value) => setSpace(value),
        defaultValue: space,
        children: [
          /* @__PURE__ */ jsx28(SelectTrigger, { size: "small", children: /* @__PURE__ */ jsx28(SelectValue, { placeholder: "HEX" }) }),
          /* @__PURE__ */ jsxs14(SelectContent, { children: [
            /* @__PURE__ */ jsx28(SelectItem, { value: "HEX", children: "HEX" }),
            /* @__PURE__ */ jsx28(SelectItem, { value: "RGB", children: "RGB" }),
            /* @__PURE__ */ jsx28(SelectItem, { value: "HSL", children: "HSL" })
          ] })
        ]
      }
    ) }),
    /* @__PURE__ */ jsx28("div", { className: cn("flex w-full items-center gap-2"), children: /* @__PURE__ */ jsx28(ColorSpaceComponent, { onChange, color }) })
  ] });
};

// src/components/ColorPicker/Fields/EyeDropper/EyeDropper.tsx
import { FontAwesomeIcon as FontAwesomeIcon7 } from "@fortawesome/react-fontawesome";
import { faEyeDropper as Icon3 } from "@fortawesome/pro-solid-svg-icons/faEyeDropper";

// src/components/ColorPicker/Fields/EyeDropper/window.ts
var enabled = () => !!window.EyeDropper;
var open = () => new window.EyeDropper().open();

// src/components/ColorPicker/Fields/EyeDropper/EyeDropper.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var EyeDropper = ({
  onChange
}) => {
  const hasEyedropperCapabilities = enabled();
  const handleEyeDropperSelection = async () => {
    try {
      const result = await open();
      onChange(result.sRGBHex);
    } catch {
    }
  };
  return hasEyedropperCapabilities ? /* @__PURE__ */ jsx29(Button, { modifier: "icon", onClick: handleEyeDropperSelection, size: "small", children: /* @__PURE__ */ jsx29(FontAwesomeIcon7, { icon: Icon3 }) }) : null;
};

// src/components/ColorPicker/Fields/Fields.tsx
import { jsx as jsx30, jsxs as jsxs15 } from "react/jsx-runtime";
var Fields = ({ color, onChange }) => {
  return /* @__PURE__ */ jsxs15("div", { className: cn("flex flex-wrap items-center gap-2"), children: [
    /* @__PURE__ */ jsx30(EyeDropper, { onChange }),
    /* @__PURE__ */ jsx30(ColorSpaceSelector, { color, onChange })
  ] });
};

// src/components/ColorPicker/Pointer.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
var Pointer = ({ shape }) => {
  const shapeClassName = shape === "circle" ? "rounded-full" : "";
  return /* @__PURE__ */ jsx31(
    "button",
    {
      type: "button",
      "aria-label": "pointer",
      className: cn(
        "border-neutral-1 block h-3 w-3 cursor-pointer border-2",
        shapeClassName
      )
    }
  );
};
var SquarePointer = () => /* @__PURE__ */ jsx31(Pointer, { shape: "square" });
var CircularPointer = () => /* @__PURE__ */ jsx31(Pointer, { shape: "circle" });

// src/components/ColorPicker/CustomColorPicker.tsx
import { Fragment as Fragment5, jsx as jsx32, jsxs as jsxs16 } from "react/jsx-runtime";
var CustomColorPickerComponent = ({
  color,
  onChange,
  ...props
}) => {
  const handleColorChange = (newColor) => {
    const { source, hex, h, s, l, a } = newColor;
    const { l: colorLightness } = toHsl(color);
    const lightness = colorLightness === 1 || colorLightness === 0 ? 0.5 : l;
    let colorSource;
    switch (source) {
      case "hex":
        colorSource = hex;
        break;
      case "hsl":
        colorSource = { h, s: s || 1, l: lightness, a };
        break;
      default:
        colorSource = newColor;
        break;
    }
    let next = toRgb(colorSource);
    const rgbChanged = next.r !== color.r || next.g !== color.g || next.b !== color.b;
    if (color.a === 0 && next.a === 0 && rgbChanged) {
      next = { ...next, a: 1 };
    }
    if (onChange) {
      onChange(next);
    }
  };
  return /* @__PURE__ */ jsxs16(Fragment5, { children: [
    /* @__PURE__ */ jsx32("div", { className: cn("h-42 relative my-2 overflow-hidden rounded-md"), children: /* @__PURE__ */ jsx32(
      Saturation,
      {
        ...props,
        color,
        onChange: handleColorChange,
        pointer: CircularPointer
      }
    ) }),
    /* @__PURE__ */ jsx32("div", { className: cn("relative my-2 h-3 overflow-hidden rounded-md"), children: /* @__PURE__ */ jsx32(
      Hue,
      {
        ...props,
        color,
        onChange: handleColorChange,
        pointer: SquarePointer
      }
    ) }),
    /* @__PURE__ */ jsx32("div", { className: cn("relative my-2 h-3 overflow-hidden rounded-md"), children: /* @__PURE__ */ jsx32(
      Alpha,
      {
        ...props,
        color,
        onChange: handleColorChange,
        pointer: SquarePointer
      }
    ) }),
    /* @__PURE__ */ jsx32(Fields, { ...props, onChange: handleColorChange, color })
  ] });
};
var CustomColorPicker = custom(CustomColorPickerComponent);

// src/components/Tooltip/Tooltip.tsx
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { jsx as jsx33, jsxs as jsxs17 } from "react/jsx-runtime";
function TooltipProvider({
  delayDuration = 0,
  ...props
}) {
  return /* @__PURE__ */ jsx33(
    TooltipPrimitive.Provider,
    {
      "data-slot": "tooltip-provider",
      delayDuration,
      ...props
    }
  );
}
function Tooltip({
  delayDuration,
  skipDelayDuration,
  disableHoverableContent,
  ...props
}) {
  return /* @__PURE__ */ jsx33(
    TooltipProvider,
    {
      delayDuration,
      skipDelayDuration,
      disableHoverableContent,
      children: /* @__PURE__ */ jsx33(
        TooltipPrimitive.Root,
        {
          "data-slot": "tooltip",
          delayDuration,
          ...props
        }
      )
    }
  );
}
function TooltipTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx33(TooltipPrimitive.Trigger, { asChild: true, "data-slot": "tooltip-trigger", ...props });
}
function TooltipContent({
  container,
  className,
  side = "top",
  sideOffset = 0,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx33(TooltipPrimitive.Portal, { container, children: /* @__PURE__ */ jsxs17(
    TooltipPrimitive.Content,
    {
      "data-slot": "tooltip-content",
      side,
      sideOffset,
      className: cn(
        // Sizing
        "max-w-xs",
        // Spacing
        "px-3 py-1.5",
        // Appearance
        "bg-level-inverse rounded-md shadow-md",
        // Position
        "z-50",
        // Typography
        "font-lato text-over-primary text-sm",
        // Studio Typography — caption type + content inverse color
        "type-caption studio:text-content-inverse-primary",
        // Animations - Enter
        "animate-in fade-in-0 zoom-in-95 origin-(--radix-tooltip-content-transform-origin)",
        // Animations - Exit
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        // Animations - Slide by side
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx33(
          TooltipPrimitive.Arrow,
          {
            className: cn(
              // Sizing
              "size-2.5",
              // Appearance
              "fill-none"
            )
          }
        )
      ]
    }
  ) });
}
function TooltipShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx33(
    "span",
    {
      "data-slot": "tooltip-shortcut",
      className: cn(
        // Layout
        "block",
        // Typography
        "text-xs text-center opacity-60",
        className
      ),
      ...props
    }
  );
}

// src/components/ColorPicker/ColorSwatchesContext.tsx
import { createContext as createContext4, useContext as useContext4, useMemo as useMemo3 } from "react";
import { jsx as jsx34 } from "react/jsx-runtime";
var ColorSwatchesContext = createContext4(
  null
);
function ColorSwatchesProvider({
  swatches,
  colorTokensAcl = "included",
  children
}) {
  const value = useMemo3(
    () => ({ swatches, colorTokensAcl }),
    [swatches, colorTokensAcl]
  );
  return /* @__PURE__ */ jsx34(ColorSwatchesContext.Provider, { value, children });
}
function useColorSwatches() {
  return useContext4(ColorSwatchesContext);
}

// src/components/ColorPicker/SwatchGrid.tsx
import { jsx as jsx35, jsxs as jsxs18 } from "react/jsx-runtime";
function SwatchGrid({
  onSelect,
  currentColor,
  container
}) {
  const ctx = useColorSwatches();
  if (!ctx) return null;
  const entries = Object.entries(ctx.swatches).sort(
    ([a], [b]) => a.localeCompare(b, void 0, { numeric: true })
  );
  if (entries.length === 0) return null;
  return /* @__PURE__ */ jsxs18("div", { className: cn("mt-2 flex flex-col gap-1"), children: [
    /* @__PURE__ */ jsx35("span", { className: cn("text-neutral-3 text-xs font-medium"), children: "Theme Colors" }),
    /* @__PURE__ */ jsx35(TooltipProvider, { children: /* @__PURE__ */ jsx35(
      "div",
      {
        className: cn(
          "grid grid-cols-[repeat(auto-fill,_minmax(20px,_1fr))] gap-1"
        ),
        children: entries.map(([name, hex]) => {
          const selected = name === currentColor.swatch;
          const label = themeVariableToLabel(name);
          return /* @__PURE__ */ jsxs18(Tooltip, { children: [
            /* @__PURE__ */ jsx35(TooltipTrigger, { children: /* @__PURE__ */ jsx35(
              "button",
              {
                type: "button",
                "aria-label": label,
                className: cn(
                  "size-5 cursor-pointer rounded-sm border border-neutral-1 outline-none transition-transform duration-150",
                  "hover:scale-150",
                  selected && "ring-2 ring-[var(--border-focus-ring)]",
                  "focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]"
                ),
                style: { background: hex },
                onClick: () => {
                  const rgb = toRgb(hex);
                  onSelect({
                    css: `var(${name}, ${hex})`,
                    hex,
                    hsl: toHsl(rgb),
                    rgb,
                    swatch: name
                  });
                }
              }
            ) }),
            /* @__PURE__ */ jsx35(
              TooltipContent,
              {
                side: "bottom",
                sideOffset: 4,
                container,
                children: label
              }
            )
          ] }, name);
        })
      }
    ) })
  ] });
}

// src/components/ColorPicker/SwatchList.tsx
import { useState as useState13, useMemo as useMemo4 } from "react";
import { FontAwesomeIcon as FontAwesomeIcon8 } from "@fortawesome/react-fontawesome";
import { faXmark as faXmark2 } from "@fortawesome/pro-regular-svg-icons/faXmark";
import { jsx as jsx36, jsxs as jsxs19 } from "react/jsx-runtime";
function SwatchList({ onSelect, currentColor }) {
  const ctx = useColorSwatches();
  const [filter, setFilter] = useState13("");
  const entries = useMemo4(() => {
    if (!ctx) return [];
    const all = Object.entries(ctx.swatches).sort(
      ([a], [b]) => a.localeCompare(b, void 0, { numeric: true })
    );
    if (!filter) return all;
    const q = filter.toLowerCase();
    return all.filter(
      ([name, hex]) => themeVariableToLabel(name).toLowerCase().includes(q) || hex.toLowerCase().includes(q)
    );
  }, [ctx, filter]);
  if (!ctx || Object.keys(ctx.swatches).length === 0) return null;
  return /* @__PURE__ */ jsxs19("div", { className: cn("flex flex-col gap-1"), children: [
    /* @__PURE__ */ jsx36(
      Input,
      {
        value: filter,
        placeholder: "Filter colors\u2026",
        size: "small",
        onChange: (e) => setFilter(e.target.value),
        suffix: filter ? /* @__PURE__ */ jsx36(
          "button",
          {
            type: "button",
            "aria-label": "Clear filter",
            className: cn("cursor-pointer text-neutral-3"),
            onClick: () => setFilter(""),
            children: /* @__PURE__ */ jsx36(FontAwesomeIcon8, { icon: faXmark2 })
          }
        ) : void 0
      }
    ),
    /* @__PURE__ */ jsxs19(
      "div",
      {
        className: cn("flex flex-col gap-0.5 max-h-[300px] overflow-y-auto"),
        children: [
          entries.length === 0 && filter ? /* @__PURE__ */ jsx36("span", { className: cn("text-neutral-3 text-xs py-2 text-center"), children: "No matching colors" }) : null,
          entries.map(([name, hex]) => {
            const selected = name === currentColor.swatch;
            const label = themeVariableToLabel(name);
            return /* @__PURE__ */ jsxs19(
              "button",
              {
                type: "button",
                className: cn(
                  "flex items-center gap-2 rounded-md px-1.5 py-1 text-left cursor-pointer",
                  "outline-none transition-colors duration-100",
                  "hover:bg-neutral-1",
                  selected && "bg-neutral-1",
                  "focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]"
                ),
                onClick: () => {
                  const rgb = toRgb(hex);
                  onSelect({
                    css: `var(${name}, ${hex})`,
                    hex,
                    hsl: toHsl(rgb),
                    rgb,
                    swatch: name
                  });
                },
                children: [
                  /* @__PURE__ */ jsx36(
                    "span",
                    {
                      className: cn(
                        "size-8 shrink-0 rounded-sm border border-neutral-1",
                        selected && "ring-2 ring-[var(--border-focus-ring)]"
                      ),
                      style: { background: hex }
                    }
                  ),
                  /* @__PURE__ */ jsx36("span", { className: cn("text-neutral-1 text-sm truncate"), children: label })
                ]
              },
              name
            );
          })
        ]
      }
    )
  ] });
}

// src/components/ColorPicker/ColorPickerContent.tsx
import { Fragment as Fragment6, jsx as jsx37, jsxs as jsxs20 } from "react/jsx-runtime";
function ColorPickerContent({
  currentColor,
  container,
  onCustomChange,
  onSwatchSelect,
  customPickerColor,
  open: open2
}) {
  const ctx = useColorSwatches();
  const colorTokensAcl = ctx?.colorTokensAcl ?? "included";
  const [showCustomPicker, setShowCustomPicker] = useState14(false);
  useEffect13(() => {
    if (!open2) {
      setShowCustomPicker(false);
    }
  }, [open2]);
  if (colorTokensAcl === "exclusive") {
    return /* @__PURE__ */ jsx37(SwatchList, { onSelect: onSwatchSelect, currentColor });
  }
  const showCustom = colorTokensAcl === "included" || showCustomPicker;
  if (!showCustom) {
    return /* @__PURE__ */ jsxs20(Flex, { direction: "column", gap: "sm", children: [
      /* @__PURE__ */ jsx37(SwatchList, { onSelect: onSwatchSelect, currentColor }),
      /* @__PURE__ */ jsx37(
        Button,
        {
          type: "button",
          variant: "outline",
          size: "small",
          fullWidth: true,
          onClick: () => setShowCustomPicker(true),
          children: "Use custom color"
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsxs20(Fragment6, { children: [
    colorTokensAcl === "emphasized" && /* @__PURE__ */ jsx37(
      Button,
      {
        type: "button",
        variant: "ghost",
        size: "small",
        onClick: () => setShowCustomPicker(false),
        children: "\u2190 Back to theme colors"
      }
    ),
    /* @__PURE__ */ jsx37(CustomColorPicker, { color: customPickerColor, onChange: onCustomChange }),
    /* @__PURE__ */ jsx37(
      SwatchGrid,
      {
        onSelect: onSwatchSelect,
        currentColor,
        container
      }
    )
  ] });
}

// src/components/ColorPicker/ColorPicker.tsx
import { jsx as jsx38, jsxs as jsxs21 } from "react/jsx-runtime";
var ColorPicker = forwardRef11(
  ({ color, container, onChange, onClose, open: open2 }, ref) => {
    const [currentColor, setCurrentColor] = useState15(color);
    const onChangeRef = useRef6(onChange);
    onChangeRef.current = onChange;
    useEffect14(() => {
      setCurrentColor(color);
    }, [color]);
    const debouncedOnChange = useMemo5(
      () => debounce(
        (colorResult) => {
          onChangeRef.current?.(colorResult);
        },
        300,
        { leading: true }
      ),
      []
    );
    useEffect14(() => {
      if (!open2) {
        debouncedOnChange.flush();
      }
    }, [open2, debouncedOnChange]);
    const handleChange = (newColor) => {
      const rgb = newColor.rgb ?? newColor;
      const result = {
        css: toHex8(rgb),
        hex: toHex8(rgb),
        hsl: toHsl(rgb),
        rgb
      };
      setCurrentColor(result);
      debouncedOnChange(result);
    };
    const handleSwatchSelect = (result) => {
      setCurrentColor(result);
      debouncedOnChange(result);
      debouncedOnChange.flush();
      onClose();
    };
    return /* @__PURE__ */ jsx38(Portal3, { container, children: /* @__PURE__ */ jsxs21(
      FloatingPanel,
      {
        open: open2,
        variant: "floating",
        onClickOutside: onClose,
        dragOptions: {
          getPositionReferenceElement: () => ref?.current || null,
          offset: 8,
          disableAutoUpdate: true
        },
        children: [
          /* @__PURE__ */ jsx38(FloatingPanelHeader, { title: "Color", onClose }),
          /* @__PURE__ */ jsx38(FloatingPanelContent, { children: /* @__PURE__ */ jsx38(
            ColorPickerContent,
            {
              currentColor,
              customPickerColor: currentColor.rgb,
              container,
              onCustomChange: handleChange,
              onSwatchSelect: handleSwatchSelect,
              open: open2
            }
          ) })
        ]
      }
    ) });
  }
);

// src/components/ComboBox/ComboBox.tsx
import * as React24 from "react";
import { FontAwesomeIcon as FontAwesomeIcon10 } from "@fortawesome/react-fontawesome";
import { faCheck as faCheck3 } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faCaretDown as faCaretDown3 } from "@fortawesome/pro-solid-svg-icons/faCaretDown";

// src/components/Popover/Popover.tsx
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { jsx as jsx39 } from "react/jsx-runtime";
function Popover({
  ...props
}) {
  return /* @__PURE__ */ jsx39(PopoverPrimitive.Root, { "data-slot": "popover", ...props });
}
function PopoverTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx39(PopoverPrimitive.Trigger, { "data-slot": "popover-trigger", ...props });
}
function PopoverAnchor({
  ...props
}) {
  return /* @__PURE__ */ jsx39(PopoverPrimitive.Anchor, { "data-slot": "popover-anchor", ...props });
}
function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  container,
  ...props
}) {
  return /* @__PURE__ */ jsx39(PopoverPrimitive.Portal, { container, children: /* @__PURE__ */ jsx39(
    PopoverPrimitive.Content,
    {
      "data-slot": "popover-content",
      "data-ignore-click-outside": "true",
      align,
      sideOffset,
      className: cn(
        // Position
        "z-50",
        "origin-(--radix-popover-content-transform-origin)",
        // Sizing
        "w-72",
        // Spacing
        "p-4",
        // Appearance
        "bg-neutral-1 rounded-md shadow-md",
        // Borders
        "border-neutral-1 border",
        // Typography
        "text-neutral-1",
        // Overflow
        "outline-none",
        // Animations
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ...props
    }
  ) });
}

// src/components/Command/Command.tsx
import { Command as CommandPrimitive } from "cmdk";
import { FontAwesomeIcon as FontAwesomeIcon9 } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/pro-regular-svg-icons/faSearch";
import { jsx as jsx40, jsxs as jsxs22 } from "react/jsx-runtime";
function Command({
  className,
  shouldFilter = true,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive,
    {
      "data-slot": "command",
      shouldFilter,
      className: cn(
        // Layout
        "flex flex-col",
        // Sizing
        "h-full w-full",
        // Overflow
        "overflow-hidden",
        // Appearance
        "bg-neutral-1 rounded-md",
        "studio:bg-neutral-0",
        // Typography
        "text-neutral-1",
        className
      ),
      ...props
    }
  );
}
function CommandInput({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsxs22(
    "div",
    {
      className: cn(
        // Layout
        "flex items-center",
        // Sizing (matches Figma h-[32px] + wrapper py-[4px])
        "h-10",
        // Spacing (matches Figma px-[12px] py-[4px] gap-[8px])
        "gap-2 px-3 py-1",
        // Borders
        "border-neutral-2 border-b"
      ),
      "cmdk-input-wrapper": "",
      children: [
        /* @__PURE__ */ jsx40(
          FontAwesomeIcon9,
          {
            icon: faSearch,
            className: cn(
              // Sizing (matches Figma 12px)
              "size-3",
              // Appearance
              "text-neutral-4 shrink-0"
            )
          }
        ),
        /* @__PURE__ */ jsx40(
          CommandPrimitive.Input,
          {
            "data-slot": "command-input",
            className: cn(
              // Layout
              "flex",
              // Sizing (matches Figma h-[24px])
              "h-6 w-full",
              // Appearance
              "bg-transparent",
              // Typography (matches Figma 14px Medium)
              "text-sm font-medium",
              // Studio: command input is a field — the `type-field` brand
              // style renders at regular weight (400). Drop the medium under
              // studio.
              "studio:font-normal",
              // Interactions
              "outline-none",
              // Placeholder
              "placeholder:text-neutral-4",
              // Disabled state
              "disabled:cursor-not-allowed disabled:opacity-50",
              className
            ),
            ...props
          }
        )
      ]
    }
  );
}
function CommandList({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive.List,
    {
      "data-slot": "command-list",
      className: cn(
        // Sizing
        "max-h-[300px]",
        // Overflow
        "overflow-y-auto overflow-x-hidden",
        // Scrollbar customization
        "[&::-webkit-scrollbar]:w-1.5",
        "[&::-webkit-scrollbar-thumb]:rounded-full",
        "[&::-webkit-scrollbar-thumb]:bg-neutral-3",
        className
      ),
      ...props
    }
  );
}
function CommandEmpty({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive.Empty,
    {
      "data-slot": "command-empty",
      className: cn(
        // Spacing
        "py-6",
        // Typography
        "text-neutral-4 text-center text-sm",
        className
      ),
      ...props
    }
  );
}
function CommandGroup({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive.Group,
    {
      "data-slot": "command-group",
      className: cn(
        // Overflow
        "overflow-hidden",
        // Spacing
        "px-1 py-0",
        // Typography
        "text-neutral-1",
        // Heading styling (matches Figma "DIVIDER TEXT" style)
        "[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1",
        "[&_[cmdk-group-heading]]:text-neutral-4 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase",
        className
      ),
      ...props
    }
  );
}
function CommandSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive.Separator,
    {
      "data-slot": "command-separator",
      className: cn(
        // Sizing
        "-mx-1 h-px",
        // Appearance
        "bg-neutral-2",
        className
      ),
      ...props
    }
  );
}
function CommandItem({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    CommandPrimitive.Item,
    {
      "data-slot": "command-item",
      className: cn(
        // Layout
        "relative flex items-center",
        // Sizing (matches Figma 32px height)
        "h-8",
        // Spacing
        "gap-1 p-1",
        // Appearance
        "rounded-sm",
        // Typography
        "text-sm font-medium",
        // Studio: command items are field-like (regular weight). Drop
        // the medium so items don't read as labels.
        "studio:font-normal",
        // Interactions
        "cursor-pointer select-none outline-none",
        // Selected/Active state — studio repoints the active-descendant
        // highlight to the warm `surface-hover` (mortar) tint instead of the
        // brand aubergine, matching ActionList. Light/legacy keep highlight-3.
        "data-[selected=true]:bg-highlight-3",
        "studio:data-[selected=true]:bg-surface-hover",
        // Disabled state
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        // Icons
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className
      ),
      ...props
    }
  );
}
function CommandShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    "span",
    {
      "data-slot": "command-shortcut",
      className: cn(
        // Layout
        "ml-auto",
        // Spacing
        "p-1",
        // Typography (matches Figma "CTRL + D" style)
        "text-neutral-3 text-[10px] font-semibold",
        className
      ),
      ...props
    }
  );
}
function CommandItemIcon({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsx40(
    "div",
    {
      "data-slot": "command-item-icon",
      className: cn(
        // Layout
        "flex items-center justify-center",
        // Spacing
        "p-1",
        // Icons
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
        className
      ),
      ...props,
      children
    }
  );
}

// src/components/ComboBox/ComboBox.tsx
import { jsx as jsx41, jsxs as jsxs23 } from "react/jsx-runtime";
var ComboBoxContext = React24.createContext(null);
function useComboBoxContext() {
  const context = React24.useContext(ComboBoxContext);
  if (!context) {
    throw new Error("ComboBox components must be used within a ComboBox");
  }
  return context;
}
function ComboBox({
  children,
  value: controlledValue,
  defaultValue = "",
  onValueChange,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  disabled = false
}) {
  const {
    open: open2,
    onOpenChange: setOpen,
    instanceId
  } = useShadowDismiss({
    open: controlledOpen,
    onOpenChange,
    defaultOpen
  });
  const [uncontrolledValue, setUncontrolledValue] = React24.useState(defaultValue);
  const isValueControlled = controlledValue !== void 0;
  const value = isValueControlled ? controlledValue : uncontrolledValue;
  const handleValueChange = React24.useCallback(
    (newValue) => {
      if (!isValueControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isValueControlled, onValueChange]
  );
  const contextValue = React24.useMemo(
    () => ({
      open: open2,
      setOpen,
      value,
      onValueChange: handleValueChange,
      disabled,
      instanceId
    }),
    [open2, setOpen, value, handleValueChange, disabled, instanceId]
  );
  return /* @__PURE__ */ jsx41(ComboBoxContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx41(Popover, { open: open2, onOpenChange: setOpen, children }) });
}
function ComboBoxTrigger({
  children,
  placeholder = "Select option...",
  className,
  size = DEFAULT_COMPONENT_SIZE,
  variant = "default",
  "aria-label": ariaLabel
}) {
  const { open: open2, disabled, instanceId } = useComboBoxContext();
  const sizeClasses = {
    small: "h-8 px-4 py-2 text-sm",
    medium: "h-10 px-4 py-3 text-sm"
  };
  return /* @__PURE__ */ jsx41(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs23(
    Button,
    {
      variant: variant === "brand" ? "neutral" : "secondary",
      role: "combobox",
      "aria-expanded": open2,
      "aria-label": ariaLabel,
      disabled,
      "data-radix-instance": instanceId,
      className: cn(
        // Layout
        "flex w-full items-center justify-between",
        // Typography
        "font-medium",
        // Studio: combobox triggers are field-like (regular weight).
        // Drop the medium so values read as field text, not labels.
        "studio:font-normal",
        // Studio default: field surface, not a filled button.
        // Button's `secondary` lands on surface-secondary/-hover, one step
        // darker than the SelectTrigger field — pull it back to neutral.
        variant === "default" && "studio:bg-neutral-1 studio:text-neutral-1 studio:border-neutral-1 studio:hover:bg-neutral-2 studio:focus-visible:bg-neutral-2",
        // Size variants
        sizeClasses[size],
        className
      ),
      children: [
        /* @__PURE__ */ jsx41("span", { className: "truncate", children: children || placeholder }),
        /* @__PURE__ */ jsx41(
          FontAwesomeIcon10,
          {
            icon: faCaretDown3,
            className: cn(
              // Sizing
              "size-4",
              // Appearance
              "shrink-0 opacity-50"
            )
          }
        )
      ]
    }
  ) });
}
function ComboBoxValue({
  placeholder = "Select option...",
  children
}) {
  const { value } = useComboBoxContext();
  if (!value) {
    return /* @__PURE__ */ jsx41("span", { children: placeholder });
  }
  if (children) {
    return /* @__PURE__ */ jsx41("span", { children: children(value) });
  }
  return /* @__PURE__ */ jsx41("span", { children: value });
}
function ComboBoxContent({
  children,
  className,
  container,
  filter
}) {
  const { instanceId } = useComboBoxContext();
  return /* @__PURE__ */ jsx41(
    PopoverContent,
    {
      container,
      "data-radix-instance": instanceId,
      onOpenAutoFocus: (e) => e.preventDefault(),
      className: cn(
        // Sizing
        "w-[var(--radix-popover-trigger-width)]",
        // Spacing
        "p-0",
        className
      ),
      children: /* @__PURE__ */ jsx41(Command, { filter, children })
    }
  );
}
function ComboBoxInput({
  placeholder = "Search...",
  className
}) {
  return /* @__PURE__ */ jsx41(CommandInput, { autoFocus: true, placeholder, className });
}
function ComboBoxList({ children, className }) {
  return /* @__PURE__ */ jsx41(CommandList, { className, children });
}
function ComboBoxEmpty({
  children = "No results found.",
  className
}) {
  return /* @__PURE__ */ jsx41(CommandEmpty, { className, children });
}
function ComboBoxGroup({ children, heading, className }) {
  return /* @__PURE__ */ jsx41(CommandGroup, { heading, className, children });
}
function ComboBoxItem({
  children,
  value: itemValue,
  disabled,
  className,
  icon,
  onSelect,
  keywords
}) {
  const { value, onValueChange, setOpen } = useComboBoxContext();
  const handleSelect = React24.useCallback(
    (currentValue) => {
      const newValue = currentValue === value ? "" : currentValue;
      onValueChange?.(newValue);
      onSelect?.(newValue);
      setOpen(false);
    },
    [value, onValueChange, onSelect, setOpen]
  );
  const isSelected = value === itemValue;
  return /* @__PURE__ */ jsxs23(
    CommandItem,
    {
      value: itemValue,
      onSelect: handleSelect,
      disabled,
      className: cn(
        isSelected && "studio:bg-surface-bright-secondary studio:text-neutral-1",
        className
      ),
      keywords,
      children: [
        icon && /* @__PURE__ */ jsx41(CommandItemIcon, { children: icon }),
        /* @__PURE__ */ jsx41("span", { className: "min-w-0 flex-1 truncate pl-1", children }),
        /* @__PURE__ */ jsx41(
          FontAwesomeIcon10,
          {
            icon: faCheck3,
            className: cn(
              // Layout
              "ml-auto",
              // Sizing
              "size-3",
              // Visibility
              isSelected ? "opacity-100" : "opacity-0"
            )
          }
        )
      ]
    }
  );
}
ComboBox.displayName = "ComboBox";
ComboBoxTrigger.displayName = "ComboBoxTrigger";
ComboBoxValue.displayName = "ComboBoxValue";
ComboBoxContent.displayName = "ComboBoxContent";
ComboBoxInput.displayName = "ComboBoxInput";
ComboBoxList.displayName = "ComboBoxList";
ComboBoxEmpty.displayName = "ComboBoxEmpty";
ComboBoxGroup.displayName = "ComboBoxGroup";
ComboBoxItem.displayName = "ComboBoxItem";

// src/components/DateFilter/DateFilter.tsx
import * as React25 from "react";
import { format } from "date-fns/format";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faChevronDown as faChevronDown3 } from "@fortawesome/pro-regular-svg-icons/faChevronDown";
import { faChevronLeft as faChevronLeft2 } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faCheck as faCheck4 } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { Fragment as Fragment7, jsx as jsx42, jsxs as jsxs24 } from "react/jsx-runtime";
var DEFAULT_RANGE_FORMAT = (range) => `${format(range.from, "dd MMM yyyy")} - ${format(range.to, "dd MMM yyyy")}`;
var triggerSizeClass = {
  small: "h-8 px-3 py-1 text-sm",
  medium: "h-10 px-3 py-2 text-sm"
};
var ROW_CLASSES = cn(
  // Layout
  "flex w-full items-center justify-between",
  // Spacing
  "gap-2 px-2 py-1.5",
  // Appearance
  "rounded-sm",
  // Typography
  "font-lato text-sm text-neutral-1",
  // Interactions
  "cursor-pointer outline-none",
  "hover:bg-highlight-3 focus-visible:bg-highlight-3",
  "data-[state=open]:bg-highlight-3"
);
function PresetRadio({
  label,
  checked,
  onSelect
}) {
  return /* @__PURE__ */ jsxs24(
    "button",
    {
      type: "button",
      role: "radio",
      "aria-checked": checked,
      tabIndex: checked ? 0 : -1,
      onClick: onSelect,
      className: ROW_CLASSES,
      children: [
        /* @__PURE__ */ jsx42("span", { children: label }),
        checked ? /* @__PURE__ */ jsx42(
          Icon,
          {
            icon: faCheck4,
            size: "small",
            className: "text-neutral-1",
            "aria-hidden": "true"
          }
        ) : null
      ]
    }
  );
}
function CustomRow({
  label,
  isActive,
  onSelect
}) {
  return /* @__PURE__ */ jsxs24(
    "button",
    {
      type: "button",
      onClick: onSelect,
      "aria-current": isActive ? "true" : void 0,
      className: ROW_CLASSES,
      children: [
        /* @__PURE__ */ jsx42("span", { children: label }),
        isActive ? /* @__PURE__ */ jsx42(
          Icon,
          {
            icon: faCheck4,
            size: "small",
            className: "text-neutral-1",
            "aria-hidden": "true"
          }
        ) : null
      ]
    }
  );
}
var DateFilter = React25.forwardRef(
  function DateFilter2({
    presets,
    value: controlledValue,
    defaultValue,
    onChange,
    open: controlledOpen,
    onOpenChange,
    label,
    placeholder = "Select date",
    customLabel = "Custom",
    icon = faCalendar,
    formatRange = DEFAULT_RANGE_FORMAT,
    allowCustom = true,
    numberOfMonths = 1,
    disabledDates,
    align = "start",
    side,
    sideOffset,
    container,
    defaultMonth,
    today,
    size = "medium",
    disabled,
    className,
    id,
    "aria-label": ariaLabel,
    "data-testid": dataTestId
  }, ref) {
    const isValueControlled = controlledValue !== void 0;
    const [internalValue, setInternalValue] = React25.useState(defaultValue ?? null);
    const value = isValueControlled ? controlledValue ?? null : internalValue;
    const commitValue = React25.useCallback(
      (next) => {
        if (!isValueControlled) setInternalValue(next);
        onChange?.(next);
      },
      [isValueControlled, onChange]
    );
    const isOpenControlled = controlledOpen !== void 0;
    const [internalOpen, setInternalOpen] = React25.useState(false);
    const open2 = isOpenControlled ? controlledOpen : internalOpen;
    const setOpen = React25.useCallback(
      (next) => {
        if (!isOpenControlled) setInternalOpen(next);
        onOpenChange?.(next);
      },
      [isOpenControlled, onOpenChange]
    );
    const [view, setView] = React25.useState("preset");
    const [stagedRange, setStagedRange] = React25.useState(
      void 0
    );
    React25.useEffect(() => {
      if (open2) setView("preset");
    }, [open2]);
    React25.useEffect(() => {
      if (view === "custom") {
        setStagedRange(
          value?.kind === "range" ? { from: value.from, to: value.to } : void 0
        );
      }
    }, [view, value]);
    const isEmpty = value == null;
    const isCustom = value?.kind === "range";
    const selectedPresetId = value?.kind === "preset" ? value.presetId : null;
    const valueText = isEmpty ? null : value.kind === "range" ? formatRange({ from: value.from, to: value.to }) : presets.find((p) => p.id === value.presetId)?.label ?? "";
    const handlePresetClick = (preset) => {
      commitValue({ kind: "preset", presetId: preset.id });
      setOpen(false);
    };
    const handleCustomClick = () => {
      setView("custom");
    };
    const handleRangeSelect = (range) => {
      setStagedRange(range);
      if (range?.from && range?.to) {
        commitValue({ kind: "range", from: range.from, to: range.to });
        setOpen(false);
      }
    };
    return /* @__PURE__ */ jsxs24(Popover, { open: open2, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx42(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs24(
        "button",
        {
          ref,
          type: "button",
          "data-slot": "date-filter-trigger",
          "data-size": size,
          "data-empty": isEmpty || void 0,
          "data-testid": dataTestId,
          id,
          "aria-label": ariaLabel,
          disabled,
          className: cn(
            // Inherits the Select trigger look: rectangular, neutral border.
            // Layout
            "inline-flex items-center justify-between",
            // Spacing
            "gap-2",
            // Appearance — studio adopts the interactive radius tier
            // (matches SelectTrigger).
            "bg-neutral-1 rounded-md studio:rounded-xl",
            // Borders
            "border-neutral-1 border",
            "data-[state=open]:border-highlight-2",
            // Typography
            "font-lato text-neutral-1 whitespace-nowrap",
            // Interactions
            "cursor-pointer outline-none",
            "transition-[color,box-shadow]",
            "focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
            "disabled:cursor-not-allowed disabled:opacity-50",
            // Icons
            "[&_svg]:pointer-events-none [&_svg]:shrink-0",
            triggerSizeClass[size],
            className
          ),
          children: [
            icon !== false ? /* @__PURE__ */ jsx42(Icon, { icon, size: "small" }) : null,
            /* @__PURE__ */ jsxs24("span", { className: "truncate", children: [
              label ? /* @__PURE__ */ jsxs24(Fragment7, { children: [
                /* @__PURE__ */ jsx42("span", { className: "font-semibold", children: label }),
                valueText ? " " : null
              ] }) : null,
              valueText ? /* @__PURE__ */ jsx42("span", { className: "text-neutral-2", children: valueText }) : /* @__PURE__ */ jsx42("span", { className: "text-neutral-3", children: placeholder })
            ] }),
            /* @__PURE__ */ jsx42(
              Icon,
              {
                icon: faChevronDown3,
                size: "small",
                className: "text-neutral-3"
              }
            )
          ]
        }
      ) }),
      /* @__PURE__ */ jsx42(
        PopoverContent,
        {
          container,
          align,
          side,
          sideOffset,
          className: cn(
            // Override Popover's default `rounded-md` so the popover frame
            // matches the studio trigger's larger radius (Select pattern).
            "p-1 studio:rounded-xl",
            view === "custom" ? "w-auto" : "w-[200px]"
          ),
          children: view === "preset" ? /* @__PURE__ */ jsxs24("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsx42(
              "div",
              {
                role: "radiogroup",
                "aria-label": ariaLabel ?? (label ? `${label} options` : "Date filter options"),
                children: presets.map((preset) => /* @__PURE__ */ jsx42(
                  PresetRadio,
                  {
                    label: preset.label,
                    checked: selectedPresetId === preset.id,
                    onSelect: () => handlePresetClick(preset)
                  },
                  preset.id
                ))
              }
            ),
            allowCustom ? /* @__PURE__ */ jsx42(
              CustomRow,
              {
                label: customLabel,
                isActive: isCustom,
                onSelect: handleCustomClick
              }
            ) : null
          ] }) : /* @__PURE__ */ jsxs24("div", { className: "flex flex-col", children: [
            /* @__PURE__ */ jsx42("div", { className: "flex items-center px-2 pt-1", children: /* @__PURE__ */ jsx42(
              Button,
              {
                type: "button",
                variant: "ghost",
                modifier: "icon",
                size: "small",
                "aria-label": "Back to date presets",
                onClick: () => setView("preset"),
                children: /* @__PURE__ */ jsx42(Icon, { icon: faChevronLeft2, size: "small" })
              }
            ) }),
            /* @__PURE__ */ jsx42(
              Calendar,
              {
                mode: "range",
                resetOnSelect: true,
                selected: stagedRange,
                onSelect: handleRangeSelect,
                numberOfMonths,
                disabled: disabledDates,
                defaultMonth: defaultMonth ?? (value?.kind === "range" ? value.from : void 0),
                today,
                autoFocus: true
              }
            )
          ] })
        }
      )
    ] });
  }
);
DateFilter.displayName = "DateFilter";

// src/components/DatePicker/DatePicker.tsx
import * as React26 from "react";
import { format as format2 } from "date-fns/format";
import { faCalendar as faCalendar2 } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { jsx as jsx43, jsxs as jsxs25 } from "react/jsx-runtime";
var DEFAULT_FORMAT = "PPP";
function isRangeMode(props) {
  return props.mode === "range";
}
function renderSingleLabel(value, dateFormat, placeholder) {
  if (!value) {
    return { label: placeholder ?? "Pick a date", empty: true };
  }
  return { label: format2(value, dateFormat), empty: false };
}
function renderRangeLabel(range, dateFormat, placeholder, formatRange) {
  if (!range?.from) {
    return { label: placeholder ?? "Pick a date range", empty: true };
  }
  if (formatRange) {
    return { label: formatRange(range), empty: false };
  }
  const from = format2(range.from, dateFormat);
  const to = range.to ? format2(range.to, dateFormat) : "?";
  return { label: `${from} \u2013 ${to}`, empty: false };
}
var DatePicker = React26.forwardRef(
  function DatePicker2(props, ref) {
    const {
      placeholder,
      disabled,
      dateFormat = DEFAULT_FORMAT,
      size,
      align = "start",
      side,
      container,
      triggerClassName,
      contentClassName,
      className,
      open: openProp,
      onOpenChange,
      id,
      "aria-label": ariaLabel,
      numberOfMonths,
      defaultMonth,
      today
    } = props;
    const formatRange = isRangeMode(props) ? props.formatRange : void 0;
    const isControlled = openProp !== void 0;
    const [internalOpen, setInternalOpen] = React26.useState(false);
    const open2 = isControlled ? openProp : internalOpen;
    const setOpen = React26.useCallback(
      (next) => {
        if (!isControlled) {
          setInternalOpen(next);
        }
        onOpenChange?.(next);
      },
      [isControlled, onOpenChange]
    );
    const { label, empty } = isRangeMode(props) ? renderRangeLabel(props.value, dateFormat, placeholder, formatRange) : renderSingleLabel(props.value, dateFormat, placeholder);
    const handleSingleSelect = (date) => {
      if (!isRangeMode(props)) {
        props.onChange?.(date);
        if (date) {
          setOpen(false);
        }
      }
    };
    const handleRangeSelect = (range) => {
      if (isRangeMode(props)) {
        props.onChange?.(range);
        if (range?.from && range?.to) {
          setOpen(false);
        }
      }
    };
    return /* @__PURE__ */ jsxs25(Popover, { open: open2, onOpenChange: setOpen, children: [
      /* @__PURE__ */ jsx43(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs25(
        Button,
        {
          ref,
          "data-slot": "date-picker-trigger",
          "data-empty": empty || void 0,
          variant: "outline",
          size,
          disabled,
          id,
          "aria-label": ariaLabel,
          className: cn(
            "justify-start text-left font-normal min-w-[280px]",
            "data-[empty]:text-neutral-3",
            triggerClassName ?? className
          ),
          children: [
            /* @__PURE__ */ jsx43(Icon, { icon: faCalendar2, size: "small" }),
            /* @__PURE__ */ jsx43("span", { className: "truncate", children: label })
          ]
        }
      ) }),
      /* @__PURE__ */ jsx43(
        PopoverContent,
        {
          container,
          align,
          side,
          className: cn("w-auto p-0 studio:rounded-xl", contentClassName),
          children: isRangeMode(props) ? /* @__PURE__ */ jsx43(
            Calendar,
            {
              mode: "range",
              resetOnSelect: true,
              selected: props.value,
              onSelect: handleRangeSelect,
              numberOfMonths: numberOfMonths ?? 2,
              defaultMonth: defaultMonth ?? props.value?.from,
              today,
              autoFocus: true
            }
          ) : /* @__PURE__ */ jsx43(
            Calendar,
            {
              mode: "single",
              selected: props.value,
              onSelect: handleSingleSelect,
              numberOfMonths: numberOfMonths ?? 1,
              defaultMonth: defaultMonth ?? props.value,
              today,
              autoFocus: true
            }
          )
        }
      )
    ] });
  }
);
DatePicker.displayName = "DatePicker";

// src/components/FillPicker/FillPicker.tsx
import {
  useState as useState21,
  useEffect as useEffect16,
  useRef as useRef8,
  forwardRef as forwardRef17,
  useMemo as useMemo7,
  useCallback as useCallback10
} from "react";
import { Portal as Portal5 } from "@radix-ui/react-portal";
import { FontAwesomeIcon as FontAwesomeIcon14 } from "@fortawesome/react-fontawesome";
import { faTrashCan as faTrashCan3 } from "@fortawesome/pro-regular-svg-icons/faTrashCan";

// src/components/SegmentedControl/SegmentedControl.tsx
import * as React27 from "react";
import * as SegmentedControlGroupPrimitive from "@radix-ui/react-radio-group";
import { jsx as jsx44 } from "react/jsx-runtime";
var SegmentedControlSizeContext = React27.createContext(
  DEFAULT_COMPONENT_SIZE
);
var groupSizeClasses = {
  small: "h-8 max-h-8 gap-1 p-0.5",
  medium: "h-10 max-h-10 gap-2 p-0.5",
  large: "h-12 max-h-12 gap-2 p-0.5"
};
var itemSizeClasses = {
  small: "px-3 py-1.5 text-xs",
  medium: "px-4 py-2 text-sm",
  large: "px-4 py-2 text-sm"
};
var SegmentedControlGroup = React27.forwardRef(
  ({
    className,
    disabled,
    readOnly,
    iconOnly,
    size = DEFAULT_COMPONENT_SIZE,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx44(SegmentedControlSizeContext.Provider, { value: size, children: /* @__PURE__ */ jsx44(
      SegmentedControlGroupPrimitive.Root,
      {
        ref,
        className: cn(
          // Layout
          "flex",
          // Sizing
          "h-10 max-h-10",
          // Appearance
          "bg-neutral-1 rounded-md",
          // Borders
          "border-1 border-neutral-1",
          // Conditional spacing
          { "gap-1": iconOnly },
          // Size variants
          groupSizeClasses[size],
          className
        ),
        ...props,
        disabled: disabled || readOnly
      }
    ) });
  }
);
SegmentedControlGroup.displayName = "SegmentedControlGroup";
var SegmentedControlGroupItem = React27.forwardRef(({ className, children, ...props }, ref) => {
  const size = React27.useContext(SegmentedControlSizeContext);
  return /* @__PURE__ */ jsx44(
    SegmentedControlGroupPrimitive.Item,
    {
      ref,
      className: cn(
        // Layout
        "inline-flex flex-1 items-center justify-center",
        // Spacing
        "gap-2 px-4 py-2",
        // Appearance
        "rounded-sm",
        // Typography
        "font-lato text-sm font-semibold",
        "text-neutral-3 whitespace-nowrap",
        // Transitions
        "transition-all",
        // Checked state
        "data-[state=checked]:bg-highlight-1 data-[state=checked]:text-highlight-2",
        "studio:data-[state=checked]:bg-surface-bright-tertiary studio:data-[state=checked]:text-neutral-1",
        // Disabled state
        "disabled:cursor-not-allowed",
        "data-[state=checked]:disabled:bg-highlight-2 data-[state=checked]:disabled:text-neutral-1",
        // Focus state
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
        // Hover state
        "hover:cursor-pointer",
        // Size variants
        itemSizeClasses[size],
        className
      ),
      ...props,
      children
    }
  );
});
SegmentedControlGroupItem.displayName = "SegmentedControlGroupItem";

// src/components/Stack/Stack.tsx
import * as React28 from "react";
import { jsx as jsx45 } from "react/jsx-runtime";
var Stack = React28.forwardRef(
  ({ children, ...props }, ref) => {
    return /* @__PURE__ */ jsx45(Flex, { ref, direction: "column", ...props, children });
  }
);
Stack.displayName = "Stack";

// src/components/FillPicker/ImageFill/ImageFill.tsx
import { FontAwesomeIcon as FontAwesomeIcon11 } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/pro-regular-svg-icons/faTrashCan";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";

// src/components/Label/Label.tsx
import * as React29 from "react";
import { cva as cva10 } from "class-variance-authority";
import { jsx as jsx46 } from "react/jsx-runtime";
var labelVariants = cva10(
  // `type-label` is a studio-scoped raw CSS class (`[data-theme='studio']
  // .type-label` in tailwind.css) that overrides font-family / size /
  // weight / line-height under studio. No-op under light, where the
  // light-mode utilities apply.
  "font-lato text-sm font-semibold leading-4 pe-2 py-0.5 text-ellipsis overflow-hidden whitespace-nowrap type-label",
  {
    variants: {
      state: {
        default: "text-neutral-1",
        error: "text-destructive-1"
      },
      row: {
        true: "pe-2 py-2.5",
        false: ""
      },
      disabled: {
        true: "text-neutral-3",
        false: ""
      },
      readOnly: {
        true: "text-neutral-3",
        false: ""
      }
    },
    defaultVariants: {
      state: "default",
      row: false,
      disabled: false,
      readOnly: false
    }
  }
);
var Label2 = React29.forwardRef(
  ({ className, state, row, disabled, readOnly, ...props }, ref) => (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    /* @__PURE__ */ jsx46(
      "label",
      {
        ref,
        className: cn(
          labelVariants({ state, row, disabled, readOnly }),
          className
        ),
        ...props
      }
    )
  )
);
Label2.displayName = "Label";

// src/components/FillPicker/ImageFill/ImageFill.tsx
import { Fragment as Fragment8, jsx as jsx47, jsxs as jsxs26 } from "react/jsx-runtime";
var POSITION_OPTIONS = [
  { value: "top left", label: "Top left" },
  { value: "top center", label: "Top center" },
  { value: "top right", label: "Top right" },
  { value: "center left", label: "Center left" },
  { value: "center", label: "Center" },
  { value: "center right", label: "Center right" },
  { value: "bottom left", label: "Bottom left" },
  { value: "bottom center", label: "Bottom center" },
  { value: "bottom right", label: "Bottom right" }
];
var REPEAT_OPTIONS = [
  { value: "repeat", label: "Repeat" },
  { value: "repeat-x", label: "Repeat horizontally" },
  { value: "repeat-y", label: "Repeat vertically" },
  { value: "no-repeat", label: "Don't repeat" }
];
var FieldRow = ({
  label,
  children
}) => /* @__PURE__ */ jsxs26("div", { className: "flex items-center gap-1", children: [
  /* @__PURE__ */ jsx47(Label2, { className: "flex-1", children: label }),
  /* @__PURE__ */ jsx47("div", { className: "flex-1", children })
] });
var ImageFill = ({
  value,
  onChange,
  onClear,
  renderImageUpload
}) => {
  const src = value?.src || "";
  const mode = value?.mode || "fill";
  const position = value?.position || "center";
  const repeat = value?.repeat || "no-repeat";
  const currentSettings = { src, mode, position, repeat };
  const updateSettings = (updates) => {
    onChange?.({ ...currentSettings, ...updates });
  };
  const handleImageSelect = (url) => {
    updateSettings({ src: url });
  };
  const handleImageClear = () => {
    onClear?.();
  };
  const handleModeChange = (newMode) => {
    const updates = {
      mode: newMode
    };
    if (newMode !== "tile") {
      updates.repeat = "no-repeat";
    }
    updateSettings(updates);
  };
  const handlePositionChange = (newPosition) => {
    updateSettings({ position: newPosition });
  };
  const handleRepeatChange = (newRepeat) => {
    updateSettings({ repeat: newRepeat });
  };
  return /* @__PURE__ */ jsxs26("div", { className: cn("flex flex-col gap-3", "py-2"), children: [
    /* @__PURE__ */ jsx47(
      "div",
      {
        className: cn(
          "relative w-full overflow-hidden rounded-md",
          src && "h-[168px]"
        ),
        children: src ? /* @__PURE__ */ jsxs26(Fragment8, { children: [
          /* @__PURE__ */ jsx47(
            "img",
            {
              src,
              alt: "",
              className: "h-full w-full object-cover",
              "data-testid": "image-preview"
            }
          ),
          /* @__PURE__ */ jsx47(
            Button,
            {
              variant: "ghost",
              modifier: "icon",
              size: "small",
              className: cn(
                "absolute bottom-2 right-2",
                "bg-neutral-1/80 hover:bg-neutral-1"
              ),
              onClick: handleImageClear,
              "aria-label": "Remove image",
              "data-testid": "remove-image-button",
              children: /* @__PURE__ */ jsx47(FontAwesomeIcon11, { icon: faTrashCan })
            }
          )
        ] }) : renderImageUpload?.({
          src,
          onImageSelect: handleImageSelect,
          onImageClear: handleImageClear
        }) || /* @__PURE__ */ jsxs26(
          "div",
          {
            className: cn(
              "flex h-full flex-col items-center justify-center gap-2",
              "text-neutral-2 text-sm"
            ),
            "data-testid": "image-placeholder",
            children: [
              /* @__PURE__ */ jsx47(FontAwesomeIcon11, { icon: faImage, className: "size-8 opacity-50" }),
              /* @__PURE__ */ jsx47("span", { children: "No image selected" })
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs26("div", { className: "flex flex-col gap-1", children: [
      /* @__PURE__ */ jsx47(Label2, { children: "Fill mode" }),
      /* @__PURE__ */ jsxs26(
        SegmentedControlGroup,
        {
          value: mode,
          onValueChange: handleModeChange,
          size: "small",
          "data-testid": "fill-mode-control",
          children: [
            /* @__PURE__ */ jsx47(SegmentedControlGroupItem, { value: "fill", children: "Fill" }),
            /* @__PURE__ */ jsx47(SegmentedControlGroupItem, { value: "fit", children: "Fit" }),
            /* @__PURE__ */ jsx47(SegmentedControlGroupItem, { value: "tile", children: "Tile" })
          ]
        }
      )
    ] }),
    mode !== "tile" ? /* @__PURE__ */ jsx47(FieldRow, { label: "Position", children: /* @__PURE__ */ jsxs26(Select, { value: position, onValueChange: handlePositionChange, children: [
      /* @__PURE__ */ jsx47(SelectTrigger, { size: "small", "data-testid": "position-trigger", children: /* @__PURE__ */ jsx47(SelectValue, {}) }),
      /* @__PURE__ */ jsx47(SelectContent, { "data-testid": "position-content", children: POSITION_OPTIONS.map((opt) => /* @__PURE__ */ jsx47(SelectItem, { value: opt.value, children: opt.label }, opt.value)) })
    ] }) }) : null,
    mode === "tile" ? /* @__PURE__ */ jsx47(FieldRow, { label: "Repeat", children: /* @__PURE__ */ jsxs26(Select, { value: repeat, onValueChange: handleRepeatChange, children: [
      /* @__PURE__ */ jsx47(SelectTrigger, { size: "small", "data-testid": "repeat-trigger", children: /* @__PURE__ */ jsx47(SelectValue, {}) }),
      /* @__PURE__ */ jsx47(SelectContent, { "data-testid": "repeat-content", children: REPEAT_OPTIONS.map((opt) => /* @__PURE__ */ jsx47(SelectItem, { value: opt.value, children: opt.label }, opt.value)) })
    ] }) }) : null
  ] });
};
ImageFill.displayName = "ImageFill";

// src/components/FillPicker/GradientFill/GradientFill.tsx
import { useState as useState20, useCallback as useCallback9 } from "react";

// src/components/FillPicker/GradientFill/GradientBar.tsx
import { useRef as useRef7, useState as useState19, useCallback as useCallback8 } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToParentElement
} from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { FontAwesomeIcon as FontAwesomeIcon12 } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";

// src/components/FillPicker/GradientFill/utils.ts
import tinycolor2 from "tinycolor2";
function generateGradientCSS(gradient) {
  const sortedStops = [...gradient.stops].sort(
    (a, b) => a.position - b.position
  );
  const stops = sortedStops.map(
    (s) => `rgba(${s.color.r},${s.color.g},${s.color.b},${s.color.a ?? 1}) ${s.position}%`
  ).join(", ");
  if (gradient.type === "radial") {
    return `radial-gradient(circle, ${stops})`;
  }
  if (gradient.type === "conic") {
    return `conic-gradient(from ${gradient.angle}deg, ${stops})`;
  }
  return `linear-gradient(${gradient.angle}deg, ${stops})`;
}
function interpolateColorAtPosition(stops, position) {
  const sorted = [...stops].sort((a, b) => a.position - b.position);
  if (position <= sorted[0].position) {
    return { ...sorted[0].color };
  }
  if (position >= sorted[sorted.length - 1].position) {
    return { ...sorted[sorted.length - 1].color };
  }
  let leftStop = sorted[0];
  let rightStop = sorted[sorted.length - 1];
  for (let i = 0; i < sorted.length - 1; i++) {
    if (position >= sorted[i].position && position <= sorted[i + 1].position) {
      leftStop = sorted[i];
      rightStop = sorted[i + 1];
      break;
    }
  }
  const range = rightStop.position - leftStop.position;
  const factor = range === 0 ? 0 : (position - leftStop.position) / range;
  const interpolate = (a, b) => Math.round(a + (b - a) * factor);
  return {
    r: interpolate(leftStop.color.r, rightStop.color.r),
    g: interpolate(leftStop.color.g, rightStop.color.g),
    b: interpolate(leftStop.color.b, rightStop.color.b),
    a: (leftStop.color.a ?? 1) + ((rightStop.color.a ?? 1) - (leftStop.color.a ?? 1)) * factor
  };
}
function reverseGradientStops(stops) {
  return stops.map((stop) => ({
    ...stop,
    position: 100 - stop.position
  }));
}
function generateStopId() {
  return `stop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
function backgroundSizeToMode(backgroundSize) {
  switch (backgroundSize) {
    case "cover":
      return "fill";
    case "contain":
      return "fit";
    default:
      return "tile";
  }
}
function parseGradientStops(stopsString) {
  const stops = [];
  const tokens = [];
  let depth = 0;
  let current = "";
  for (const char of stopsString) {
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (char === "," && depth === 0) {
      tokens.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  if (current.trim()) tokens.push(current.trim());
  tokens.forEach((token) => {
    const posMatch = token.match(/([\d.]+)%\s*$/);
    if (!posMatch) return;
    const position = Number.parseFloat(posMatch[1]);
    const colorStr = token.slice(0, posMatch.index).trim();
    const parsed = tinycolor2(colorStr);
    if (!parsed.isValid()) return;
    const { r, g, b, a } = parsed.toRgb();
    stops.push({
      id: generateStopId(),
      position,
      color: { r, g, b, a }
    });
  });
  return stops;
}
function parseGradientFromCSS(cssGradient) {
  const linearMatch = cssGradient.match(
    /^linear-gradient\((\d+)deg,\s*(.+)\)$/
  );
  if (linearMatch) {
    const angle = Number.parseInt(linearMatch[1], 10);
    const stopsString = linearMatch[2];
    const stops = parseGradientStops(stopsString);
    if (stops.length >= 2) {
      return { type: "linear", angle, stops };
    }
  }
  const radialMatch = cssGradient.match(/^radial-gradient\(circle,\s*(.+)\)$/);
  if (radialMatch) {
    const stopsString = radialMatch[1];
    const stops = parseGradientStops(stopsString);
    if (stops.length >= 2) {
      return { type: "radial", angle: 0, stops };
    }
  }
  const conicMatch = cssGradient.match(
    /^conic-gradient\(from\s+(\d+)deg,\s*(.+)\)$/
  );
  if (conicMatch) {
    const angle = Number.parseInt(conicMatch[1], 10);
    const stopsString = conicMatch[2];
    const stops = parseGradientStops(stopsString);
    if (stops.length >= 2) {
      return { type: "conic", angle, stops };
    }
  }
  return null;
}
function splitBackgroundImageParts(bgImage) {
  const parts = [];
  let depth = 0;
  let currentPart = "";
  [...bgImage].forEach((char) => {
    if (char === "(") depth += 1;
    if (char === ")") depth -= 1;
    if (char === "," && depth === 0) {
      parts.push(currentPart.trim());
      currentPart = "";
    } else {
      currentPart += char;
    }
  });
  if (currentPart.trim()) {
    parts.push(currentPart.trim());
  }
  return parts;
}

// src/components/FillPicker/GradientFill/GradientBar.tsx
import { jsx as jsx48, jsxs as jsxs27 } from "react/jsx-runtime";
function getStopColorCSS(color) {
  return `rgba(${color.r},${color.g},${color.b},${color.a ?? 1})`;
}
var StopHandle = ({ stop, isActive, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: stop.id,
    data: { stop }
  });
  const handleClick = (e) => {
    e.stopPropagation();
    onClick(stop.id);
  };
  const dragTransform = CSS.Translate.toString(transform);
  const combinedTransform = dragTransform ? `translateX(-50%) ${dragTransform}` : "translateX(-50%)";
  return /* @__PURE__ */ jsx48(
    "button",
    {
      ref: setNodeRef,
      type: "button",
      style: {
        left: `${stop.position}%`,
        transform: combinedTransform,
        border: "2px solid white",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.2), 0 1px 3px rgba(0,0,0,0.3)"
      },
      className: cn(
        "absolute top-[2px]",
        "box-border flex h-[60px] w-[15px] cursor-grab items-center rounded-[2px]",
        "focus:outline-none focus:ring-2 focus:ring-[var(--border-focus-ring)]",
        isDragging && "z-10 cursor-grabbing",
        isActive && "ring-2 ring-[var(--border-focus-ring)]"
      ),
      onClick: handleClick,
      "aria-label": `Color stop at ${stop.position}%`,
      "data-testid": `stop-handle-${stop.id}`,
      ...attributes,
      ...listeners,
      children: /* @__PURE__ */ jsx48(
        "div",
        {
          className: "relative h-full w-full rounded-[1px]",
          style: { backgroundColor: getStopColorCSS(stop.color) },
          children: /* @__PURE__ */ jsx48("div", { className: "absolute inset-0 rounded-[1px] bg-[rgba(0,0,0,0.2)]" })
        }
      )
    }
  );
};
var GradientBar = ({
  gradient,
  activeStopId,
  onStopDrag,
  onStopClick,
  onAddStop
}) => {
  const barRef = useRef7(null);
  const [hoverPosition, setHoverPosition] = useState19(null);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 6 }
  });
  const sensors = useSensors(pointerSensor);
  const handleDragEnd = useCallback8(
    (event) => {
      const { active, delta } = event;
      if (!barRef.current) return;
      const barRect = barRef.current.getBoundingClientRect();
      const stop = gradient.stops.find((s) => s.id === active.id);
      if (!stop) return;
      const pixelPos = stop.position / 100 * barRect.width + delta.x;
      const newPosition = Math.round(pixelPos / barRect.width * 100);
      onStopDrag(String(active.id), clamp(newPosition, 0, 100));
    },
    [gradient.stops, onStopDrag]
  );
  const handleBarClick = useCallback8(
    (e) => {
      if (!barRef.current) return;
      const barRect = barRef.current.getBoundingClientRect();
      const clickX = e.clientX - barRect.left;
      const position = Math.round(clickX / barRect.width * 100);
      onAddStop(clamp(position, 0, 100));
    },
    [onAddStop]
  );
  const handleMouseMove = useCallback8((e) => {
    if (!barRef.current) return;
    const barRect = barRef.current.getBoundingClientRect();
    const hoverX = e.clientX - barRect.left;
    const position = Math.round(hoverX / barRect.width * 100);
    setHoverPosition(clamp(position, 0, 100));
  }, []);
  const handleMouseLeave = useCallback8(() => {
    setHoverPosition(null);
  }, []);
  const handleKeyDown = useCallback8(
    (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onAddStop(50);
      }
    },
    [onAddStop]
  );
  const previewGradient = {
    ...gradient,
    type: "linear",
    angle: 90
  };
  const gradientCSS = generateGradientCSS(previewGradient);
  return /* @__PURE__ */ jsx48(
    DndContext,
    {
      sensors,
      onDragEnd: handleDragEnd,
      modifiers: [restrictToHorizontalAxis, restrictToParentElement],
      children: /* @__PURE__ */ jsxs27(
        "div",
        {
          ref: barRef,
          className: "relative h-16 w-full cursor-pointer rounded-md",
          style: { background: gradientCSS },
          onClick: handleBarClick,
          onKeyDown: handleKeyDown,
          onMouseMove: handleMouseMove,
          onMouseLeave: handleMouseLeave,
          role: "toolbar",
          tabIndex: 0,
          "aria-label": "Gradient bar - click to add a color stop",
          "data-testid": "gradient-bar",
          children: [
            hoverPosition !== null ? /* @__PURE__ */ jsx48(
              "div",
              {
                className: cn(
                  "absolute top-[2px] -translate-x-1/2",
                  "box-border flex h-[60px] w-[15px] flex-col items-center justify-center",
                  "pointer-events-none rounded-[2px]"
                ),
                style: {
                  left: `${hoverPosition}%`,
                  border: "1px solid white"
                },
                "data-testid": "hover-indicator",
                children: /* @__PURE__ */ jsx48(
                  "div",
                  {
                    className: "relative h-full w-full rounded-[1px]",
                    style: {
                      backgroundColor: getStopColorCSS(
                        interpolateColorAtPosition(gradient.stops, hoverPosition)
                      )
                    },
                    children: /* @__PURE__ */ jsx48("div", { className: "absolute inset-0 flex items-center justify-center", children: /* @__PURE__ */ jsx48(
                      FontAwesomeIcon12,
                      {
                        icon: faPlus,
                        style: {
                          fontSize: "10px",
                          color: "white",
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))"
                        }
                      }
                    ) })
                  }
                )
              }
            ) : null,
            gradient.stops.map((stop) => /* @__PURE__ */ jsx48(
              StopHandle,
              {
                stop,
                isActive: stop.id === activeStopId,
                onClick: onStopClick
              },
              stop.id
            ))
          ]
        }
      )
    }
  );
};
GradientBar.displayName = "GradientBar";

// src/components/FillPicker/GradientFill/GradientStopRow.tsx
import { FontAwesomeIcon as FontAwesomeIcon13 } from "@fortawesome/react-fontawesome";
import { faTrashCan as faTrashCan2 } from "@fortawesome/pro-regular-svg-icons/faTrashCan";
import { jsx as jsx49, jsxs as jsxs28 } from "react/jsx-runtime";
var GradientStopRow = ({
  stop,
  canDelete,
  container,
  onPositionChange,
  onColorChange,
  onDelete
}) => {
  const handlePositionChange = (e) => {
    const value = Number.parseInt(e.target.value, 10);
    if (!Number.isNaN(value)) {
      onPositionChange(stop.id, Math.max(0, Math.min(100, value)));
    }
  };
  const handleColorChange = (result) => {
    onColorChange(stop.id, result.rgb);
  };
  const handleDelete = () => {
    onDelete(stop.id);
  };
  return /* @__PURE__ */ jsxs28(
    "div",
    {
      className: "flex items-center gap-1",
      "data-testid": `stop-row-${stop.id}`,
      children: [
        /* @__PURE__ */ jsx49("div", { className: "w-[112px] shrink-0", children: /* @__PURE__ */ jsx49(
          Input,
          {
            type: "number",
            value: stop.position,
            min: 0,
            max: 100,
            step: 1,
            suffix: "%",
            size: "small",
            onChange: handlePositionChange,
            "aria-label": "Stop position",
            "data-testid": `stop-position-${stop.id}`
          }
        ) }),
        /* @__PURE__ */ jsx49("div", { className: "flex-1", children: /* @__PURE__ */ jsx49(
          ColorInput,
          {
            value: stop.color,
            onChange: handleColorChange,
            display: "HEX",
            container
          }
        ) }),
        /* @__PURE__ */ jsx49(
          Button,
          {
            variant: "ghost",
            modifier: "icon",
            size: "small",
            disabled: !canDelete,
            onClick: handleDelete,
            "aria-label": "Delete stop",
            "data-testid": `stop-delete-${stop.id}`,
            children: /* @__PURE__ */ jsx49(FontAwesomeIcon13, { icon: faTrashCan2 })
          }
        )
      ]
    }
  );
};
GradientStopRow.displayName = "GradientStopRow";

// src/components/FillPicker/GradientFill/GradientFill.tsx
import { jsx as jsx50, jsxs as jsxs29 } from "react/jsx-runtime";
var GradientFill = ({ value, container, onChange }) => {
  const [activeStopId, setActiveStopId] = useState20(
    value.stops[0]?.id
  );
  const updateGradient = useCallback9(
    (updates) => {
      onChange({ ...value, ...updates });
    },
    [value, onChange]
  );
  const handleTypeChange = useCallback9(
    (type) => {
      updateGradient({ type });
    },
    [updateGradient]
  );
  const handleAngleChange = useCallback9(
    (e) => {
      const angle = Number.parseInt(e.target.value, 10);
      if (!Number.isNaN(angle)) {
        updateGradient({ angle: Math.max(0, Math.min(360, angle)) });
      }
    },
    [updateGradient]
  );
  const handleReverse = useCallback9(() => {
    updateGradient({ stops: reverseGradientStops(value.stops) });
  }, [value.stops, updateGradient]);
  const handleStopDrag = useCallback9(
    (id, newPosition) => {
      const newStops = value.stops.map(
        (stop) => stop.id === id ? { ...stop, position: newPosition } : stop
      );
      updateGradient({ stops: newStops });
    },
    [value.stops, updateGradient]
  );
  const handleStopClick = useCallback9((id) => {
    setActiveStopId(id);
  }, []);
  const handleAddStop = useCallback9(
    (position) => {
      const newStop = {
        id: generateStopId(),
        position,
        color: interpolateColorAtPosition(value.stops, position)
      };
      const newStops = [...value.stops, newStop];
      updateGradient({ stops: newStops });
      setActiveStopId(newStop.id);
    },
    [value.stops, updateGradient]
  );
  const handleStopPositionChange = useCallback9(
    (id, position) => {
      const newStops = value.stops.map(
        (stop) => stop.id === id ? { ...stop, position } : stop
      );
      updateGradient({ stops: newStops });
    },
    [value.stops, updateGradient]
  );
  const handleStopColorChange = useCallback9(
    (id, color) => {
      const newStops = value.stops.map(
        (stop) => stop.id === id ? { ...stop, color } : stop
      );
      updateGradient({ stops: newStops });
    },
    [value.stops, updateGradient]
  );
  const handleStopDelete = useCallback9(
    (id) => {
      if (value.stops.length <= 2) return;
      const newStops = value.stops.filter((stop) => stop.id !== id);
      updateGradient({ stops: newStops });
      if (activeStopId === id) {
        setActiveStopId(newStops[0]?.id);
      }
    },
    [value.stops, activeStopId, updateGradient]
  );
  const sortedStops = [...value.stops].sort((a, b) => a.position - b.position);
  const canDeleteStops = value.stops.length > 2;
  return /* @__PURE__ */ jsxs29("div", { className: cn("flex flex-col gap-3 py-2"), children: [
    /* @__PURE__ */ jsxs29("div", { className: "flex items-center gap-1", children: [
      /* @__PURE__ */ jsx50("div", { className: "flex-1", children: /* @__PURE__ */ jsxs29(Select, { value: value.type, onValueChange: handleTypeChange, children: [
        /* @__PURE__ */ jsx50(SelectTrigger, { size: "small", "data-testid": "gradient-type-trigger", children: /* @__PURE__ */ jsx50(SelectValue, {}) }),
        /* @__PURE__ */ jsxs29(SelectContent, { "data-testid": "gradient-type-content", children: [
          /* @__PURE__ */ jsx50(SelectItem, { value: "linear", children: "Linear" }),
          /* @__PURE__ */ jsx50(SelectItem, { value: "radial", children: "Radial" }),
          /* @__PURE__ */ jsx50(SelectItem, { value: "conic", children: "Conic" })
        ] })
      ] }) }),
      value.type === "linear" || value.type === "conic" ? /* @__PURE__ */ jsx50("div", { className: "w-[100px]", children: /* @__PURE__ */ jsx50(
        Input,
        {
          type: "number",
          value: value.angle,
          min: 0,
          max: 360,
          step: 1,
          suffix: "\xB0",
          size: "small",
          onChange: handleAngleChange,
          "aria-label": "Gradient angle",
          "data-testid": "gradient-angle-input"
        }
      ) }) : null,
      /* @__PURE__ */ jsx50(
        Button,
        {
          variant: "neutral",
          modifier: "icon",
          size: "small",
          onClick: handleReverse,
          "aria-label": "Reverse gradient",
          "data-testid": "gradient-reverse-button",
          children: /* @__PURE__ */ jsx50(
            "svg",
            {
              width: "14",
              height: "14",
              viewBox: "0 0 14 14",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg",
              children: /* @__PURE__ */ jsx50(
                "path",
                {
                  d: "M0.875 4.8125H11.0141L9.44508 6.38148C9.10328 6.72328 9.10328 7.27699 9.44508 7.61879C9.61406 7.79023 9.83828 7.875 10.0625 7.875C10.2867 7.875 10.5104 7.78955 10.681 7.61865L13.7435 4.55615C14.0853 4.21436 14.0853 3.66064 13.7435 3.31885L10.681 0.256348C10.3392 -0.0854492 9.78551 -0.0854492 9.44371 0.256348C9.10191 0.598145 9.10191 1.15186 9.44371 1.49365L11.0141 3.0625H0.875C0.391289 3.0625 0 3.45379 0 3.9375C0 4.42121 0.391289 4.8125 0.875 4.8125ZM13.125 9.1875H2.98867L4.55766 7.61852C4.89945 7.27672 4.89945 6.72301 4.55766 6.38121C4.21586 6.03941 3.66215 6.03941 3.32035 6.38121L0.257852 9.44371C-0.0839453 9.78551 -0.0839453 10.3392 0.257852 10.681L3.32035 13.7435C3.48906 13.9152 3.71328 14 3.9375 14C4.16172 14 4.38539 13.9146 4.55602 13.7437C4.89781 13.4019 4.89781 12.8481 4.55602 12.5063L2.98867 10.9375H13.125C13.6087 10.9375 14 10.5462 14 10.0625C14 9.57879 13.609 9.1875 13.125 9.1875Z",
                  fill: "currentColor"
                }
              )
            }
          )
        }
      )
    ] }),
    /* @__PURE__ */ jsx50(
      GradientBar,
      {
        gradient: value,
        activeStopId,
        onStopDrag: handleStopDrag,
        onStopClick: handleStopClick,
        onAddStop: handleAddStop
      }
    ),
    /* @__PURE__ */ jsx50("div", { className: "flex flex-col gap-1", children: sortedStops.map((stop) => /* @__PURE__ */ jsx50(
      GradientStopRow,
      {
        stop,
        canDelete: canDeleteStops,
        container,
        onPositionChange: handleStopPositionChange,
        onColorChange: handleStopColorChange,
        onDelete: handleStopDelete
      },
      stop.id
    )) })
  ] });
};
GradientFill.displayName = "GradientFill";

// src/components/FillPicker/FillPicker.tsx
import { Fragment as Fragment9, jsx as jsx51, jsxs as jsxs30 } from "react/jsx-runtime";
var DEFAULT_FILL_COLOR = {
  r: 255,
  g: 255,
  b: 255,
  a: 1
};
var DEFAULT_IMAGE_FILL = {
  src: "",
  mode: "fill",
  position: "center",
  repeat: "no-repeat"
};
function isColorEqual(a, b) {
  return a.r === b.r && a.g === b.g && a.b === b.b && (a.a ?? 1) === (b.a ?? 1);
}
function contrastingBW(color) {
  const luminance = (0.299 * color.r + 0.587 * color.g + 0.114 * color.b) / 255;
  return luminance > 0.5 ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 };
}
function buildGradientFromColor(color) {
  return {
    type: "linear",
    angle: 90,
    stops: [
      { id: generateStopId(), position: 10, color: { ...color, a: 1 } },
      { id: generateStopId(), position: 90, color: contrastingBW(color) }
    ]
  };
}
var DEFAULT_GRADIENT = buildGradientFromColor(DEFAULT_FILL_COLOR);
function getInitialTab(value) {
  if (value?.image?.src) return "image";
  if (value?.gradient) return "gradient";
  return "color";
}
function colorResultFromFill(value) {
  const rgb = value?.color || DEFAULT_FILL_COLOR;
  if (value?.colorCss) {
    const cssVar = parseCssVariable(value.colorCss);
    if (cssVar) {
      return {
        css: value.colorCss,
        hex: toHex8(rgb),
        hsl: toHsl(rgb),
        rgb,
        swatch: cssVar.name
      };
    }
    return { css: value.colorCss, hex: toHex8(rgb), hsl: toHsl(rgb), rgb };
  }
  return { css: toHex8(rgb), hex: toHex8(rgb), hsl: toHsl(rgb), rgb };
}
var FillPicker = forwardRef17(
  ({
    value,
    container,
    onChange,
    onClose,
    onClear,
    hideClearAll,
    open: open2,
    renderImageUpload,
    variant = "floating"
  }, ref) => {
    const isInline = variant === "inline";
    const isOpen = isInline ? true : Boolean(open2);
    const [activeTab, setActiveTab] = useState21(
      () => getInitialTab(value)
    );
    const [currentColor, setCurrentColor] = useState21(
      value?.color || DEFAULT_FILL_COLOR
    );
    const [currentColorResult, setCurrentColorResult] = useState21(
      () => colorResultFromFill(value)
    );
    const [currentGradient, setCurrentGradient] = useState21(
      value?.gradient || DEFAULT_GRADIENT
    );
    const [currentImage, setCurrentImage] = useState21(
      value?.image || DEFAULT_IMAGE_FILL
    );
    const selfChangeRef = useRef8(false);
    useEffect16(() => {
      if (isOpen) {
        setCurrentColor(value?.color || DEFAULT_FILL_COLOR);
        setCurrentColorResult(colorResultFromFill(value));
        setCurrentGradient(value?.gradient || DEFAULT_GRADIENT);
        setCurrentImage(value?.image || DEFAULT_IMAGE_FILL);
        setActiveTab(getInitialTab(value));
      }
    }, [isOpen]);
    useEffect16(() => {
      if (!isOpen) return;
      if (selfChangeRef.current) {
        selfChangeRef.current = false;
        return;
      }
      if (value?.color) {
        setCurrentColor(value.color);
      }
      if (value?.gradient) {
        setCurrentGradient(value.gradient);
      }
      if (value?.image) {
        setCurrentImage(value.image);
      }
    }, [value, isOpen]);
    const emitChange = useCallback10(
      (fill) => {
        selfChangeRef.current = true;
        onChange?.(fill);
      },
      [onChange]
    );
    const handleColorChange = useCallback10(
      (newColor) => {
        const color = typeof newColor === "object" && newColor !== null && "rgb" in newColor ? newColor.rgb : typeof newColor === "object" && newColor !== null && "r" in newColor ? newColor : void 0;
        if (!color) {
          return;
        }
        setCurrentColor(color);
        setCurrentColorResult({
          css: toHex8(color),
          hex: toHex8(color),
          hsl: toHsl(color),
          rgb: color
        });
        const gradientToPreserve = activeTab !== "color" ? value?.gradient || (currentGradient !== DEFAULT_GRADIENT ? currentGradient : void 0) : void 0;
        emitChange({
          color,
          gradient: gradientToPreserve,
          image: currentImage.src ? currentImage : void 0
        });
      },
      [activeTab, currentGradient, currentImage, emitChange, value?.gradient]
    );
    const handleSwatchSelect = useCallback10(
      (result) => {
        const color = result.rgb;
        setCurrentColor(color);
        setCurrentColorResult(result);
        const gradientToPreserve = activeTab !== "color" ? value?.gradient || (currentGradient !== DEFAULT_GRADIENT ? currentGradient : void 0) : void 0;
        emitChange({
          color,
          colorCss: result.css,
          gradient: gradientToPreserve,
          image: currentImage.src ? currentImage : void 0
        });
      },
      [activeTab, currentGradient, currentImage, emitChange, value?.gradient]
    );
    const handleGradientChange = useCallback10(
      (newGradient) => {
        setCurrentGradient(newGradient);
        const hasCustomColor2 = !isColorEqual(currentColor, DEFAULT_FILL_COLOR);
        emitChange({
          color: hasCustomColor2 ? currentColor : value?.color,
          gradient: newGradient,
          image: currentImage.src ? currentImage : void 0
        });
      },
      [currentColor, currentImage, emitChange, value?.color]
    );
    const handleImageChange = useCallback10(
      (newImage) => {
        setCurrentImage(newImage);
        const hasCustomColor2 = !isColorEqual(currentColor, DEFAULT_FILL_COLOR);
        const gradientToPreserve = value?.gradient || (currentGradient !== DEFAULT_GRADIENT ? currentGradient : void 0);
        emitChange({
          color: hasCustomColor2 ? currentColor : value?.color,
          gradient: gradientToPreserve,
          image: newImage
        });
      },
      [currentColor, currentGradient, emitChange, value?.color, value?.gradient]
    );
    const handleImageClear = useCallback10(() => {
      setCurrentImage(DEFAULT_IMAGE_FILL);
      const hasCustomColor2 = !isColorEqual(currentColor, DEFAULT_FILL_COLOR);
      const hasColor = value?.color || hasCustomColor2;
      const gradientToPreserve = value?.gradient || (currentGradient !== DEFAULT_GRADIENT ? currentGradient : void 0);
      if (hasColor || gradientToPreserve) {
        emitChange({
          color: value?.color || (hasCustomColor2 ? currentColor : void 0),
          gradient: gradientToPreserve
        });
      } else {
        emitChange(null);
      }
    }, [
      currentColor,
      currentGradient,
      emitChange,
      value?.color,
      value?.gradient
    ]);
    const handleClearAll = useCallback10(() => {
      onClear?.();
      onClose?.();
    }, [onClear, onClose]);
    const handleTabChange = useCallback10(
      (tab) => {
        setActiveTab(tab);
        if (tab === "gradient") {
          const hasCustomColor2 = !isColorEqual(
            currentColor,
            DEFAULT_FILL_COLOR
          );
          const gradient = value?.gradient || (currentGradient !== DEFAULT_GRADIENT ? currentGradient : buildGradientFromColor(currentColor));
          setCurrentGradient(gradient);
          emitChange({
            color: hasCustomColor2 ? currentColor : value?.color,
            gradient,
            image: currentImage.src ? currentImage : void 0
          });
        } else if (tab === "color") {
          const sortedStops = [...currentGradient.stops].sort(
            (a, b) => a.position - b.position
          );
          const gradientColor = currentGradient !== DEFAULT_GRADIENT ? sortedStops[0]?.color : void 0;
          const color = gradientColor || currentColor;
          setCurrentColor(color);
          emitChange({
            color,
            image: currentImage.src ? currentImage : void 0
          });
        }
      },
      [
        currentColor,
        currentGradient,
        currentImage,
        emitChange,
        value?.color,
        value?.gradient
      ]
    );
    const hasCustomColor = !isColorEqual(currentColor, DEFAULT_FILL_COLOR);
    const hasAnyFill = Boolean(
      hasCustomColor || value?.color || value?.gradient || currentImage.src
    );
    const clearAllAction = !hideClearAll && hasAnyFill && onClear ? /* @__PURE__ */ jsx51(
      Button,
      {
        "aria-label": "Clear fill",
        onClick: handleClearAll,
        variant: "ghost",
        modifier: "icon",
        size: "small",
        children: /* @__PURE__ */ jsx51(FontAwesomeIcon14, { icon: faTrashCan3 })
      }
    ) : null;
    const dragOptions = useMemo7(
      () => ({
        getPositionReferenceElement: () => ref?.current || null,
        offset: 8
      }),
      [ref]
    );
    console.log("SB: currentColorResult", currentColorResult);
    const content = /* @__PURE__ */ jsxs30(Fragment9, { children: [
      /* @__PURE__ */ jsxs30(
        SegmentedControlGroup,
        {
          value: activeTab,
          onValueChange: handleTabChange,
          size: "small",
          children: [
            /* @__PURE__ */ jsx51(SegmentedControlGroupItem, { value: "color", children: "Color" }),
            /* @__PURE__ */ jsx51(SegmentedControlGroupItem, { value: "gradient", children: "Gradient" }),
            /* @__PURE__ */ jsx51(SegmentedControlGroupItem, { value: "image", children: "Image" })
          ]
        }
      ),
      activeTab === "color" ? /* @__PURE__ */ jsx51(
        ColorPickerContent,
        {
          currentColor: currentColorResult,
          customPickerColor: currentColor,
          container,
          onCustomChange: handleColorChange,
          onSwatchSelect: handleSwatchSelect,
          open: isOpen
        }
      ) : null,
      activeTab === "gradient" ? /* @__PURE__ */ jsx51(
        GradientFill,
        {
          value: currentGradient,
          container,
          onChange: handleGradientChange
        }
      ) : null,
      activeTab === "image" ? /* @__PURE__ */ jsx51(
        ImageFill,
        {
          value: currentImage,
          onChange: handleImageChange,
          onClear: handleImageClear,
          renderImageUpload
        }
      ) : null
    ] });
    if (isInline) {
      return /* @__PURE__ */ jsxs30(Stack, { gap: "md", children: [
        clearAllAction ? /* @__PURE__ */ jsx51(Flex, { justify: "end", children: clearAllAction }) : null,
        content
      ] });
    }
    return /* @__PURE__ */ jsx51(Portal5, { container, children: /* @__PURE__ */ jsxs30(
      FloatingPanel,
      {
        open: isOpen,
        variant: "floating",
        onClickOutside: onClose,
        dragOptions,
        children: [
          /* @__PURE__ */ jsx51(
            FloatingPanelHeader,
            {
              title: "Fill",
              onClose,
              actions: clearAllAction ? [clearAllAction] : void 0
            }
          ),
          /* @__PURE__ */ jsx51(FloatingPanelContent, { children: content })
        ]
      }
    ) });
  }
);
FillPicker.displayName = "FillPicker";

// src/components/Dropdown/Dropdown.tsx
import * as React33 from "react";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { faCheck as faCheck5 } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faCircle } from "@fortawesome/pro-solid-svg-icons/faCircle";
import { faChevronRight as faChevronRight2 } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { cva as cva11 } from "class-variance-authority";
import { jsx as jsx52, jsxs as jsxs31 } from "react/jsx-runtime";
var dropdownMenuContentVariants = cva11(
  [
    // Position
    "z-50",
    "origin-(--radix-dropdown-menu-content-transform-origin)",
    // Sizing
    "max-h-(--radix-dropdown-menu-content-available-height)",
    // Spacing
    "p-1",
    // Typography
    "text-neutral-1",
    // Appearance
    "bg-neutral-0 rounded-md shadow-md",
    // Borders
    "border-neutral-1 border",
    // Overflow
    "overflow-x-hidden overflow-y-auto",
    // Animations - Enter
    "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
    // Animations - Exit
    "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
    // Animations - Slide by side
    "data-[side=bottom]:slide-in-from-top-2",
    "data-[side=left]:slide-in-from-right-2",
    "data-[side=right]:slide-in-from-left-2",
    "data-[side=top]:slide-in-from-bottom-2"
  ],
  {
    variants: {
      size: {
        compact: "min-w-[8rem]",
        spacious: "min-w-[20rem]"
      }
    },
    defaultVariants: {
      size: "compact"
    }
  }
);
var DropdownInstanceContext = React33.createContext(null);
function DropdownMenu({
  open: controlledOpen,
  onOpenChange,
  defaultOpen,
  ...props
}) {
  const {
    open: open2,
    onOpenChange: handleOpenChange,
    instanceId
  } = useShadowDismiss({
    open: controlledOpen,
    onOpenChange,
    defaultOpen
  });
  return /* @__PURE__ */ jsx52(DropdownInstanceContext.Provider, { value: instanceId, children: /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Root,
    {
      "data-slot": "dropdown-menu",
      open: open2,
      onOpenChange: handleOpenChange,
      ...props
    }
  ) });
}
function DropdownMenuPortal({
  ...props
}) {
  return /* @__PURE__ */ jsx52(DropdownMenuPrimitive.Portal, { "data-slot": "dropdown-menu-portal", ...props });
}
function DropdownMenuTrigger({
  ...props
}) {
  const instanceId = React33.useContext(DropdownInstanceContext);
  return /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Trigger,
    {
      "data-slot": "dropdown-menu-trigger",
      "data-radix-instance": instanceId ?? void 0,
      ...props
    }
  );
}
function DropdownMenuContent({
  className,
  sideOffset = 4,
  container,
  size,
  ...props
}) {
  const instanceId = React33.useContext(DropdownInstanceContext);
  return /* @__PURE__ */ jsx52(DropdownMenuPrimitive.Portal, { container, children: /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Content,
    {
      "data-slot": "dropdown-menu-content",
      "data-radix-instance": instanceId ?? void 0,
      sideOffset,
      className: cn(dropdownMenuContentVariants({ size }), className),
      ...props
    }
  ) });
}
function DropdownMenuGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx52(DropdownMenuPrimitive.Group, { "data-slot": "dropdown-menu-group", ...props });
}
var dropdownMenuItemVariants = cva11(
  [
    // Layout - grid with optional icon column
    "relative grid items-center",
    "grid-cols-[0_1fr_auto] has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr_auto]",
    "has-[>svg]:gap-x-2",
    // Sizing
    "rounded-sm",
    // Spacing
    "px-2 py-1.5",
    // Typography
    "text-sm",
    // Appearance
    "cursor-pointer select-none outline-hidden",
    // States - Disabled
    "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    // Inset
    "data-[inset=true]:pl-8",
    // Icons - align to first column, vertically center
    '[&>svg]:pointer-events-none [&>svg]:shrink-0 [&>svg:not([class*="size-"])]:size-4',
    "[&>svg]:col-start-1 [&>svg]:row-span-2"
  ],
  {
    variants: {
      variant: {
        default: [
          "focus:bg-highlight-3 focus:text-neutral-1",
          "studio:focus:bg-surface-bright-secondary"
        ],
        destructive: ["text-destructive-1", "focus:bg-destructive-2/10"],
        // No-visuals variant: the consumer supplies its own row styling
        // (e.g. `actionListItemVariants(...)`), which already covers
        // layout, typography, cursor, disabled, and the `data-[highlighted]`
        // highlight. We don't want the default grid/`text-sm` base fighting
        // it, while Radix still provides menuitem behavior (roving focus,
        // data-highlighted, onSelect, dismiss-on-select, data-disabled).
        unstyled: ""
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }
);
function DropdownMenuItem({
  className,
  inset,
  variant,
  children,
  asChild,
  ...props
}) {
  const wrappedChildren = asChild ? children : React33.Children.map(
    children,
    (child) => typeof child === "string" || typeof child === "number" ? /* @__PURE__ */ jsx52("span", { className: "col-start-2", children: child }) : child
  );
  return /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Item,
    {
      "data-slot": "dropdown-menu-item",
      "data-inset": inset,
      asChild,
      className: cn(dropdownMenuItemVariants({ variant }), className),
      ...props,
      children: wrappedChildren
    }
  );
}
function DropdownMenuItemTitle({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    "span",
    {
      "data-slot": "dropdown-menu-item-title",
      className: cn("col-start-2", className),
      ...props
    }
  );
}
function DropdownMenuItemDescription({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    "span",
    {
      "data-slot": "dropdown-menu-item-description",
      className: cn(
        "text-neutral-3 col-start-2 py-1 text-xs font-normal",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}) {
  return /* @__PURE__ */ jsxs31(
    DropdownMenuPrimitive.CheckboxItem,
    {
      "data-slot": "dropdown-menu-checkbox-item",
      className: cn(
        // Layout
        "relative flex items-center gap-2",
        // Sizing
        "rounded-sm",
        // Spacing
        "py-1.5 pl-8 pr-2",
        // Typography
        "text-sm",
        // Appearance
        "outline-hidden cursor-default select-none",
        // States - Focus
        "focus:bg-highlight-3 focus:text-neutral-1",
        "studio:focus:bg-surface-bright-secondary",
        // States - Disabled
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // Icons
        '[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
        className
      ),
      checked,
      ...props,
      children: [
        /* @__PURE__ */ jsx52("span", { className: "size-3.5 pointer-events-none absolute left-2 flex items-center justify-center", children: /* @__PURE__ */ jsx52(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx52(Icon, { icon: faCheck5, size: "small" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuRadioGroup({
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.RadioGroup,
    {
      "data-slot": "dropdown-menu-radio-group",
      ...props
    }
  );
}
function DropdownMenuRadioItem({
  className,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs31(
    DropdownMenuPrimitive.RadioItem,
    {
      "data-slot": "dropdown-menu-radio-item",
      className: cn(
        // Layout
        "relative flex items-center gap-2",
        // Sizing
        "rounded-sm",
        // Spacing
        "py-1.5 pl-8 pr-2",
        // Typography
        "text-sm",
        // Appearance
        "outline-hidden cursor-default select-none",
        // States - Focus
        "focus:bg-highlight-3 focus:text-neutral-1",
        "studio:focus:bg-surface-bright-secondary",
        // States - Disabled
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // Icons
        '[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
        className
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsx52("span", { className: "size-3.5 pointer-events-none absolute left-2 flex items-center justify-center", children: /* @__PURE__ */ jsx52(DropdownMenuPrimitive.ItemIndicator, { children: /* @__PURE__ */ jsx52(Icon, { icon: faCircle, size: "x-small" }) }) }),
        children
      ]
    }
  );
}
function DropdownMenuLabel({
  className,
  inset,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Label,
    {
      "data-slot": "dropdown-menu-label",
      "data-inset": inset,
      className: cn(
        // Spacing
        "px-2 py-1.5",
        // Typography
        "text-neutral-3 text-xs font-semibold uppercase",
        // Inset
        "data-[inset]:pl-8",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSeparator({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.Separator,
    {
      "data-slot": "dropdown-menu-separator",
      className: cn(
        // Spacing
        "mx-2 my-1",
        // Sizing
        "h-px",
        // Appearance
        "bg-neutral-2",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuShortcut({
  className,
  ...props
}) {
  return /* @__PURE__ */ jsx52(
    "span",
    {
      "data-slot": "dropdown-menu-shortcut",
      className: cn(
        // Grid positioning
        "col-start-3 row-span-2 row-start-1",
        // Typography
        "text-neutral-4 text-xs tracking-widest",
        className
      ),
      ...props
    }
  );
}
function DropdownMenuSub({
  ...props
}) {
  return /* @__PURE__ */ jsx52(DropdownMenuPrimitive.Sub, { "data-slot": "dropdown-menu-sub", ...props });
}
function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}) {
  return /* @__PURE__ */ jsxs31(
    DropdownMenuPrimitive.SubTrigger,
    {
      "data-slot": "dropdown-menu-sub-trigger",
      "data-inset": inset,
      className: cn(
        // Layout
        "flex items-center gap-2",
        // Sizing
        "rounded-sm",
        // Spacing
        "px-2 py-1.5",
        // Typography
        "text-sm",
        // Appearance
        "outline-hidden cursor-default select-none",
        // States - Focus
        "focus:bg-highlight-3 focus:text-neutral-1",
        "studio:focus:bg-surface-bright-secondary",
        // States - Open
        "data-[state=open]:bg-highlight-3 data-[state=open]:text-neutral-1",
        "studio:data-[state=open]:bg-surface-bright-secondary",
        // Inset
        "data-[inset]:pl-8",
        // Icons
        '[&_svg:not([class*="size-"])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0',
        '[&_svg:not([class*="text-"])]:text-neutral-3',
        className
      ),
      ...props,
      children: [
        children,
        /* @__PURE__ */ jsx52(Icon, { icon: faChevronRight2, size: "small", className: "ml-auto" })
      ]
    }
  );
}
function DropdownMenuSubContent({
  className,
  container,
  ...props
}) {
  return /* @__PURE__ */ jsx52(DropdownMenuPrimitive.Portal, { container, children: /* @__PURE__ */ jsx52(
    DropdownMenuPrimitive.SubContent,
    {
      "data-slot": "dropdown-menu-sub-content",
      className: cn(
        // Position
        "z-50",
        "origin-(--radix-dropdown-menu-content-transform-origin)",
        // Sizing
        "min-w-[8rem]",
        // Spacing
        "p-1",
        // Typography
        "text-neutral-1",
        // Appearance
        "bg-neutral-1 rounded-md shadow-lg",
        // Borders
        "border-neutral-1 border",
        // Overflow
        "overflow-hidden",
        // Animations - Enter
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        // Animations - Exit
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        // Animations - Slide by side
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        className
      ),
      ...props
    }
  ) });
}

// src/components/RadioGroup/RadioGroup.tsx
import * as React34 from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { jsx as jsx53, jsxs as jsxs32 } from "react/jsx-runtime";
var RadioGroup2 = React34.forwardRef(({ className, orientation = "vertical", ...props }, ref) => /* @__PURE__ */ jsx53(
  RadioGroupPrimitive.Root,
  {
    ref,
    className: cn(
      "flex",
      orientation === "horizontal" ? "flex-row gap-4" : "flex-col gap-2",
      className
    ),
    orientation,
    ...props
  }
));
RadioGroup2.displayName = "RadioGroup";
var RadioGroupItem = React34.forwardRef(({ className, children, label, ...props }, ref) => (
  // eslint-disable-next-line jsx-a11y/label-has-associated-control
  /* @__PURE__ */ jsxs32("label", { className: "has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 flex min-w-0 cursor-pointer flex-row items-center gap-2", children: [
    /* @__PURE__ */ jsx53(
      RadioGroupPrimitive.Item,
      {
        ref,
        className: cn(
          // Layout
          "peer shrink-0",
          // Sizing — 16×16 circle
          "size-4",
          // Appearance
          "rounded-full",
          // Borders — 1px neutral border (light), brand-soft (studio)
          "border border-neutral-4",
          "studio:border-[var(--color-brand-soft)]",
          // Checked state — brand border
          "data-[state=checked]:border-brand-1",
          "studio:data-[state=checked]:border-[var(--color-brand)]",
          // Focus ring
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
          "data-[focus=true]:ring-2 data-[focus=true]:ring-[var(--border-focus-ring)]",
          // Disabled
          "disabled:cursor-not-allowed",
          "studio:disabled:border-edge",
          // Disabled + checked
          "disabled:data-[state=checked]:border-neutral-3",
          "studio:disabled:data-[state=checked]:border-edge",
          // Center the indicator dot
          "flex items-center justify-center",
          className
        ),
        ...props,
        children: /* @__PURE__ */ jsx53(
          RadioGroupPrimitive.Indicator,
          {
            className: cn(
              // 8px filled circle — centered by the Item's flex layout
              "block size-2 rounded-full",
              // Light: brand fill
              "bg-brand-1",
              // Studio: brand fill
              "studio:bg-[var(--color-brand)]",
              // Disabled: muted fill (Indicator carries data-disabled from Radix)
              "data-[disabled]:bg-neutral-3",
              "studio:data-[disabled]:bg-[var(--color-edge)]"
            )
          }
        )
      }
    ),
    children || label ? /* @__PURE__ */ jsx53("span", { className: "text-neutral-1 flex min-w-0 items-center gap-2 overflow-hidden text-sm", children: children || label }) : null
  ] })
));
RadioGroupItem.displayName = "RadioGroupItem";

// src/components/Form/FormItem.tsx
import {
  useMemo as useMemo8,
  createContext as createContext8,
  forwardRef as forwardRef19
} from "react";
import { jsx as jsx54 } from "react/jsx-runtime";
var FormItemContext = createContext8({
  id: "",
  error: false,
  disabled: false,
  readOnly: false,
  labelId: ""
});
var FormItem = forwardRef19(
  ({ className, error, disabled, readOnly, ...props }, ref) => {
    const id = useMemo8(() => crypto.randomUUID(), []);
    const contextValue = useMemo8(
      () => ({ id, error, disabled, readOnly, labelId: `${id}-label` }),
      [id, error, disabled, readOnly]
    );
    return /* @__PURE__ */ jsx54(FormItemContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx54(
      "div",
      {
        ref,
        className: cn("flex flex-col gap-1 pb-3", className),
        ...props
      }
    ) });
  }
);
FormItem.displayName = "FormItem";

// src/components/Form/FormLabel.tsx
import React36 from "react";

// src/components/Form/use-form-item.ts
import { useContext as useContext8 } from "react";
var useFormItem = () => {
  const itemContext = useContext8(FormItemContext);
  if (!itemContext) {
    throw new Error("useFormField should be used within <FormItem>");
  }
  const { id, error, disabled, readOnly, labelId } = itemContext;
  return {
    id,
    formItemId: `${id}-form-item`,
    formMessageId: `${id}-form-item-message`,
    error,
    disabled,
    readOnly,
    labelId
  };
};

// src/components/Form/FormLabel.tsx
import { jsx as jsx55 } from "react/jsx-runtime";
var FormLabel = React36.forwardRef(
  ({ className, ...props }, ref) => {
    const { formItemId, error, disabled, readOnly, labelId } = useFormItem();
    return /* @__PURE__ */ jsx55(
      Label2,
      {
        id: labelId,
        ref,
        htmlFor: formItemId,
        state: error ? "error" : "default",
        className: cn("flex-1", className),
        disabled,
        readOnly,
        ...props
      }
    );
  }
);
FormLabel.displayName = "FormLabel";

// src/components/Form/FormControl.tsx
import React37 from "react";
import { Slot as Slot5 } from "@radix-ui/react-slot";
import { jsx as jsx56 } from "react/jsx-runtime";
var FormControl = React37.forwardRef(({ className, ...props }, ref) => {
  const { error, formItemId, formMessageId, labelId, ...itemProps } = useFormItem();
  return /* @__PURE__ */ jsx56(
    Slot5,
    {
      ...itemProps,
      ...props,
      ref,
      id: formItemId,
      "aria-describedby": formMessageId,
      "aria-invalid": !!error,
      "aria-labelledby": labelId,
      className: cn("flex-1", className)
    }
  );
});
FormControl.displayName = "FormControl";

// src/components/Form/FormMessage.tsx
import React38 from "react";
import { FontAwesomeIcon as FontAwesomeIcon15 } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons/faCircleExclamation";
import { jsx as jsx57, jsxs as jsxs33 } from "react/jsx-runtime";
var FormMessage = React38.forwardRef(({ className, state, children, ...props }, ref) => {
  const { formMessageId, disabled, readOnly } = useFormItem();
  return /* @__PURE__ */ jsxs33(
    "p",
    {
      ref,
      id: formMessageId,
      className: cn(
        // Layout
        "flex flex-row items-center",
        // Spacing
        "gap-1",
        // Typography
        "font-lato text-neutral-3 text-xs font-normal leading-6",
        // Error state
        state === "error" && "text-destructive-1",
        // Disabled/ReadOnly state
        (disabled || readOnly) && "text-neutral-4",
        className
      ),
      ...props,
      children: [
        state === "error" && /* @__PURE__ */ jsx57(FontAwesomeIcon15, { icon: faCircleExclamation }),
        children
      ]
    }
  );
});
FormMessage.displayName = "FormMessage";

// src/components/Form/FormRow.tsx
import { forwardRef as forwardRef20 } from "react";
import { jsx as jsx58 } from "react/jsx-runtime";
var FormRow = forwardRef20(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsx58(
      "div",
      {
        ref,
        className: cn("flex flex-row items-center gap-1", className),
        ...props
      }
    );
  }
);
FormRow.displayName = "FormRow";

// src/components/Modal/Modal.tsx
import * as React40 from "react";
import * as ModalPrimitive from "@radix-ui/react-dialog";
import { cva as cva12 } from "class-variance-authority";
import { FontAwesomeIcon as FontAwesomeIcon16 } from "@fortawesome/react-fontawesome";
import { faXmarkLarge as faXmarkLarge2 } from "@fortawesome/pro-solid-svg-icons/faXmarkLarge";

// src/components/Modal/ModalPortalContext.ts
import { createContext as createContext9, useContext as useContext9 } from "react";
var ModalPortalContext = createContext9(null);
var useModalPortal = () => useContext9(ModalPortalContext);

// src/components/Modal/Modal.tsx
import { jsx as jsx59, jsxs as jsxs34 } from "react/jsx-runtime";
var modalContentVariants = cva12("grid w-full", {
  variants: {
    size: {
      small: "max-w-sm",
      regular: "max-w-lg",
      large: "max-w-4xl"
    }
  },
  defaultVariants: {
    size: "regular"
  }
});
var ModalClose = ModalPrimitive.Close;
var ModalTrigger = ModalPrimitive.Trigger;
var ModalPortal = ModalPrimitive.Portal;
var ModalOverlay = React40.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx59(
  ModalPrimitive.Overlay,
  {
    ref,
    className: cn(
      // Position
      "fixed inset-0 z-50",
      // Appearance
      "bg-[rgba(0,0,0,0.4)] backdrop-blur-[2px]",
      // Animations
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className
    ),
    ...props
  }
));
ModalOverlay.displayName = ModalPrimitive.Overlay.displayName;
var Modal = React40.forwardRef(({ children, container, trigger, ...props }, ref) => {
  const [overlayRoot, setOverlayRoot] = React40.useState(
    null
  );
  return /* @__PURE__ */ jsx59(ModalPortalContext.Provider, { value: overlayRoot, children: /* @__PURE__ */ jsxs34(ModalPrimitive.Root, { ...props, children: [
    trigger,
    /* @__PURE__ */ jsxs34(ModalPortal, { container, children: [
      /* @__PURE__ */ jsx59(
        "div",
        {
          ref: setOverlayRoot,
          className: "fixed inset-0 z-51 [&>*]:pointer-events-auto",
          style: { pointerEvents: "none" }
        }
      ),
      /* @__PURE__ */ jsx59(ModalOverlay, { ref }),
      children
    ] })
  ] }) });
});
var ModalContent = React40.forwardRef(
  ({
    className,
    size = "regular",
    children,
    withoutFooter,
    hideClose,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs34(
      ModalPrimitive.Content,
      {
        ref,
        className: cn(
          // Layout
          modalContentVariants({ size }),
          // Position
          "fixed left-[50%] top-[50%] z-50",
          "translate-x-[-50%] translate-y-[-50%]",
          // Appearance
          "bg-neutral-0 text-neutral-1 rounded-md shadow-2xl",
          // Typography
          "font-lato",
          // Animations
          "duration-200",
          "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
          "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
          // Conditional padding
          { "pb-4": withoutFooter },
          className
        ),
        ...props,
        children: [
          children,
          hideClose ? null : /* @__PURE__ */ jsxs34(
            ModalPrimitive.Close,
            {
              className: cn(
                // Position
                "absolute right-6 top-5",
                // Appearance
                "text-neutral-1 rounded-sm opacity-70",
                // Interactions
                "transition-opacity",
                "hover:cursor-pointer hover:opacity-100",
                "focus:outline-none focus:ring-2 focus:ring-offset-2",
                "disabled:pointer-events-none"
              ),
              children: [
                /* @__PURE__ */ jsx59(FontAwesomeIcon16, { icon: faXmarkLarge2 }),
                /* @__PURE__ */ jsx59("span", { className: "sr-only", children: "Close" })
              ]
            }
          )
        ]
      }
    );
  }
);
ModalContent.displayName = ModalPrimitive.Content.displayName;
var ModalHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx59(
  "div",
  {
    className: cn(
      // Layout
      "flex flex-col",
      // Spacing
      "space-y-1.5 px-6 py-5",
      // Typography
      "text-center sm:text-left",
      className
    ),
    ...props
  }
);
ModalHeader.displayName = "ModalHeader";
var ModalFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx59(
  "div",
  {
    className: cn(
      // Layout
      "flex items-center justify-end",
      // Spacing
      "gap-2 p-4",
      // Borders
      "border-neutral-1 border-t",
      className
    ),
    ...props
  }
);
ModalFooter.displayName = "ModalFooter";
var ModalTitle = React40.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx59(
  ModalPrimitive.Title,
  {
    ref,
    className: cn(
      // Layout
      "flex items-center",
      // Spacing
      "gap-2",
      // Typography
      "text-lg font-semibold",
      className
    ),
    ...props
  }
));
ModalTitle.displayName = ModalPrimitive.Title.displayName;
var ModalDescription = React40.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx59(
  ModalPrimitive.Description,
  {
    ref,
    className: cn(
      // Spacing
      "px-6 pb-4 pt-2",
      className
    ),
    asChild: true,
    ...props,
    children: /* @__PURE__ */ jsx59("div", { children })
  }
));
ModalDescription.displayName = ModalPrimitive.Description.displayName;

// src/components/MultiSelect/MultiSelect.tsx
import * as React41 from "react";
import { Command as CommandPrimitive2 } from "cmdk";
import { FontAwesomeIcon as FontAwesomeIcon17 } from "@fortawesome/react-fontawesome";
import { faCheck as faCheck6 } from "@fortawesome/pro-regular-svg-icons/faCheck";
import { faCaretDown as faCaretDown4 } from "@fortawesome/pro-solid-svg-icons/faCaretDown";
import { jsx as jsx60, jsxs as jsxs35 } from "react/jsx-runtime";
var MultiSelectContext = React41.createContext(
  null
);
function useMultiSelectContext() {
  const context = React41.useContext(MultiSelectContext);
  if (!context) {
    throw new Error("MultiSelect components must be used within a MultiSelect");
  }
  return context;
}
function useMultiSelect() {
  return useMultiSelectContext();
}
var EMPTY = [];
function MultiSelect({
  children,
  value: controlledValue,
  defaultValue = EMPTY,
  onValueChange,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  disabled = false
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React41.useState(defaultOpen);
  const [uncontrolledValue, setUncontrolledValue] = React41.useState(defaultValue);
  const [search, setSearch] = React41.useState("");
  const isOpenControlled = controlledOpen !== void 0;
  const isValueControlled = controlledValue !== void 0;
  const open2 = isOpenControlled ? controlledOpen : uncontrolledOpen;
  const value = isValueControlled ? controlledValue : uncontrolledValue;
  const setOpen = React41.useCallback(
    (newOpen) => {
      if (disabled && newOpen) {
        return;
      }
      if (!isOpenControlled) {
        setUncontrolledOpen(newOpen);
      }
      if (!newOpen) {
        setSearch("");
      }
      onOpenChange?.(newOpen);
    },
    [disabled, isOpenControlled, onOpenChange]
  );
  const commitValue = React41.useCallback(
    (next) => {
      if (!isValueControlled) {
        setUncontrolledValue(next);
      }
      onValueChange?.(next);
    },
    [isValueControlled, onValueChange]
  );
  const toggleValue = React41.useCallback(
    (itemValue) => {
      commitValue(
        value.includes(itemValue) ? value.filter((v) => v !== itemValue) : [...value, itemValue]
      );
    },
    [value, commitValue]
  );
  const removeValue = React41.useCallback(
    (itemValue) => {
      commitValue(value.filter((v) => v !== itemValue));
    },
    [value, commitValue]
  );
  const contextValue = React41.useMemo(
    () => ({
      open: open2,
      setOpen,
      value,
      toggleValue,
      removeValue,
      search,
      setSearch,
      disabled
    }),
    [open2, setOpen, value, toggleValue, removeValue, search, disabled]
  );
  return /* @__PURE__ */ jsx60(MultiSelectContext.Provider, { value: contextValue, children: /* @__PURE__ */ jsx60(Popover, { open: open2, onOpenChange: setOpen, children }) });
}
function MultiSelectTrigger({
  placeholder = "Select options...",
  className,
  size = "medium",
  "aria-label": ariaLabel,
  getLabel
}) {
  const { open: open2, setOpen, disabled, value, removeValue } = useMultiSelectContext();
  const hasValue = value.length > 0;
  const sizeClasses = {
    small: "min-h-8 px-2 py-1 text-sm",
    medium: "min-h-10 px-2 py-1.5 text-sm"
  };
  return /* @__PURE__ */ jsx60(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs35(
    "div",
    {
      role: "combobox",
      "aria-expanded": open2,
      "aria-label": ariaLabel,
      "aria-disabled": disabled || void 0,
      tabIndex: disabled ? -1 : 0,
      onKeyDown: (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen(!open2);
        }
      },
      className: cn(
        // Layout — wrap chips, caret pinned to the end
        "flex w-full flex-wrap items-center gap-1",
        // Appearance — mirror Button `neutral` field styling
        "rounded-md border border-neutral-1 bg-neutral-1 text-neutral-1",
        "hover:border-neutral-4 focus-visible:border-neutral-4",
        // Interactions
        "cursor-pointer outline-none",
        "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
        // Size variants
        sizeClasses[size],
        className
      ),
      children: [
        hasValue ? value.map((v) => {
          const label = getLabel ? getLabel(v) : v;
          return /* @__PURE__ */ jsx60(
            Chip,
            {
              variant: "neutral",
              size: "sm",
              onRemove: disabled ? void 0 : () => removeValue(v),
              removeLabel: typeof label === "string" ? `Remove ${label}` : "Remove",
              children: label
            },
            v
          );
        }) : /* @__PURE__ */ jsx60("span", { className: "text-neutral-4 truncate font-medium studio:font-normal", children: placeholder }),
        /* @__PURE__ */ jsx60(
          FontAwesomeIcon17,
          {
            icon: faCaretDown4,
            className: cn(
              // Layout
              "ml-auto self-center",
              // Sizing
              "size-4 shrink-0",
              // Appearance
              "opacity-50"
            )
          }
        )
      ]
    }
  ) });
}
function MultiSelectAnchor({ children }) {
  return /* @__PURE__ */ jsx60(PopoverAnchor, { asChild: true, children });
}
function MultiSelectContent({
  children,
  className,
  container,
  filter,
  side,
  sideOffset,
  align,
  alignOffset,
  onFocusOutside,
  onInteractOutside,
  onPointerDownOutside,
  onEscapeKeyDown
}) {
  return /* @__PURE__ */ jsx60(
    PopoverContent,
    {
      container,
      side,
      sideOffset,
      align,
      alignOffset,
      onFocusOutside,
      onInteractOutside,
      onPointerDownOutside,
      onEscapeKeyDown,
      onOpenAutoFocus: (e) => {
        e.preventDefault();
        const content = e.currentTarget;
        content?.querySelector("[cmdk-input]")?.focus();
      },
      className: cn(
        // Sizing — defaults to the anchor's width; override via className
        // (e.g. `w-80`) when anchoring to a small element.
        "w-[var(--radix-popover-trigger-width)]",
        // Spacing
        "p-0",
        // Radius — studio uses a larger pill-like popover radius per design
        // (PopoverContent's base `rounded-md` covers light/legacy).
        "studio:rounded-3xl",
        // Surface — studio paints the whole dialog on the bright (white)
        // surface per design; the inner Command is made transparent below so
        // this shows through. Light/legacy keep PopoverContent's bg-neutral-1.
        "studio:bg-surface-bright",
        className
      ),
      children: /* @__PURE__ */ jsx60(Command, { filter, className: "studio:bg-transparent", children })
    }
  );
}
function MultiSelectInput({
  placeholder = "Search...",
  className
}) {
  const { search, setSearch } = useMultiSelectContext();
  return /* @__PURE__ */ jsx60(
    CommandInput,
    {
      autoFocus: true,
      value: search,
      onValueChange: setSearch,
      placeholder,
      "aria-label": placeholder,
      className
    }
  );
}
function MultiSelectTokenField({
  placeholder = "Add tags\u2026",
  className,
  getLabel
}) {
  const { value, removeValue, search, setSearch, disabled } = useMultiSelectContext();
  const handleKeyDown = React41.useCallback(
    (e) => {
      if (e.key === "Backspace" && search === "" && value.length > 0) {
        e.preventDefault();
        removeValue(value[value.length - 1]);
      }
    },
    [search, value, removeValue]
  );
  return /* @__PURE__ */ jsxs35(
    "div",
    {
      className: cn(
        // Layout — chips wrap, input flows after them
        "flex flex-wrap items-center gap-1",
        // Box — studio uses a larger field radius and the `edge` border
        // token (mortar) per design; light/legacy keep neutral-2.
        "rounded-md border border-neutral-2 p-1.5 studio:rounded-lg studio:border-edge",
        className
      ),
      children: [
        value.map((v) => {
          const label = getLabel ? getLabel(v) : v;
          return /* @__PURE__ */ jsx60(
            Chip,
            {
              variant: "tag",
              size: "sm",
              onRemove: disabled ? void 0 : () => removeValue(v),
              removeLabel: typeof label === "string" ? `Remove ${label}` : "Remove",
              children: label
            },
            v
          );
        }),
        /* @__PURE__ */ jsx60(
          CommandPrimitive2.Input,
          {
            autoFocus: true,
            disabled,
            value: search,
            onValueChange: setSearch,
            onKeyDown: handleKeyDown,
            "aria-label": placeholder,
            placeholder: value.length === 0 ? placeholder : "",
            className: cn(
              // Sizing — grow to fill the row, stay on the chips' line
              "h-6 min-w-[80px] flex-1",
              // Appearance
              "bg-transparent",
              // Typography
              "text-sm font-medium studio:font-normal",
              // Interactions
              "outline-none",
              // Placeholder
              "placeholder:text-neutral-4",
              // Disabled
              "disabled:cursor-not-allowed disabled:opacity-50"
            )
          }
        )
      ]
    }
  );
}
function MultiSelectList({ children, className }) {
  return /* @__PURE__ */ jsx60(CommandList, { className, children });
}
function MultiSelectEmpty({
  children = "No results found.",
  className
}) {
  const { search } = useMultiSelectContext();
  return /* @__PURE__ */ jsx60(CommandEmpty, { className, children: typeof children === "function" ? children(search) : children });
}
function MultiSelectGroup({
  children,
  heading,
  className
}) {
  return /* @__PURE__ */ jsx60(CommandGroup, { heading, className, children });
}
function MultiSelectItem({
  children,
  value: itemValue,
  disabled,
  className,
  icon,
  onSelect,
  keywords
}) {
  const { value, toggleValue, setSearch } = useMultiSelectContext();
  const isSelected = value.includes(itemValue);
  const handleSelect = React41.useCallback(
    (currentValue) => {
      toggleValue(currentValue);
      setSearch("");
      onSelect?.(currentValue, !isSelected);
    },
    [toggleValue, setSearch, onSelect, isSelected]
  );
  return /* @__PURE__ */ jsxs35(
    CommandItem,
    {
      value: itemValue,
      onSelect: handleSelect,
      disabled,
      className: cn(
        // Studio row radius matches the design's rounded option row (10px);
        // there's no radius token at that step, so this mirrors the design's
        // own value, overriding CommandItem's default `rounded-sm`.
        "studio:rounded-[10px]",
        isSelected && "studio:bg-surface-bright-secondary studio:text-neutral-1",
        className
      ),
      keywords,
      children: [
        icon && /* @__PURE__ */ jsx60(CommandItemIcon, { children: icon }),
        /* @__PURE__ */ jsx60("span", { className: "min-w-0 flex-1 truncate", children }),
        /* @__PURE__ */ jsx60(
          FontAwesomeIcon17,
          {
            icon: faCheck6,
            className: cn(
              // Layout
              "ml-auto",
              // Sizing
              "size-3",
              // Visibility
              isSelected ? "opacity-100" : "opacity-0"
            )
          }
        )
      ]
    }
  );
}
MultiSelect.displayName = "MultiSelect";
MultiSelectTrigger.displayName = "MultiSelectTrigger";
MultiSelectAnchor.displayName = "MultiSelectAnchor";
MultiSelectContent.displayName = "MultiSelectContent";
MultiSelectInput.displayName = "MultiSelectInput";
MultiSelectTokenField.displayName = "MultiSelectTokenField";
MultiSelectList.displayName = "MultiSelectList";
MultiSelectEmpty.displayName = "MultiSelectEmpty";
MultiSelectGroup.displayName = "MultiSelectGroup";
MultiSelectItem.displayName = "MultiSelectItem";

// src/components/Sheet/Sheet.tsx
import * as React42 from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva as cva13 } from "class-variance-authority";
import { FontAwesomeIcon as FontAwesomeIcon18 } from "@fortawesome/react-fontawesome";
import { faXmarkLarge as faXmarkLarge3 } from "@fortawesome/pro-solid-svg-icons/faXmarkLarge";
import { jsx as jsx61, jsxs as jsxs36 } from "react/jsx-runtime";
var SheetTrigger = SheetPrimitive.Trigger;
var SheetClose = SheetPrimitive.Close;
var SheetPortal = SheetPrimitive.Portal;
var SheetOverlay = React42.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx61(
  SheetPrimitive.Overlay,
  {
    ref,
    "data-testid": "sheet-overlay",
    className: cn(
      "fixed inset-0 z-50",
      "bg-[rgba(0,0,0,0.4)] backdrop-blur-[2px]",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      className
    ),
    ...props
  }
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;
var sheetContentVariants = cva13(
  [
    "fixed z-50 flex flex-col overflow-hidden",
    "bg-neutral-0 text-neutral-1 shadow-2xl",
    "font-lato",
    "duration-300",
    "data-[state=open]:animate-in",
    "data-[state=closed]:animate-out"
  ],
  {
    variants: {
      side: {
        top: [
          "inset-x-0 top-0",
          "max-h-[80vh]",
          "border-neutral-1 border-b",
          "data-[state=open]:slide-in-from-top",
          "data-[state=closed]:slide-out-to-top"
        ],
        right: [
          "inset-y-0 right-0",
          "h-full w-3/4 max-w-sm",
          "border-neutral-1 border-l",
          "data-[state=open]:slide-in-from-right",
          "data-[state=closed]:slide-out-to-right"
        ],
        bottom: [
          "inset-x-0 bottom-0",
          "max-h-[80vh]",
          "border-neutral-1 border-t",
          "data-[state=open]:slide-in-from-bottom",
          "data-[state=closed]:slide-out-to-bottom"
        ],
        left: [
          "inset-y-0 left-0",
          "h-full w-3/4 max-w-sm",
          "border-neutral-1 border-r",
          "data-[state=open]:slide-in-from-left",
          "data-[state=closed]:slide-out-to-left"
        ]
      }
    },
    defaultVariants: {
      side: "right"
    }
  }
);
var SheetContext = React42.createContext({
  showOverlay: true
});
function Sheet({
  showOverlay = true,
  children,
  ...props
}) {
  const ctx = React42.useMemo(() => ({ showOverlay }), [showOverlay]);
  return /* @__PURE__ */ jsx61(SheetPrimitive.Root, { ...props, children: /* @__PURE__ */ jsx61(SheetContext.Provider, { value: ctx, children }) });
}
Sheet.displayName = "Sheet";
var SheetContent = React42.forwardRef(
  ({
    className,
    side = "right",
    container,
    showCloseButton = true,
    children,
    ...props
  }, ref) => {
    const { showOverlay } = React42.useContext(SheetContext);
    const handleInteractOutside = showOverlay ? props.onInteractOutside : (e) => {
      props.onInteractOutside?.(e);
      if (!e.defaultPrevented) e.preventDefault();
    };
    return /* @__PURE__ */ jsxs36(SheetPortal, { container, children: [
      showOverlay ? /* @__PURE__ */ jsx61(SheetOverlay, {}) : null,
      /* @__PURE__ */ jsxs36(
        SheetPrimitive.Content,
        {
          ref,
          className: cn(sheetContentVariants({ side }), className),
          ...props,
          onInteractOutside: handleInteractOutside,
          children: [
            children,
            showCloseButton ? /* @__PURE__ */ jsx61(
              SheetPrimitive.Close,
              {
                "aria-label": "Close",
                className: cn(
                  "absolute right-6 top-5",
                  "text-neutral-1 rounded-sm opacity-70",
                  "transition-opacity",
                  "hover:cursor-pointer hover:opacity-100",
                  "focus:outline-none focus:ring-2 focus:ring-offset-2",
                  "disabled:pointer-events-none"
                ),
                children: /* @__PURE__ */ jsx61(FontAwesomeIcon18, { icon: faXmarkLarge3 })
              }
            ) : null
          ]
        }
      )
    ] });
  }
);
SheetContent.displayName = SheetPrimitive.Content.displayName;
var SheetHeader = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx61(
  "div",
  {
    className: cn(
      "flex shrink-0 flex-col",
      "space-y-1.5 px-6 py-5",
      "text-center sm:text-left",
      className
    ),
    ...props
  }
);
SheetHeader.displayName = "SheetHeader";
var SheetFooter = ({
  className,
  ...props
}) => /* @__PURE__ */ jsx61(
  "div",
  {
    className: cn(
      "mt-auto flex shrink-0 items-center justify-end",
      "gap-2 p-4",
      "border-neutral-1 border-t",
      className
    ),
    ...props
  }
);
SheetFooter.displayName = "SheetFooter";
var SheetBody = React42.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx61(
  "div",
  {
    ref,
    className: cn("min-h-0 flex-1 overflow-y-auto", className),
    ...props
  }
));
SheetBody.displayName = "SheetBody";
var SheetTitle = React42.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx61(
  SheetPrimitive.Title,
  {
    ref,
    className: cn(
      "flex items-center",
      "gap-2",
      "text-lg font-semibold",
      className
    ),
    ...props
  }
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;
var SheetDescription = React42.forwardRef(({ className, children, ...props }, ref) => /* @__PURE__ */ jsx61(
  SheetPrimitive.Description,
  {
    ref,
    className: cn("px-6 pb-4 pt-2", className),
    asChild: true,
    ...props,
    children: /* @__PURE__ */ jsx61("div", { children })
  }
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

// src/components/StepBar/StepBar.tsx
import React43, {
  useState as useState24,
  useMemo as useMemo11,
  useCallback as useCallback12,
  useEffect as useEffect17,
  useRef as useRef9,
  isValidElement
} from "react";
import { faPlus as faPlus2 } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon as FontAwesomeIcon20 } from "@fortawesome/react-fontawesome";
import {
  DndContext as DndContext2,
  closestCenter,
  PointerSensor as PointerSensor2,
  useSensor as useSensor2,
  useSensors as useSensors2
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy
} from "@dnd-kit/sortable";

// src/components/StepBar/StepBarNavigation.tsx
import { faChevronLeft as faChevronLeft3 } from "@fortawesome/pro-regular-svg-icons/faChevronLeft";
import { faChevronRight as faChevronRight3 } from "@fortawesome/pro-regular-svg-icons/faChevronRight";
import { FontAwesomeIcon as FontAwesomeIcon19 } from "@fortawesome/react-fontawesome";
import { jsx as jsx62 } from "react/jsx-runtime";
var StepBarNavigation = ({
  direction,
  disabled,
  onClick
}) => {
  return /* @__PURE__ */ jsx62(
    "button",
    {
      type: "button",
      disabled,
      onClick,
      className: cn(
        // Layout
        "flex items-center justify-center",
        // Sizing
        "w-[18px] h-12",
        // Appearance
        "bg-neutral-0 rounded-sm",
        // Typography
        "text-neutral-1 text-sm",
        // Interactions
        "cursor-pointer",
        // Hover state
        "hover:bg-highlight-3",
        // Focus state
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
        // Active/pressed state
        "active:bg-highlight-2",
        // Disabled state
        "disabled:opacity-40 disabled:cursor-default disabled:pointer-events-none"
      ),
      "aria-label": direction === "left" ? "Scroll steps left" : "Scroll steps right",
      children: /* @__PURE__ */ jsx62(
        FontAwesomeIcon19,
        {
          icon: direction === "left" ? faChevronLeft3 : faChevronRight3,
          style: { fontSize: 14 }
        }
      )
    }
  );
};
StepBarNavigation.displayName = "StepBarNavigation";

// src/components/StepBar/StepBar.tsx
import { Fragment as Fragment10, jsx as jsx63, jsxs as jsxs37 } from "react/jsx-runtime";
var StepBar = ({
  className,
  children,
  maxVisibleSteps = 6,
  onAddStep,
  onDragEnd,
  ...props
}) => {
  const [items, setItems] = useState24([]);
  const [scrollOffset, setScrollOffset] = useState24(0);
  const didDragRef = useRef9(false);
  useEffect17(() => {
    const ids = React43.Children.toArray(children).map(
      (child) => child.props.id
    );
    setItems((prevItems) => {
      const filtered = prevItems.filter((id) => ids.includes(id));
      const appended = ids.filter((id) => !filtered.includes(id));
      if (filtered.length === prevItems.length && appended.length === 0) {
        return ids;
      }
      return [...filtered, ...appended];
    });
    const newTotal = ids.length;
    if (newTotal > maxVisibleSteps) {
      setScrollOffset(
        (prev) => Math.min(prev, Math.max(0, newTotal - maxVisibleSteps))
      );
    } else {
      setScrollOffset(0);
    }
  }, [children, maxVisibleSteps]);
  const totalSteps = items.length;
  const needsNavigation = totalSteps > maxVisibleSteps;
  const { childById, activeId } = useMemo11(() => {
    const childrenArray = React43.Children.toArray(
      children
    );
    const map = new Map(childrenArray.map((child) => [child.props.id, child]));
    const active = childrenArray.find((child) => child.props.active);
    return { childById: map, activeId: active?.props.id ?? "" };
  }, [children]);
  useEffect17(() => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    if (!needsNavigation) return;
    const activeIndex = items.indexOf(activeId);
    if (activeIndex === -1) return;
    setScrollOffset((prev) => {
      if (activeIndex < prev) return activeIndex;
      if (activeIndex >= prev + maxVisibleSteps) {
        return activeIndex - maxVisibleSteps + 1;
      }
      return prev;
    });
  }, [activeId, items, maxVisibleSteps, needsNavigation]);
  const canScrollLeft = scrollOffset > 0;
  const canScrollRight = scrollOffset + maxVisibleSteps < totalSteps;
  const scrollLeft = useCallback12(() => {
    setScrollOffset((prev) => Math.max(0, prev - 1));
  }, []);
  const scrollRight = useCallback12(() => {
    setScrollOffset((prev) => Math.min(totalSteps - maxVisibleSteps, prev + 1));
  }, [totalSteps, maxVisibleSteps]);
  const isDndEnabled = Boolean(onDragEnd);
  const pointerSensor = useSensor2(PointerSensor2, {
    activationConstraint: { distance: 6 }
  });
  const enabledSensors = useSensors2(pointerSensor);
  const disabledSensors = useSensors2();
  const sensors = isDndEnabled ? enabledSensors : disabledSensors;
  const handleDragEnd = useCallback12(
    (event) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        return;
      }
      const oldIndex = items.indexOf(String(active.id));
      const newIndex = items.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
        return;
      }
      const nextOrder = arrayMove(items, oldIndex, newIndex);
      didDragRef.current = true;
      setItems(nextOrder);
      const payload = {
        from: oldIndex,
        to: newIndex,
        targetStepId: String(over.id)
      };
      onDragEnd?.(String(active.id), payload);
    },
    [items, onDragEnd]
  );
  const orderedChildren = useMemo11(() => {
    return items.map((id, index) => {
      const child = childById.get(id);
      return child ? React43.cloneElement(child, { index }) : null;
    }).filter(Boolean);
  }, [childById, items]);
  const visibleChildren = useMemo11(
    () => needsNavigation ? orderedChildren.slice(scrollOffset, scrollOffset + maxVisibleSteps) : orderedChildren,
    [needsNavigation, orderedChildren, scrollOffset, maxVisibleSteps]
  );
  const visibleItemIds = useMemo11(
    () => needsNavigation ? items.slice(scrollOffset, scrollOffset + maxVisibleSteps) : items,
    [needsNavigation, items, scrollOffset, maxVisibleSteps]
  );
  const isTriggerElement = isValidElement(onAddStep);
  const TriggerContent = onAddStep && /* @__PURE__ */ jsxs37(Fragment10, { children: [
    /* @__PURE__ */ jsx63("div", { className: "border-1 border-neutral-1 gap-1 self-stretch rounded-sm" }),
    isTriggerElement ? onAddStep : /* @__PURE__ */ jsx63(
      Button,
      {
        variant: "ghost",
        modifier: "icon",
        size: "large",
        onClick: onAddStep,
        children: /* @__PURE__ */ jsx63(FontAwesomeIcon20, { icon: faPlus2 })
      }
    )
  ] });
  return /* @__PURE__ */ jsx63(
    DndContext2,
    {
      sensors,
      collisionDetection: closestCenter,
      onDragEnd: handleDragEnd,
      children: /* @__PURE__ */ jsx63("div", { className: "flex items-center justify-center", children: /* @__PURE__ */ jsxs37(
        "nav",
        {
          className: cn(
            // Layout
            "relative flex items-center justify-center",
            // Spacing
            "gap-1 p-1",
            // Appearance
            "bg-neutral-0 shadow-xs rounded-lg",
            // Borders
            "border-1 border-neutral-1",
            // Typography
            "font-lato",
            className
          ),
          ...props,
          children: [
            needsNavigation ? /* @__PURE__ */ jsx63(
              StepBarNavigation,
              {
                direction: "left",
                disabled: !canScrollLeft,
                onClick: scrollLeft
              }
            ) : null,
            /* @__PURE__ */ jsx63(
              SortableContext,
              {
                items: visibleItemIds,
                strategy: horizontalListSortingStrategy,
                children: visibleChildren
              }
            ),
            needsNavigation ? /* @__PURE__ */ jsx63(
              StepBarNavigation,
              {
                direction: "right",
                disabled: !canScrollRight,
                onClick: scrollRight
              }
            ) : null,
            TriggerContent
          ]
        }
      ) })
    }
  );
};
StepBar.displayName = "StepBar";

// src/components/StepBar/StepBarItem.tsx
import { useSortable } from "@dnd-kit/sortable";
import { CSS as CSS2 } from "@dnd-kit/utilities";

// src/components/StepBar/icons.tsx
import { jsx as jsx64, jsxs as jsxs38 } from "react/jsx-runtime";
var ModalRightBottomIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V13H13.6C13.2686 13 13 13.2686 13 13.6V18.4C13 18.7314 13.2686 19 13.6 19H21.5V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalLeftBottomIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V19H10.4C10.7314 19 11 18.7314 11 18.4V13.6C11 13.2686 10.7314 13 10.4 13H2.5V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalRightTopIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V7.40002H13.6C13.2686 7.40002 13 7.66865 13 8.00003V12.8C13 13.1314 13.2686 13.4 13.6 13.4H21.5V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalLeftTopIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V13.4H10.4C10.7314 13.4 11 13.1314 11 12.8V8.00003C11 7.66865 10.7314 7.40002 10.4 7.40002H2.5V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalCenterTopIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM7 6V12.4C7 12.7314 7.26863 13 7.6 13H16.4C16.7314 13 17 12.7314 17 12.4V6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8C2.5 6.35817 2.85817 6 3.3 6H7Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalCenterBottomIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H16.9917C16.9972 20.4675 17 20.4341 17 20.4V13.6C17 13.2686 16.7314 13 16.4 13H7.6C7.26863 13 7 13.2686 7 13.6V20.4C7 20.4341 7.00284 20.4675 7.0083 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalCenterCenterIcon = () => /* @__PURE__ */ jsxs38(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: [
      /* @__PURE__ */ jsx64(
        "path",
        {
          d: "M5.6 9C5.26863 9 5 9.26863 5 9.6V17.4C5 17.7314 5.26863 18 5.6 18H18.4C18.7314 18 19 17.7314 19 17.4V9.6C19 9.26862 18.7314 9 18.4 9H5.6Z",
          fill: "currentColor"
        }
      ),
      /* @__PURE__ */ jsx64(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M3 1.99999C1.89543 1.99999 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V3.99999C23 2.89543 22.1046 1.99999 21 1.99999H3ZM2 3.99999C2 3.44771 2.44772 2.99999 3 2.99999C3.55228 2.99999 4 3.44771 4 3.99999C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 3.99999ZM6 2.99999C5.44772 2.99999 5 3.44771 5 3.99999C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 3.99999C7 3.44771 6.55228 2.99999 6 2.99999ZM9 2.99999C8.44772 2.99999 8 3.44771 8 3.99999C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 3.99999C10 3.44771 9.55228 2.99999 9 2.99999ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8Z",
          fill: "currentColor"
        }
      )
    ]
  }
);
var ModalRightCenterIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V10.4H13.6C13.2686 10.4 13 10.6686 13 11V15.8C13 16.1314 13.2686 16.4 13.6 16.4H21.5V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalLeftCenterIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M3 2C1.89543 2 1 2.89543 1 4V20C1 21.1046 1.89543 22 3 22H21C22.1046 22 23 21.1046 23 20V4C23 2.89543 22.1046 2 21 2H3ZM2 4C2 3.44772 2.44772 3 3 3C3.55228 3 4 3.44772 4 4C4 4.55228 3.55228 5 3 5C2.44772 5 2 4.55228 2 4ZM6 3C5.44772 3 5 3.44772 5 4C5 4.55228 5.44772 5 6 5C6.55228 5 7 4.55228 7 4C7 3.44772 6.55228 3 6 3ZM9 3C8.44772 3 8 3.44772 8 4C8 4.55228 8.44772 5 9 5C9.55228 5 10 4.55228 10 4C10 3.44772 9.55228 3 9 3ZM2.5 6.8C2.5 6.35817 2.85817 6 3.3 6H20.7C21.1418 6 21.5 6.35817 21.5 6.8V19.7C21.5 20.1418 21.1418 20.5 20.7 20.5H3.3C2.85817 20.5 2.5 20.1418 2.5 19.7V16H10.4C10.7314 16 11 15.7314 11 15.4V10.6C11 10.2686 10.7314 10 10.4 10H2.5V6.8Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalIcon = ModalRightBottomIcon;
var TooltipIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M3.62422 4.08582C3.07194 4.08582 2.62422 4.53353 2.62422 5.08581V16.2535C2.62422 16.8058 3.07194 17.2535 3.62422 17.2535H9.2081L11.7929 19.8383C12.1834 20.2288 12.8166 20.2288 13.2071 19.8383L15.7919 17.2535H21.3758C21.9281 17.2535 22.3758 16.8058 22.3758 16.2535V5.08582C22.3758 4.53353 21.9281 4.08582 21.3758 4.08582H3.62422Z",
        fill: "currentColor"
      }
    )
  }
);
var ModalPositionIcons = {
  "right-bottom": ModalRightBottomIcon,
  "right-center": ModalRightCenterIcon,
  "left-center": ModalLeftCenterIcon,
  "left-bottom": ModalLeftBottomIcon,
  "right-top": ModalRightTopIcon,
  "left-top": ModalLeftTopIcon,
  "center-top": ModalCenterTopIcon,
  "center-bottom": ModalCenterBottomIcon,
  "center-center": ModalCenterCenterIcon,
  tooltip: TooltipIcon,
  default: ModalRightBottomIcon
};
var DelayIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 24",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64("g", { transform: "translate(2, 0)", children: /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M10.875 15C10.875 15.6234 10.3734 16.125 9.75 16.125C9.12656 16.125 8.625 15.6234 8.625 15V10.125C8.625 9.50156 9.12656 9 9.75 9C10.3734 9 10.875 9.50156 10.875 10.125V15ZM6 1.125C6 0.503906 6.50156 0 7.125 0H12.375C12.9984 0 13.5 0.503906 13.5 1.125C13.5 1.74609 12.9984 2.25 12.375 2.25H10.875V4.56422C12.9094 4.8 14.7562 5.65781 16.2094 6.94687L17.5781 5.57812C18.0187 5.14219 18.7313 5.14219 19.1297 5.57812C19.6078 6.01875 19.6078 6.73125 19.1297 7.12969L17.7141 8.625C18.8391 10.2141 19.5 12.1547 19.5 14.25C19.5 19.6359 15.1359 24 9.75 24C4.36406 24 0 19.6359 0 14.25C0 9.24375 3.77016 5.12344 8.625 4.56422V2.25H7.125C6.50156 2.25 6 1.74609 6 1.125ZM9.75 21.75C13.8938 21.75 17.25 18.3937 17.25 14.25C17.25 10.1062 13.8938 6.75 9.75 6.75C5.60625 6.75 2.25 10.1062 2.25 14.25C2.25 18.3937 5.60625 21.75 9.75 21.75Z",
        fill: "currentColor"
      }
    ) })
  }
);
var NavigateIcon = () => /* @__PURE__ */ jsx64(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "24",
    height: "24",
    viewBox: "0 0 24 21",
    fill: "none",
    "aria-hidden": "true",
    children: /* @__PURE__ */ jsx64(
      "path",
      {
        d: "M23.6098 9.10223L15.3602 16.1847C14.6393 16.8081 13.4998 16.304 13.4998 15.3335V11.2537C6.2294 11.3568 3.09642 13.1614 5.22163 19.958C5.45745 20.7122 4.5462 21.2967 3.90638 20.8308C1.8557 19.3393 0 16.4894 0 13.6067C0 6.47267 5.9716 4.96336 13.4994 4.8743V1.12541C13.4994 0.156553 14.6374 -0.349204 15.3598 0.274204L23.6094 7.35669C24.1301 7.84604 24.1301 8.65225 23.6098 9.10223Z",
        fill: "currentColor"
      }
    )
  }
);
var StepItemIcon = ModalIcon;

// src/components/StepBar/StepBarItem.tsx
import { Fragment as Fragment11, jsx as jsx65, jsxs as jsxs39 } from "react/jsx-runtime";
var StepBarItem = ({
  id,
  index,
  className,
  active,
  type = "modal",
  delay,
  modalPosition = "center-center",
  onClick,
  ...props
}) => {
  const stepIndex = (index ?? 0) + 1;
  const sortable = useSortable({ id });
  const { setNodeRef, transform, transition, isDragging } = sortable;
  const style = {
    transform: CSS2.Transform.toString(transform),
    transition
  };
  const onPointerDown = sortable.listeners?.onPointerDown;
  const handleClick = (e) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(id);
  };
  const ModalIcon2 = ModalPositionIcons[modalPosition] || ModalPositionIcons.default;
  const renderContent = () => {
    switch (type) {
      case "delay":
        return /* @__PURE__ */ jsxs39(Fragment11, { children: [
          /* @__PURE__ */ jsx65(DelayIcon, {}),
          /* @__PURE__ */ jsx65(
            "span",
            {
              className: cn("text-neutral-3", active && "!text-over-theme"),
              "aria-hidden": "true",
              children: delay ?? 90
            }
          )
        ] });
      case "navigate":
        return /* @__PURE__ */ jsx65(NavigateIcon, {});
      case "modal":
      default:
        return /* @__PURE__ */ jsxs39(Fragment11, { children: [
          /* @__PURE__ */ jsx65("span", { className: "w-5", "aria-hidden": "true", children: stepIndex }),
          /* @__PURE__ */ jsx65(ModalIcon2, {})
        ] });
    }
  };
  return /* @__PURE__ */ jsx65(
    "button",
    {
      type: "button",
      ref: setNodeRef,
      style,
      onPointerDown,
      className: cn(
        // Layout
        "flex items-center justify-center",
        // Sizing
        "w-18 h-12",
        // Spacing
        "gap-1",
        // Appearance
        "bg-neutral-0 rounded-sm",
        // Borders
        "border-1 border-neutral-1",
        // Typography
        "text-neutral-1 text-sm font-semibold leading-4",
        // Interactions
        "cursor-pointer",
        // Hover state
        "hover:bg-highlight-3 hover:border-highlight-3",
        // Focus state
        "focus-visible:bg-highlight-3 focus-visible:border-highlight-3",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)] focus-visible:ring-offset-2",
        // Active/Drag state
        "active:bg-highlight-2 active:border-highlight-3",
        // Selected state
        active && "!bg-level-inverse !border-level-inverse !text-over-theme",
        className
      ),
      "aria-label": `Click to select step ${type === "delay" ? `delay ${delay}s` : type === "navigate" ? "navigate" : stepIndex} ${active ? "- step active" : ""}`,
      ...props,
      onClick: handleClick,
      children: renderContent()
    }
  );
};
StepBarItem.displayName = "StepBarItem";

// src/components/Switch/Switch.tsx
import * as React44 from "react";
import * as SwitchPrimitives from "@radix-ui/react-switch";
import { jsx as jsx66 } from "react/jsx-runtime";
var Switch = React44.forwardRef(({ className, "aria-label": ariaLabel = "Toggle switch", ...props }, ref) => /* @__PURE__ */ jsx66(
  SwitchPrimitives.Root,
  {
    className: cn(
      // Layout
      "peer inline-flex items-center",
      // Sizing
      "max-w-9 h-5 max-h-5 w-9 shrink-0",
      // Appearance
      "rounded-full shadow-sm",
      // Borders
      "border-2 border-transparent",
      // Checked state
      "data-[state=checked]:bg-highlight-1",
      "studio:data-[state=checked]:bg-brand-1",
      "data-[state=unchecked]:bg-neutral-3",
      "studio:data-[state=unchecked]:bg-brand-soft",
      // Interactions
      "cursor-pointer transition-colors",
      // Focus state — `data-[focus=true]:` mirrors `:focus-visible` so
      // stories can force the visual without a play function. Studio
      // overrides the ring-offset color so the 2px gap reads as white
      // against the off-white canvas (per Figma spec).
      "ring-offset-transparent",
      "studio:ring-offset-[var(--color-surface-bright)]",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--border-focus-ring)] focus-visible:ring-offset-2",
      "data-[focus=true]:ring-1 data-[focus=true]:ring-[var(--border-focus-ring)] data-[focus=true]:ring-offset-2",
      // Disabled state
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    "aria-label": ariaLabel,
    ...props,
    ref,
    children: /* @__PURE__ */ jsx66(
      SwitchPrimitives.Thumb,
      {
        className: cn(
          // Layout
          "block",
          // Sizing
          "h-4 w-4",
          // Appearance
          "rounded-full shadow-lg",
          "bg-[var(--fg-over-primary)]",
          // Ring
          "ring-0",
          // Interactions
          "pointer-events-none transition-transform",
          // State translations
          "data-[state=checked]:translate-x-4",
          "data-[state=unchecked]:translate-x-0"
        )
      }
    )
  }
));
Switch.displayName = SwitchPrimitives.Root.displayName;

// src/components/BlockTile/BlockTile.tsx
import { jsx as jsx67, jsxs as jsxs40 } from "react/jsx-runtime";
var BlockTile = ({
  id,
  icon,
  onClick,
  title,
  "data-state": dataState
}) => {
  return /* @__PURE__ */ jsxs40(
    "button",
    {
      id,
      type: "button",
      onClick,
      "data-state": dataState,
      className: cn(
        // Layout
        "flex items-center",
        // Sizing
        "w-[150px]",
        // Spacing
        "gap-2 p-3",
        // Appearance
        "bg-neutral-0 rounded-sm",
        // Borders
        "border-1 border-transparent",
        // Typography
        "text-neutral-1 font-semibold",
        // Interactions
        "cursor-pointer",
        // Hover state
        "hover:border-neutral-1 data-[state=hover]:border-neutral-1"
      ),
      children: [
        /* @__PURE__ */ jsx67(Icon, { size: "large", icon }),
        /* @__PURE__ */ jsx67("span", { children: title })
      ]
    }
  );
};
var BlockTile_default = BlockTile;

// src/components/Tabs/Tabs.tsx
import {
  Root as RootPrimitive,
  List as ListPrimitive,
  Trigger as TriggerPrimitive,
  Content as ContentPrimitive
} from "@radix-ui/react-tabs";
import { jsx as jsx68 } from "react/jsx-runtime";
var TabsList = ({ children, ...props }) => /* @__PURE__ */ jsx68(
  ListPrimitive,
  {
    className: cn(
      // Layout
      "flex",
      // Spacing
      "gap-2 pb-2"
    ),
    ...props,
    children
  }
);
var TabsTrigger = ({ children, ...props }) => /* @__PURE__ */ jsx68(
  TriggerPrimitive,
  {
    className: cn(
      "flex items-center justify-center",
      "gap-2 p-2 studio:px-3 studio:py-1.5",
      "rounded-sm bg-transparent studio:rounded-lg",
      "text-neutral-1 text-sm font-bold",
      // Studio: GT Standard L only ships up to Semibold; drop bold (700)
      // and shift text to content-secondary so muted tabs match Figma.
      "studio:font-semibold studio:text-content-secondary",
      "cursor-pointer",
      "data-[state=active]:bg-highlight-3 data-[state=active]:text-highlight-1",
      // Studio active state matches Figma: surface-secondary mortar bg,
      // content-primary text, subtle drop shadow.
      "studio:data-[state=active]:bg-surface-secondary studio:data-[state=active]:text-content-primary studio:data-[state=active]:shadow-xs",
      "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50"
    ),
    ...props,
    children
  }
);
var TabsContent = ({ children, ...props }) => /* @__PURE__ */ jsx68(ContentPrimitive, { ...props, children });
var TabsRoot = ({ children, ...props }) => /* @__PURE__ */ jsx68(
  RootPrimitive,
  {
    className: cn(
      // Appearance
      "bg-highlight-0"
    ),
    ...props,
    children
  }
);

// src/components/Textarea/Textarea.tsx
import { forwardRef as forwardRef24 } from "react";
import { jsx as jsx69 } from "react/jsx-runtime";
var Textarea = forwardRef24(
  ({ className, disabled, size = DEFAULT_COMPONENT_SIZE, ...props }, ref) => {
    const sizeClasses = {
      small: "px-4 py-2 min-h-20",
      medium: "px-4 py-3 min-h-24"
    };
    return /* @__PURE__ */ jsx69(
      "textarea",
      {
        className: cn(
          // Layout
          "flex w-full",
          // Appearance
          "bg-neutral-1 rounded-md",
          // Borders
          "border-1 border-neutral-1",
          // Typography
          "font-lato text-neutral-1 text-sm font-semibold leading-5",
          // Focus state
          "ring-offset-transparent focus:outline-none focus:ring-2 focus:ring-[var(--border-focus-ring)]",
          // Disabled state
          "disabled:text-neutral-4 disabled:cursor-not-allowed disabled:border-transparent",
          // Read-only state
          "read-only:text-neutral-3 read-only:border-transparent",
          // Invalid state
          'aria-[invalid="true"]:border-destructive-1',
          // Placeholder
          "placeholder:text-neutral-3",
          // Resize
          "resize-none",
          sizeClasses[size],
          className
        ),
        ref,
        disabled,
        ...props
      }
    );
  }
);
Textarea.displayName = "Textarea";

// src/components/Text/Text.tsx
import * as React46 from "react";
import { jsx as jsx70 } from "react/jsx-runtime";
var VARIANT_CLASS = {
  display: "type-display",
  "title-lg": "type-title-lg",
  title: "type-title",
  "title-sm": "type-title-sm",
  "subtitle-lg": "type-subtitle-lg",
  subtitle: "type-subtitle",
  "subtitle-sm": "type-subtitle-sm",
  "body-lg": "type-body-lg",
  "body-lg-bold": "type-body-lg-bold",
  body: "type-body",
  "body-bold": "type-body-bold",
  "body-sm": "type-body-sm",
  "body-sm-bold": "type-body-sm-bold",
  field: "type-field",
  label: "type-label",
  "label-bold": "type-label-bold",
  caption: "type-caption",
  "caption-bold": "type-caption-bold",
  button: "type-button",
  "button-sm": "type-button-sm",
  "thumbnail-display": "type-thumbnail-display",
  eyebrow: "type-eyebrow",
  code: "type-code"
};
var SIZE_CLASS = {
  xs: "text-xs",
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl"
};
var WEIGHT_CLASS = {
  normal: "font-normal",
  medium: "font-medium",
  semibold: "font-semibold",
  bold: "font-bold"
};
var LEGACY_COLOR_CLASS = {
  primary: "text-[var(--pairing-fg,var(--fg-neutral-1))]",
  secondary: "text-neutral-3",
  muted: "text-neutral-4",
  inherit: "text-inherit"
};
var BRAND_COLOR_CLASS = {
  primary: "text-[var(--pairing-fg,var(--color-content-primary))]",
  secondary: "text-content-secondary",
  tertiary: "text-content-tertiary",
  "inverse-primary": "text-content-inverse-primary",
  "inverse-secondary": "text-content-inverse-secondary",
  "inverse-tertiary": "text-content-inverse-tertiary",
  "brand-primary": "text-content-brand-primary",
  "brand-secondary": "text-content-brand-secondary",
  "brand-tertiary": "text-content-brand-tertiary",
  "positive-primary": "text-content-positive-primary",
  "positive-secondary": "text-content-positive-secondary",
  "positive-tertiary": "text-content-positive-tertiary",
  "warning-primary": "text-content-warning-primary",
  "warning-secondary": "text-content-warning-secondary",
  "warning-tertiary": "text-content-warning-tertiary",
  "danger-primary": "text-content-danger-primary",
  "danger-secondary": "text-content-danger-secondary",
  "danger-tertiary": "text-content-danger-tertiary",
  inherit: "text-inherit"
};
var ALIGN_CLASS = {
  left: "text-left",
  center: "text-center",
  right: "text-right"
};
var Text = React46.forwardRef((props, ref) => {
  const {
    className,
    truncate,
    align,
    as: Tag = "span",
    asChild,
    children,
    ...rest
  } = props;
  let composed;
  if ("variant" in props && props.variant) {
    const { variant, color = "primary" } = props;
    composed = cn(
      VARIANT_CLASS[variant],
      BRAND_COLOR_CLASS[color],
      truncate && "truncate",
      align && ALIGN_CLASS[align],
      className
    );
  } else {
    const {
      size = "md",
      weight = "normal",
      color = "primary"
    } = props;
    composed = cn(
      "font-lato",
      SIZE_CLASS[size],
      WEIGHT_CLASS[weight],
      LEGACY_COLOR_CLASS[color],
      truncate && "truncate",
      align && ALIGN_CLASS[align],
      className
    );
  }
  const DISCRIMINANT_KEYS = /* @__PURE__ */ new Set(["variant", "size", "weight", "color"]);
  const domProps = {};
  for (const [key, value] of Object.entries(rest)) {
    if (!DISCRIMINANT_KEYS.has(key)) {
      domProps[key] = value;
    }
  }
  return /* @__PURE__ */ jsx70(
    Box,
    {
      ref,
      ...asChild ? { asChild: true } : { as: Tag },
      className: composed,
      ...domProps,
      children
    }
  );
});
Text.displayName = "Text";

// src/components/Toast/Toast.tsx
import { FontAwesomeIcon as FontAwesomeIcon21 } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons/faCircleCheck";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons/faCircleInfo";
import { faCircleXmark } from "@fortawesome/pro-regular-svg-icons/faCircleXmark";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons/faTriangleExclamation";
import { Toaster as SonnerToaster, toast as sonnerToast } from "sonner";
import { jsx as jsx71 } from "react/jsx-runtime";
var THEME_VARIABLES = {
  "--normal-bg": "var(--bg-neutral-0)",
  "--normal-text": "var(--fg-neutral-1)",
  "--normal-border": "var(--border-neutral-1)",
  "--success-bg": "var(--color-positive-subtle, var(--sb-color-emerald-50))",
  "--success-text": "var(--color-content-positive-primary, var(--sb-color-emerald-800))",
  "--success-border": "var(--color-positive-soft, var(--sb-color-emerald-200))",
  "--info-bg": "var(--bg-info-1)",
  "--info-text": "var(--fg-info-1)",
  "--info-border": "var(--border-info-1)",
  "--warning-bg": "var(--bg-alert-1)",
  "--warning-text": "var(--fg-alert-2)",
  "--warning-border": "var(--border-alert-1)",
  "--error-bg": "var(--bg-destructive-3)",
  "--error-text": "var(--fg-destructive-3)",
  "--error-border": "var(--border-destructive-3)"
};
function Toaster({
  position = "bottom-right",
  duration = 4e3,
  expand = false,
  visibleToasts = 3,
  closeButton = false,
  gap,
  offset,
  className,
  style
}) {
  return /* @__PURE__ */ jsx71(
    SonnerToaster,
    {
      position,
      duration,
      expand,
      visibleToasts,
      closeButton,
      gap,
      offset,
      className: cn(className),
      style: { ...THEME_VARIABLES, ...style },
      richColors: true,
      icons: {
        success: /* @__PURE__ */ jsx71(FontAwesomeIcon21, { icon: faCircleCheck, className: "size-4" }),
        info: /* @__PURE__ */ jsx71(FontAwesomeIcon21, { icon: faCircleInfo, className: "size-4" }),
        warning: /* @__PURE__ */ jsx71(FontAwesomeIcon21, { icon: faTriangleExclamation, className: "size-4" }),
        error: /* @__PURE__ */ jsx71(FontAwesomeIcon21, { icon: faCircleXmark, className: "size-4" })
      },
      toastOptions: {
        className: cn("rounded-md border text-sm"),
        classNames: {
          toast: cn("[&_[data-button]~[data-button]]:!ml-0"),
          actionButton: cn(
            "!inline-flex !items-center !justify-center",
            "!rounded-md studio:!rounded-xl",
            "!h-auto !px-2.5 !py-1",
            "!text-xs !font-semibold !cursor-pointer",
            "!bg-neutral-1 !text-neutral-1 !border-1 !border-neutral-1",
            "studio:!bg-brand-1 studio:!text-content-inverse-primary studio:!border-transparent"
          ),
          cancelButton: cn(
            "!inline-flex !items-center !justify-center",
            "!rounded-md studio:!rounded-xl",
            "!h-auto !px-2.5 !py-1",
            "!text-xs !font-semibold !cursor-pointer",
            "!bg-transparent !text-neutral-1 !border-1 !border-neutral-1",
            "studio:!text-content-secondary studio:!border-edge"
          )
        }
      }
    }
  );
}
Toaster.displayName = "Toaster";
function mapOptions(options) {
  if (!options) return void 0;
  const { onDismiss, onAutoClose, ...rest } = options;
  return {
    ...rest,
    ...onDismiss ? { onDismiss } : {},
    ...onAutoClose ? { onAutoClose } : {}
  };
}
function createToast(message, options) {
  return sonnerToast(message, mapOptions(options));
}
var toast = Object.assign(createToast, {
  success(message, options) {
    return sonnerToast.success(message, mapOptions(options));
  },
  error(message, options) {
    return sonnerToast.error(message, mapOptions(options));
  },
  warning(message, options) {
    return sonnerToast.warning(message, mapOptions(options));
  },
  info(message, options) {
    return sonnerToast.info(message, mapOptions(options));
  },
  loading(message, options) {
    return sonnerToast.loading(message, mapOptions(options));
  },
  message(message, options) {
    return sonnerToast.message(message, mapOptions(options));
  },
  promise(promise, data) {
    return sonnerToast.promise(promise, data);
  },
  custom(jsx82, options) {
    return sonnerToast.custom(jsx82, mapOptions(options));
  },
  dismiss(id) {
    return sonnerToast.dismiss(id);
  }
});

// src/components/Toggle/Toggle.tsx
import { Toggle as TogglePrimitive } from "@radix-ui/react-toggle";
import {
  ToggleGroup as ToggleGroupPrimitive,
  ToggleGroupItem as ToggleGroupItemPrimitive
} from "@radix-ui/react-toggle-group";
import { jsx as jsx72 } from "react/jsx-runtime";
var Toggle = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx72(
  TogglePrimitive,
  {
    className: cn(
      // Layout
      "flex flex-1 items-center justify-center",
      // Sizing
      "h-8",
      // Spacing
      "p-1",
      // Appearance
      "bg-neutral-1 rounded-md",
      // Borders
      "border-1 border-neutral-1",
      // Interactions
      "cursor-pointer",
      // Focus state
      "outline-none ring-offset-transparent focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)] data-[state=focus]:ring-2 data-[state=focus]:ring-[var(--border-focus-ring)]",
      // On state
      "data-[state=on]:bg-highlight-1 data-[state=on]:text-highlight-2",
      "studio:data-[state=on]:bg-surface-bright-tertiary studio:data-[state=on]:text-neutral-1",
      // Disabled state
      "disabled:bg-neutral-0 disabled:text-neutral-4 disabled:cursor-not-allowed",
      className
    ),
    ...props,
    children
  }
);
var ToggleGroup = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx72(
  ToggleGroupPrimitive,
  {
    className: cn(
      // Layout
      "flex w-full items-center justify-between",
      // Spacing
      "gap-2 p-[1px]",
      // Appearance
      "bg-neutral-1 rounded-md",
      // Borders
      "border-1 border-neutral-1",
      // Typography
      "font-lato text-neutral-3 text-sm font-bold",
      // Disabled state
      "disabled:bg-neutral-0 disabled:text-neutral-4 disabled:cursor-not-allowed",
      className
    ),
    ...props,
    children
  }
);
var ToggleGroupItem = ({
  children,
  className,
  ...props
}) => /* @__PURE__ */ jsx72(
  ToggleGroupItemPrimitive,
  {
    className: cn(
      // Layout
      "flex flex-1 items-center justify-center",
      // Sizing
      "h-7",
      // Appearance
      "rounded-sm",
      // Interactions
      "cursor-pointer",
      // Focus state
      "outline-none ring-offset-transparent focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)] data-[state=focus]:ring-2 data-[state=focus]:ring-[var(--border-focus-ring)]",
      // On state
      "data-[state=on]:bg-highlight-1 data-[state=on]:text-highlight-2",
      "studio:data-[state=on]:bg-surface-bright-tertiary studio:data-[state=on]:text-neutral-1",
      // Disabled state — active bg lightens to highlight-3, so text needs
      // to darken to stay legible. Studio active text is already neutral-1
      // (see normal active row above); spell it out for the disabled case
      // too so source-order specificity can't drop it back to text-highlight-2.
      "data-[state=on]:disabled:bg-highlight-3 data-[state=on]:disabled:text-highlight-1 studio:data-[state=on]:disabled:text-neutral-1 disabled:cursor-not-allowed",
      className
    ),
    ...props,
    children
  }
);

// src/components/GridSelector/GridSelector.tsx
import { jsx as jsx73 } from "react/jsx-runtime";
var generateArrayOfIndexes = (amount) => Array.from({ length: amount }, (_, index) => index);
var GridSelector = ({
  amount,
  initialValue,
  onChange,
  ariaLabels
}) => {
  const selectors = generateArrayOfIndexes(amount);
  const onChangePosition = (positionIndex) => onChange(positionIndex);
  return /* @__PURE__ */ jsx73(
    "form",
    {
      className: cn(
        "flex flex-row flex-wrap",
        "gap-2 p-2",
        "w-full",
        "border-neutral-2 rounded-md border",
        "bg-neutral"
      ),
      children: selectors.map((position) => /* @__PURE__ */ jsx73(
        "input",
        {
          className: cn(
            "block flex-1 basis-[calc(100%/3-8px)] self-stretch",
            "h-4 cursor-pointer appearance-none",
            "border-neutral-3 rounded-sm border",
            "bg-neutral-1",
            "transition-all duration-200 ease-in-out",
            position === initialValue && "border-highlight-2 bg-highlight-2 studio:border-[var(--bg-brand-1)] studio:bg-brand-1"
          ),
          type: "radio",
          name: "position-selector",
          value: position,
          "aria-label": ariaLabels?.[position] ?? `position ${position + 1}`,
          checked: position === initialValue,
          onChange: () => onChangePosition(position)
        },
        `position-selector-${position}`
      ))
    }
  );
};

// src/components/MarginPaddingEditor/MarginPaddingEditor.tsx
import { forwardRef as forwardRef28 } from "react";

// src/components/MarginPaddingEditor/SpacingSection.tsx
import { forwardRef as forwardRef27, useState as useState26, useCallback as useCallback14 } from "react";
import { faLock } from "@fortawesome/pro-regular-svg-icons/faLock";
import { faLockOpen } from "@fortawesome/pro-regular-svg-icons/faLockOpen";

// src/components/MarginPaddingEditor/SpacingInput.tsx
import {
  forwardRef as forwardRef26,
  useCallback as useCallback13,
  useEffect as useEffect18,
  useRef as useRef10,
  useState as useState25
} from "react";
import { jsx as jsx74 } from "react/jsx-runtime";
var MAX_SPACING = 9999;
function clamp2(n, min, max = MAX_SPACING) {
  let result = n;
  if (min != null) result = Math.max(min, result);
  result = Math.min(max, result);
  return result;
}
var SpacingInput = forwardRef26(
  ({ value, onChange, disabled, min, max, className, ...props }, ref) => {
    const [localValue, setLocalValue] = useState25(String(value));
    const focused = useRef10(false);
    const hasUserEdited = useRef10(false);
    useEffect18(() => {
      if (!(focused.current && hasUserEdited.current)) {
        setLocalValue(String(value));
      }
    }, [value]);
    const handleFocus = useCallback13((e) => {
      focused.current = true;
      e.target.select();
    }, []);
    const handleBlur = useCallback13(
      (e) => {
        focused.current = false;
        hasUserEdited.current = false;
        const n = Number(e.target.value);
        const committed = clamp2(Number.isNaN(n) ? 0 : n, min, max);
        setLocalValue(String(committed));
        if (committed !== value) onChange(committed);
      },
      [onChange, min, max, value]
    );
    const isNegativeDisallowed = min != null && min >= 0;
    const handleChange = useCallback13(
      (e) => {
        const raw = e.target.value;
        if (isNegativeDisallowed && raw.includes("-")) return;
        hasUserEdited.current = true;
        setLocalValue(raw);
        const isPartialInput = raw === "" || raw === "-";
        if (isPartialInput) return;
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) {
          onChange(clamp2(parsed, min, max));
        }
      },
      [isNegativeDisallowed, onChange, min, max]
    );
    if (disabled) {
      return /* @__PURE__ */ jsx74(
        "div",
        {
          className: cn(
            "flex items-center justify-center",
            "w-10 h-5",
            "text-xs font-medium text-neutral-4 select-none",
            className
          ),
          children: "-"
        }
      );
    }
    return /* @__PURE__ */ jsx74(
      "input",
      {
        ref,
        type: "text",
        inputMode: "numeric",
        value: localValue,
        onChange: handleChange,
        onFocus: handleFocus,
        onBlur: handleBlur,
        className: cn(
          "w-10 h-5",
          "bg-transparent rounded text-center",
          "text-xs font-medium text-neutral-1",
          "outline-none border border-transparent",
          "hover:bg-neutral-0 focus:border-highlight-2",
          className
        ),
        ...props
      }
    );
  }
);
SpacingInput.displayName = "SpacingInput";

// src/components/MarginPaddingEditor/SpacingSection.tsx
import { jsx as jsx75, jsxs as jsxs41 } from "react/jsx-runtime";
function allEqual(values) {
  return values.top === values.right && values.right === values.bottom && values.bottom === values.left;
}
var SpacingSection = forwardRef27(
  ({
    label,
    values,
    onChange,
    disabled,
    children,
    variant,
    size = "default",
    className
  }, ref) => {
    const isCompact = size === "compact";
    const isFluid = size === "fluid";
    const [locked, setLocked] = useState26(() => allEqual(values));
    const handleLockChange = useCallback14(
      (newLocked) => {
        setLocked(newLocked);
        if (newLocked) {
          const synced = {
            top: values.top,
            right: values.top,
            bottom: values.top,
            left: values.top
          };
          onChange(synced);
        }
      },
      [onChange, values.top]
    );
    const handleInputChange = useCallback14(
      (side, value) => {
        if (locked) {
          onChange({ top: value, right: value, bottom: value, left: value });
        } else {
          onChange({ ...values, [side]: value });
        }
      },
      [locked, onChange, values]
    );
    const isOuter = variant === "outer";
    const isMargin = label.toUpperCase() === "MARGIN";
    const min = isMargin ? void 0 : 0;
    return /* @__PURE__ */ jsxs41(
      "div",
      {
        ref,
        className: cn(
          "relative rounded-[6px]",
          isOuter ? cn(
            "bg-neutral-1 border border-neutral-2",
            // eslint-disable-next-line no-nested-ternary
            isFluid ? "h-[112px] w-full" : isCompact ? "h-[112px] w-[192px]" : "h-[168px] w-[288px]"
          ) : "bg-neutral-0 border border-neutral-2 rounded-[4px]",
          className
        ),
        children: [
          /* @__PURE__ */ jsxs41(
            "div",
            {
              className: cn(
                "absolute top-[3px] left-[3px] flex items-center gap-0.5"
              ),
              children: [
                /* @__PURE__ */ jsx75(
                  "span",
                  {
                    className: cn(
                      "text-[11px] font-semibold uppercase leading-3",
                      disabled ? "text-neutral-4" : "text-neutral-3"
                    ),
                    children: label
                  }
                ),
                !disabled ? /* @__PURE__ */ jsx75(
                  Toggle,
                  {
                    pressed: locked,
                    onPressedChange: handleLockChange,
                    "aria-label": locked ? `Unlock ${label.toLowerCase()} values` : `Lock ${label.toLowerCase()} values`,
                    className: cn(
                      "size-[14px] p-0 border-none bg-transparent rounded-none",
                      "data-[state=on]:bg-transparent",
                      "disabled:bg-transparent"
                    ),
                    children: /* @__PURE__ */ jsx75(
                      Icon,
                      {
                        icon: locked ? faLock : faLockOpen,
                        className: cn(
                          "text-[10px]",
                          locked ? "text-neutral-3" : "text-neutral-4"
                        )
                      }
                    )
                  }
                ) : null
              ]
            }
          ),
          /* @__PURE__ */ jsx75("div", { className: cn("absolute top-[9px] left-1/2 -translate-x-1/2"), children: /* @__PURE__ */ jsx75(
            SpacingInput,
            {
              value: values.top,
              onChange: (v) => handleInputChange("top", v),
              disabled,
              min,
              "aria-label": `${label.toLowerCase()} top`
            }
          ) }),
          /* @__PURE__ */ jsx75("div", { className: cn("absolute bottom-[9px] left-1/2 -translate-x-1/2"), children: /* @__PURE__ */ jsx75(
            SpacingInput,
            {
              value: values.bottom,
              onChange: (v) => handleInputChange("bottom", v),
              disabled,
              min,
              "aria-label": `${label.toLowerCase()} bottom`
            }
          ) }),
          /* @__PURE__ */ jsx75(
            "div",
            {
              className: cn(
                "absolute top-1/2 -translate-y-1/2",
                isOuter ? "left-[5px]" : "left-[25px]"
              ),
              children: /* @__PURE__ */ jsx75(
                SpacingInput,
                {
                  value: values.left,
                  onChange: (v) => handleInputChange("left", v),
                  disabled,
                  min,
                  "aria-label": `${label.toLowerCase()} left`
                }
              )
            }
          ),
          /* @__PURE__ */ jsx75(
            "div",
            {
              className: cn(
                "absolute top-1/2 -translate-y-1/2",
                isOuter ? "right-[5px]" : "right-[26px]"
              ),
              children: /* @__PURE__ */ jsx75(
                SpacingInput,
                {
                  value: values.right,
                  onChange: (v) => handleInputChange("right", v),
                  disabled,
                  min,
                  "aria-label": `${label.toLowerCase()} right`
                }
              )
            }
          ),
          children
        ]
      }
    );
  }
);
SpacingSection.displayName = "SpacingSection";

// src/components/MarginPaddingEditor/MarginPaddingEditor.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var ZERO_VALUES = { top: 0, right: 0, bottom: 0, left: 0 };
var noop = () => {
};
var ContentPill = () => /* @__PURE__ */ jsx76(
  "div",
  {
    className: cn(
      "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
      "bg-neutral-2 rounded-[2px]",
      "h-4 w-8",
      "flex items-center justify-center",
      "text-[10px] text-neutral-3 select-none pointer-events-none"
    )
  }
);
var MarginPaddingEditor = forwardRef28(
  ({
    margin,
    padding,
    onMarginChange = noop,
    onPaddingChange = noop,
    size = "default",
    className
  }, ref) => {
    const hasMargin = margin !== void 0;
    const hasPadding = padding !== void 0;
    if (!hasMargin && !hasPadding) {
      return null;
    }
    if (hasMargin && hasPadding) {
      return /* @__PURE__ */ jsx76(
        "div",
        {
          ref,
          className: cn(
            size === "fluid" ? "block w-full" : "inline-block",
            className
          ),
          children: /* @__PURE__ */ jsx76(
            SpacingSection,
            {
              label: "MARGIN",
              values: margin,
              onChange: onMarginChange,
              variant: "outer",
              size,
              children: /* @__PURE__ */ jsx76("div", { className: cn("absolute", "inset-[24%_17%]"), children: /* @__PURE__ */ jsx76(
                SpacingSection,
                {
                  label: "PADDING",
                  values: padding,
                  onChange: onPaddingChange,
                  variant: "inner",
                  size,
                  className: "h-full w-full",
                  children: /* @__PURE__ */ jsx76(ContentPill, {})
                }
              ) })
            }
          )
        }
      );
    }
    if (hasMargin && !hasPadding) {
      return /* @__PURE__ */ jsx76(
        "div",
        {
          ref,
          className: cn(
            size === "fluid" ? "block w-full" : "inline-block",
            className
          ),
          children: /* @__PURE__ */ jsx76(
            SpacingSection,
            {
              label: "MARGIN",
              values: margin,
              onChange: onMarginChange,
              variant: "outer",
              size,
              children: /* @__PURE__ */ jsx76("div", { className: cn("absolute", "inset-[24%_17%]"), children: /* @__PURE__ */ jsx76(
                SpacingSection,
                {
                  label: "PADDING",
                  values: ZERO_VALUES,
                  onChange: noop,
                  disabled: true,
                  variant: "inner",
                  size,
                  className: "h-full w-full",
                  children: /* @__PURE__ */ jsx76(ContentPill, {})
                }
              ) })
            }
          )
        }
      );
    }
    return /* @__PURE__ */ jsx76(
      "div",
      {
        ref,
        className: cn(
          size === "fluid" ? "block w-full" : "inline-block",
          className
        ),
        children: /* @__PURE__ */ jsx76(
          SpacingSection,
          {
            label: "PADDING",
            values: padding,
            onChange: onPaddingChange,
            variant: "outer",
            size,
            children: /* @__PURE__ */ jsx76(ContentPill, {})
          }
        )
      }
    );
  }
);
MarginPaddingEditor.displayName = "MarginPaddingEditor";

// src/components/AlignmentSelector/AlignmentSelector.tsx
import { useState as useState27, useEffect as useEffect19 } from "react";

// src/components/AlignmentSelector/PositionControls.tsx
import { jsx as jsx77, jsxs as jsxs42 } from "react/jsx-runtime";
var PositionControls = ({
  position,
  onPositionChange
}) => /* @__PURE__ */ jsxs42(
  SegmentedControlGroup,
  {
    value: position,
    onValueChange: onPositionChange,
    size: "small",
    children: [
      /* @__PURE__ */ jsx77(SegmentedControlGroupItem, { value: "auto", children: "Auto" }),
      /* @__PURE__ */ jsx77(SegmentedControlGroupItem, { value: "fixed", children: "Fixed" })
    ]
  }
);

// src/components/AlignmentSelector/AlignmentGrid.tsx
import * as RadioGroupPrimitive3 from "@radix-ui/react-radio-group";

// src/components/AlignmentSelector/AlignmentRadio.tsx
import * as RadioGroupPrimitive2 from "@radix-ui/react-radio-group";
import { jsx as jsx78 } from "react/jsx-runtime";
var AlignmentRadio = ({ value }) => /* @__PURE__ */ jsx78(
  RadioGroupPrimitive2.Item,
  {
    className: cn(
      "h-4 w-4 shrink-0 cursor-pointer rounded-full border-2",
      "border-neutral-4 bg-neutral-0",
      "transition-all duration-150 ease-in-out",
      "data-[state=checked]:border-highlight-2 data-[state=checked]:bg-highlight-2",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--border-focus-ring)]",
      "hover:border-highlight-2"
    ),
    "aria-label": value,
    value
  }
);

// src/components/AlignmentSelector/caret.ts
var CARET_SIZE = 6;
var DISTANCE = -(CARET_SIZE * 2);
var CARET_COLOR = "var(--bg-neutral-2)";
var ELEMENT_POSITION_STYLE = {
  position: "absolute",
  width: 0,
  height: 0
};
var BASE_ARROW_STYLE = {
  borderWidth: `${CARET_SIZE}px`,
  borderStyle: "solid",
  borderTopColor: "transparent",
  borderRightColor: "transparent",
  borderBottomColor: "transparent",
  borderLeftColor: "transparent"
};
var getHorizontalAlignment = (direction) => {
  if (direction === "right") return "7%";
  if (direction === "left") return "93%";
  return "50%";
};
var getVerticalAlignment = (direction) => {
  if (direction === "bottom") return "10%";
  if (direction === "top") return "90%";
  return "50%";
};
var getTransformAxis = (side) => side === "top" || side === "bottom" ? "X" : "Y";
var getPositionArrowStyle = (side, direction) => ({
  transform: `translate${getTransformAxis(side)}(-50%)`,
  ...side === "top" && {
    bottom: DISTANCE,
    left: getHorizontalAlignment(direction),
    borderTopColor: CARET_COLOR
  },
  ...side === "bottom" && {
    top: DISTANCE,
    left: getHorizontalAlignment(direction),
    borderBottomColor: CARET_COLOR
  },
  ...side === "left" && {
    right: DISTANCE,
    top: getVerticalAlignment(direction),
    borderLeftColor: CARET_COLOR
  },
  ...side === "right" && {
    left: DISTANCE,
    top: getVerticalAlignment(direction),
    borderRightColor: CARET_COLOR
  }
});
var getCaretStyle = (alignment) => {
  if (alignment === "auto") return null;
  const [side, direction] = alignment.split("-");
  return {
    ...ELEMENT_POSITION_STYLE,
    ...BASE_ARROW_STYLE,
    ...getPositionArrowStyle(side, direction)
  };
};

// src/components/AlignmentSelector/AlignmentPreview.tsx
import { jsx as jsx79, jsxs as jsxs43 } from "react/jsx-runtime";
var AlignmentPreview = ({ alignment }) => {
  const caretStyle = getCaretStyle(alignment);
  const text = alignment === "auto" ? "Automatically choose the optimal position" : `Always show from ${alignment}`;
  return /* @__PURE__ */ jsxs43(
    "div",
    {
      className: cn(
        "relative flex flex-col gap-2 bg-neutral-1 rounded-sm p-3 w-full h-20",
        "items-center justify-center text-center overflow-visible"
      ),
      children: [
        caretStyle && /* @__PURE__ */ jsx79("div", { style: caretStyle }),
        /* @__PURE__ */ jsx79("span", { className: cn("text-xs text-neutral-3"), children: text })
      ]
    }
  );
};

// src/components/AlignmentSelector/AlignmentGrid.tsx
import { jsx as jsx80, jsxs as jsxs44 } from "react/jsx-runtime";
var AlignmentRow = ({ children, className }) => /* @__PURE__ */ jsx80("div", { className: cn("flex flex-row w-full justify-around", className), children });
var AlignmentColumn = ({ children }) => /* @__PURE__ */ jsx80("div", { className: cn("flex flex-col justify-between"), children });
var AlignmentGrid = ({
  alignment,
  onChangeAlignment
}) => /* @__PURE__ */ jsxs44(
  RadioGroupPrimitive3.Root,
  {
    value: alignment,
    onValueChange: onChangeAlignment,
    className: cn("w-full flex flex-col gap-4"),
    children: [
      /* @__PURE__ */ jsxs44(AlignmentRow, { children: [
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "bottom-right" }),
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "bottom" }),
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "bottom-left" })
      ] }),
      /* @__PURE__ */ jsxs44(AlignmentRow, { className: "gap-4 w-full justify-center", children: [
        /* @__PURE__ */ jsxs44(AlignmentColumn, { children: [
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "right-bottom" }),
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "right" }),
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "right-top" })
        ] }),
        /* @__PURE__ */ jsx80(AlignmentPreview, { alignment }),
        /* @__PURE__ */ jsxs44(AlignmentColumn, { children: [
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "left-bottom" }),
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "left" }),
          /* @__PURE__ */ jsx80(AlignmentRadio, { value: "left-top" })
        ] })
      ] }),
      /* @__PURE__ */ jsxs44(AlignmentRow, { children: [
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "top-right" }),
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "top" }),
        /* @__PURE__ */ jsx80(AlignmentRadio, { value: "top-left" })
      ] })
    ]
  }
);

// src/components/AlignmentSelector/AlignmentSelector.tsx
import { jsx as jsx81, jsxs as jsxs45 } from "react/jsx-runtime";
var AUTO = "auto";
var FIXED = "fixed";
var DEFAULT_POSITION = "bottom-left";
var AlignmentSelector = ({
  initialValue = "auto",
  onChange
}) => {
  const [alignment, setAlignment] = useState27(initialValue);
  useEffect19(() => {
    setAlignment(initialValue);
  }, [initialValue]);
  const position = alignment === AUTO ? AUTO : FIXED;
  const onChangeAlignment = (value) => {
    setAlignment(value);
    onChange(value);
  };
  const onPositionChange = (value) => {
    const nextAlignment = value === FIXED ? DEFAULT_POSITION : AUTO;
    setAlignment(nextAlignment);
    onChange(nextAlignment);
  };
  return /* @__PURE__ */ jsxs45("div", { className: "flex flex-col gap-4 w-full", children: [
    /* @__PURE__ */ jsx81(
      PositionControls,
      {
        position,
        onPositionChange
      }
    ),
    /* @__PURE__ */ jsx81(
      AlignmentGrid,
      {
        alignment,
        onChangeAlignment
      }
    )
  ] });
};

// src/hooks/use-autocomplete.ts
import { useState as useState28, useEffect as useEffect20, useCallback as useCallback15, useRef as useRef11 } from "react";
var useAutocomplete = (adapter, triggers, category) => {
  const [activeTrigger, setActiveTrigger] = useState28(null);
  const [target, setTarget] = useState28(null);
  const [search, setSearch] = useState28("");
  const [activeIndex, setActiveIndex] = useState28(0);
  const [pendingReplacement, setPendingReplacement] = useState28(null);
  const triggersRef = useRef11(triggers);
  triggersRef.current = triggers;
  const adapterRef = useRef11(adapter);
  adapterRef.current = adapter;
  const activeIndexRef = useRef11(activeIndex);
  activeIndexRef.current = activeIndex;
  const targetRef = useRef11(null);
  targetRef.current = target;
  const items = activeTrigger !== null ? triggersRef.current[activeTrigger]?.getItems(search, category) ?? [] : [];
  const itemsRef = useRef11(items);
  itemsRef.current = items;
  useEffect20(() => {
    if (!pendingReplacement) return;
    const { range, text } = pendingReplacement;
    setPendingReplacement(null);
    adapterRef.current.replaceRange(range, text);
  }, [pendingReplacement]);
  const closeMenu = useCallback15(() => {
    setActiveTrigger(null);
    setTarget(null);
    setSearch("");
    setActiveIndex(0);
  }, []);
  const onSelect = useCallback15(
    (value) => {
      if (!targetRef.current) return;
      adapterRef.current.replaceRange(targetRef.current, value);
      closeMenu();
    },
    [closeMenu]
  );
  const onEditorChange = useCallback15(() => {
    const context = adapterRef.current.getCursorContext();
    if (!context) {
      if (targetRef.current) closeMenu();
      return;
    }
    const { textBefore, textAfter } = context;
    const tgs = triggersRef.current;
    for (const tg of tgs) {
      const sc = tg.shortcode;
      if (sc) {
        const scMatch = sc.pattern.exec(textBefore);
        if (scMatch) {
          const resolved = sc.resolve(scMatch[1]);
          if (resolved !== null) {
            const matchStart = textBefore.length - scMatch[0].length;
            setPendingReplacement({
              range: adapterRef.current.createRange(matchStart),
              text: resolved
            });
            if (targetRef.current) closeMenu();
            return;
          }
        }
      }
    }
    for (const [i, tg] of tgs.entries()) {
      const match = tg.pattern.exec(textBefore);
      if (match && !(tg.guard && tg.guard.test(textAfter))) {
        const matchStart = textBefore.length - match[0].length;
        setActiveTrigger(i);
        setTarget(adapterRef.current.createRange(matchStart));
        setSearch(match[1]);
        setActiveIndex(0);
        return;
      }
    }
    if (targetRef.current) closeMenu();
  }, [closeMenu]);
  const onMenuKeyDown = useCallback15(
    (event) => {
      if (!targetRef.current) return false;
      const its = itemsRef.current;
      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          setActiveIndex((i) => its.length > 0 ? (i + 1) % its.length : 0);
          return true;
        }
        case "ArrowUp": {
          event.preventDefault();
          setActiveIndex(
            (i) => its.length > 0 ? (i - 1 + its.length) % its.length : 0
          );
          return true;
        }
        case "Enter":
        case "Tab": {
          if (its.length > 0) {
            event.preventDefault();
            const selected = its[activeIndexRef.current] ?? its[0];
            onSelect(selected.value);
            return true;
          }
          return false;
        }
        case "Escape": {
          event.preventDefault();
          closeMenu();
          return true;
        }
        default:
          return false;
      }
    },
    [onSelect, closeMenu]
  );
  return {
    target,
    search,
    items,
    activeIndex,
    onSelect,
    closeMenu,
    onEditorChange,
    onMenuKeyDown
  };
};

// src/hooks/use-input-autocomplete-adapter.ts
import { useMemo as useMemo12 } from "react";
function useInputAutocompleteAdapter(inputRef, onReplace) {
  return useMemo12(
    () => ({
      getCursorContext() {
        const el = inputRef.current;
        if (!el || el.selectionStart === null || el.selectionStart !== el.selectionEnd)
          return null;
        return {
          textBefore: el.value.slice(0, el.selectionStart),
          textAfter: el.value.slice(el.selectionStart)
        };
      },
      createRange(matchStart) {
        return {
          start: matchStart,
          end: inputRef.current?.selectionStart ?? matchStart
        };
      },
      replaceRange(range, text) {
        const { start, end } = range;
        const el = inputRef.current;
        if (!el) return;
        const next = el.value.slice(0, start) + text + el.value.slice(end);
        const cursor = start + text.length;
        onReplace(next);
        requestAnimationFrame(() => {
          el.focus();
          el.setSelectionRange(cursor, cursor);
        });
      }
    }),
    [inputRef, onReplace]
  );
}
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ActionList,
  ActionListGroup,
  ActionListItem,
  ActionListLabel,
  ActionListSeparator,
  Alert,
  AlertDescription,
  AlertTitle,
  AlignmentSelector,
  AutocompleteMenu,
  BlockTile_default as BlockTile,
  Box,
  Button,
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
  Calendar,
  Checkbox,
  Chip,
  ColorPicker,
  ColorSwatchesProvider,
  ComboBox,
  ComboBoxContent,
  ComboBoxEmpty,
  ComboBoxGroup,
  ComboBoxInput,
  ComboBoxItem,
  ComboBoxList,
  ComboBoxTrigger,
  ComboBoxValue,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandItemIcon,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  DEFAULT_FILL_COLOR,
  DateFilter,
  DatePicker,
  DelayIcon,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemDescription,
  DropdownMenuItemTitle,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Fieldset,
  FillPicker,
  Flex,
  FloatingPanel,
  FloatingPanelContent,
  FloatingPanelHeader,
  FloatingPanelNavigation,
  FloatingPanelStickyHeader,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormRow,
  GridSelector,
  Icon,
  Input,
  Label2 as Label,
  MarginPaddingEditor,
  Modal,
  ModalCenterBottomIcon,
  ModalCenterCenterIcon,
  ModalCenterTopIcon,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalIcon,
  ModalLeftBottomIcon,
  ModalLeftCenterIcon,
  ModalLeftTopIcon,
  ModalPortalContext,
  ModalPositionIcons,
  ModalRightBottomIcon,
  ModalRightCenterIcon,
  ModalRightTopIcon,
  ModalTitle,
  ModalTrigger,
  MultiSelect,
  MultiSelectAnchor,
  MultiSelectContent,
  MultiSelectEmpty,
  MultiSelectGroup,
  MultiSelectInput,
  MultiSelectItem,
  MultiSelectList,
  MultiSelectTokenField,
  MultiSelectTrigger,
  NavigateIcon,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
  RadioGroup2 as RadioGroup,
  RadioGroupItem,
  SegmentedControlGroup,
  SegmentedControlGroupItem,
  Select,
  SelectButton,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectPortal,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Stack,
  StepBar,
  StepBarItem,
  StepItemIcon,
  SwatchList,
  Switch,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  Text,
  Textarea,
  Toaster,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipIcon,
  TooltipProvider,
  TooltipShortcut,
  TooltipTrigger,
  actionListItemVariants,
  alertVariants,
  backgroundSizeToMode,
  boxVariants,
  buttonGroupVariants,
  chipVariants,
  cn,
  flexVariants,
  generateGradientCSS,
  isCssVariable,
  parseCssVariable,
  parseGradientFromCSS,
  parseGradientStops,
  separatorVariants,
  splitBackgroundImageParts,
  splitBoxProps,
  themeVariableToLabel,
  toast,
  useAutocomplete,
  useClickOutside,
  useColorSwatches,
  useFloatingPanelNavigation,
  use_floating_window_default as useFloatingWindow,
  useFloatingWindowListener,
  useInputAutocompleteAdapter,
  useModalPortal,
  useMultiSelect,
  useShadowDismiss
};
//# sourceMappingURL=index.mjs.map